/**
 * Calendar page. Picks a facility + date, fetches the day's appointments
 * and dock door layout, renders them in a Gantt grid.
 *
 * Interactions wired here:
 *  - Drag-create on grid → opens NewAppointmentDialog
 *  - + New appointment button → opens NewAppointmentDialog (empty)
 *  - Click appointment → opens AppointmentDetailDialog
 *  - Drag-move appointment → optimistic reschedule with rollback on conflict
 *  - Right-click appointment → ContextMenu with quick actions
 */
import { AppointmentSearch } from '@/components/AppointmentSearch';
import type { OrdersPanelRowDto } from '@/api/client';
// ⛔⛤⛤ ~~import { APPOINTMENT_STATUSES } from '@/lib/appointmentStatuses';~~
// ~~and `Check` from lucide-react~~ — BOTH ORPHANED 2026-09-19 when the inline
// status bar below was replaced by <StatusFilterBar>. ** MEASURED: `Check` had
// exactly two uses and `APPOINTMENT_STATUSES` exactly one, ALL inside the deleted
// block. ** `noUnusedLocals` is on — this is the third time tonight a deletion
// has threatened to orphan an import, and the first caught before the build.
import { StatusFilterBar } from '@/components/StatusFilterBar';
/* ⭐ The second filter axis — see the `workFilter` state below for why it is not
   seven more entries in `APPOINTMENT_STATUSES`. */
import { WorkFilterBar } from '@/components/WorkFilterBar';
/* ⭐⭐ The THIRD filter axis (Pete, 2026-09-27: *"the WMS NO WMS search filters
   that follow Loading/Unloading next to status"*). ⛔ A separate bar rather than
   two more chips in the work row, for the reason `WmsFilterBar.tsx` opens with:
   chips inside ONE row are an OR, and *"unloading AND no WMS"* has to be
   askable. See `wmsFilter` below. */
import { WmsFilterBar, WMS_FILTER_KINDS } from '@/components/WmsFilterBar';
import { workKind } from '@/lib/labourState';
import { AssignDoorDialog } from '@/components/AssignDoorDialog';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format, addDays, addMonths, startOfWeek, startOfMonth, parseISO, isToday as dfIsToday } from 'date-fns';
import {
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    AlertCircle,
    Ban,
    Plus,
    LogIn,
    LogOut,
    Pencil,
    XCircle,
    Info,
    CheckCircle2,
    Send,
    MapPin,
    UserX,
    Truck,
    MessageSquare,
    DoorOpen,
    Layers,
    FileSpreadsheet,
    FileText,
    PenLine,
    Printer,
    Undo2,
    Loader2,
    ShieldAlert,
    /* ⛔⤴ ~~`X`~~ — ORPHANED when the scope pill became the `R` glyph. `web/tsconfig`
       sets `noUnusedLocals`, so leaving it would have FAILED THE BUILD, not merely
       been untidy. ⚠️ Fourth orphaned-import catch in three days (§1AV.170). */
} from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import {
    ApiCallError,
    listBackoutSources,
    backOutMassCreateBatch,
    type BackoutSourceDto,
    backOutUpload,
    type UploadBackoutResult,
    approveAppointment,
    cancelAppointment,
    getCalendar,
    getCalendarRange,
    getLookups,
    markArrived,
    markDeparted,
    markNoShow,
    rejectAppointment,
    rescheduleAppointment,
    notifyAppointment,
    getAppointmentEvents,
    type NotifyEvent,
    type NotifyTarget,
    getMe,
    setCapacityRuleActive,
    setCapacityRuleTimes,
    deleteCapacityRule,
    setDedicationRuleActive,
    setDedicationRuleTimes,
    deleteDedicationRule,
    attachCheckIn,
    getDocumentConfig,
    driverConflict,
    /* ⭐ #73 — the same reader, for the second question a drop can raise. */
    vehicleNumberConflict,
    paperworkNumberConflict,
    type AttachResult,
    type DoorTextKind,
    // ⭐ #150 — a scan becomes a document, then the document gets a home.
    ingestScan, fileDocument, uploadDocument,
    getCheckInDocuments, getAppointmentPaperworkDossier, getDocumentUrl, getSuggestLoad,
    flattenDossierDocuments,
    // ⭐ A5 — an order dragged onto a load.
    getWmsOrderLinks, attachWmsOrder, type WmsOrderLinkDto,
} from '@/api/client';
import type { DoorAdminRow, CapabilityAdminRow } from '@/api/client';
import type { AppointmentDriverDto, AppointmentDto, CalendarDayDto, CalendarRangeDto, LookupsDto, CheckInDto, DocumentDto, ReleaseOverrideDto, SuggestLoadDto } from '@/api/types';
import {
    CreateInSlotEvent,
    DropCheckInInSlotEvent,
    DropCheckInOnAppointmentEvent,
    DropWmsOrderOnAppointmentEvent,
    GanttCalendar,
    MoveAppointmentEvent,
    RequestContextMenuEvent,
    RequestDoorMenuEvent,
    RequestLimitMenuEvent,
    RequestDedicationMenuEvent,
    EditLimitTimesEvent,
    EditDedicationTimesEvent,
} from '@/components/GanttCalendar';
import { WeekBoard } from '@/components/WeekBoard';
import {
    readPanelSubject, writePanelSubject, type PanelSubject,
} from '@/lib/panelSubject';
import { MonthGrid } from '@/components/MonthGrid';
import { NewAppointmentDialog } from '@/components/NewAppointmentDialog';
import { MassCreateDialog } from '@/components/MassCreateDialog';
import { UploadAppointmentsDialog } from '@/components/UploadAppointmentsDialog';
import { AppointmentDetailDialog } from '@/components/AppointmentDetailDialog';
import { EditAppointmentDialog } from '@/components/EditAppointmentDialog';
import { ContextMenu } from '@/components/ContextMenu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { WmsOrderReplaceDialog } from '@/components/WmsOrderReplaceDialog';
import { WmsOrderMatchDialog } from '@/components/WmsOrderMatchDialog';
import type { WmsOrderDragKey } from '@/lib/dnd';
import { SendDirectionsDialog } from '@/components/SendDirectionsDialog';
import { SendConsigneePinDialog } from '@/components/SendConsigneePinDialog';
import { AssignDriverDialog } from '@/components/AssignDriverDialog';
import { CheckInPanel } from '@/components/CheckInPanel';
import { PossibleNoShowsChip } from '@/components/PossibleNoShows';
import { PossibleNoDeparturesChip } from '@/components/PossibleNoDepartures';
import { NoWmsChip } from '@/components/NoWmsChip';
import type { NoWmsRowDto } from '@/api/types';
import { ScanFolderPanel } from '@/components/ScanFolderPanel';
import { PaperworkSliver } from '@/components/PaperworkSliver';
import { printDocumentToDialog } from '@/lib/printDocument';
import type { AttachedTarget } from '@/components/AttachedPaperworkPanel';
/* ⭐ `focusScopeLabel` joined 2026-09-27 with the single Ⓡ — the board header now
   names what it is about to reset, and it must say it the way the five rails say it
   rather than composing a sixth sentence. */
import { focusFromAppointment, focusFromParty, focusScopeLabel, type RailFocus } from '@/lib/railFocus';
import { apptHasPartyKind, apptMatchesParty, isPartyKind, partyNameKey, type PartyKind } from '@/lib/partyRows';
import { setThumbTokenAccessor } from '@/components/DocumentThumb';
import type { PaperworkDragPayload, PaperworkDragItem } from '@/lib/dnd';
import { readPaperworkDrag, lookupLocalScan, lookupLocalFolderWriter } from '@/lib/dnd';
import { unfileDocumentApi, fetchPreviewBlob, voidDocumentApi, deleteScan,
    type PreviewTarget } from '@/api/client';
import { choosePaperworkAction } from '@/lib/paperworkActions';
import { SignDocumentDialog } from '@/components/SignDocumentDialog';
import { AppointmentDocumentsDialog } from '@/components/AppointmentDocumentsDialog';
import { DoorTextDialog } from '@/components/DoorTextDialog';
import { StatusOverrideDialog } from '@/components/StatusOverrideDialog';
import { OutboundReleaseDialog } from '@/components/OutboundReleaseDialog';
import { SettingsDialog, DoorEditor } from '@/components/SettingsDialog';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * ⭐ F121 (Decision 303): how long a click on a grid chip waits for a second click
 * before it scopes. Short enough to feel immediate; long enough that a double-click
 * opens the load instead of re-laying the board under the cursor.
 */
const SCOPE_CLICK_DELAY_MS = 250;

/**
 * ---- ⛔⛔ THE SCOPE SENTENCE THE WMS ORDERS PANEL PRINTS -------------------
 *
 * ⚠️ IT MUST DESCRIBE THE RANGE ACTUALLY QUERIED. The predecessor of this
 * function was one line — `viewMode === 'day' ? 'today' : ...` — which named
 * the VIEW MODE and therefore said "today" on every day view, whatever day was
 * on screen. §1Z.2 makes that sentence load-bearing: it is the price paid for
 * narrowing a default, and a false one is worse than none.
 *
 * ⚠️ THE DATES ARE PARSED FIELD BY FIELD, NOT WITH `new Date(isoDay)`. That
 * constructor reads a bare YYYY-MM-DD as UTC MIDNIGHT, so west of Greenwich
 * `new Date('2026-09-08')` formats as Sep 7 — the label would disagree with the
 * calendar beside it, which is the whole failure being fixed here.
 */
export function describeOrdersScope(fromIso: string, toIso: string): string {
    const parse = (d: string) => {
        const [y, m, dd] = d.split('-').map(Number);
        return (y && m && dd) ? new Date(y, m - 1, dd) : null;
    };
    const from = parse(fromIso);
    const to = parse(toIso);
    /* ⛔ UNPARSEABLE FALLS BACK TO THE RAW DATES, never to a friendly word that
       might be wrong. An honest ugly label beats a pretty lie. */
    if (!from || !to) return fromIso === toIso ? fromIso : `${fromIso} to ${toIso}`;

    const today = new Date();
    const isToday = (d: Date) =>
        d.getFullYear() === today.getFullYear()
        && d.getMonth() === today.getMonth()
        && d.getDate() === today.getDate();

    const day = (d: Date) => d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

    if (fromIso === toIso) return isToday(from) ? 'today' : day(from);
    return `${day(from)} to ${day(to)}`;
}

export function CalendarPage() {
    const { getToken } = useAuth();
    /**
     * ⭐ THE THUMBNAIL COMPONENT'S TOKEN, SET ONCE HERE.
     * ⚠️ The alternative was threading `getToken` through every tray row purely so
     * a decorative 28px square could authenticate — auth plumbing in four
     * components that have no other use for it. See `DocumentThumb`.
     */
    useEffect(() => { setThumbTokenAccessor(getToken); }, [getToken]);

    const [lookups, setLookups] = useState<LookupsDto | null>(null);
    const [lookupsError, setLookupsError] = useState<string | null>(null);

    const [facilityId, setFacilityId] = useState<string | null>(null);
    const [date, setDate] = useState<string>(() => format(new Date(), 'yyyy-MM-dd'));
    const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('day');

    // Status legend doubles as a multi-select filter: empty set = All, otherwise
    // show appointments whose status is checked (or blocks when 'BLOCK' checked).
    // Applied to every view (day/week/month).
    const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
    const toggleStatus = (code: string) =>
        setStatusFilter((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    /**
     * ---- ⭐⭐⭐ THE WORK FILTER: A SECOND AXIS, NOT MORE STATUSES ---------------
     *
     * > Pete, 2026-09-24: *"it would be great to filter the calendar by Unloading and
     * > Loading in the status bar, is it possible to add those?"* → **"Separate
     * > control beside the status bar."**
     *
     * ⛔⛔ IT COULD NOT HAVE GONE IN `statusFilter`. That set is matched against
     * `a.statusCode`, and a truck being unloaded is `ARRIVED` — so an `UNLOADING`
     * entry there would have matched nothing, and, worse, selecting it alongside
     * `ARRIVED` would have been an **OR**, making "arrived AND being unloaded"
     * unaskable. See `components/WorkFilterBar.tsx` for the full argument.
     *
     * ⭐ SAME SHAPE AS `statusFilter` ON PURPOSE — a `Set<string>`, empty means no
     * filter, one toggle function. A scheduler who has learnt one row has learnt
     * both, and `filterAppts` applies them as a plain intersection.
     */
    const [workFilter, setWorkFilter] = useState<Set<string>>(new Set());
    const toggleWork = (code: string) =>
        setWorkFilter((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    /**
     * ---- ⭐⭐⭐ THE WMS FILTER: A **THIRD** AXIS (Pete, 2026-09-27) ------------
     *
     * > *"We are also missing the WMS icon on the appointment with a circle slash
     * > showing no WMS (in lower right hand corner) and the WMS NO WMS search
     * > filters that follow Loading/Unloading next to status."*
     *
     * ⛔⛔ IT COULD NOT HAVE GONE IN `workFilter` EITHER, AND THE REASON IS THE
     * ONE THAT FILE WAS CREATED TO STATE. Chips inside ONE bar are an **OR** —
     * `workFilter.has(k)` — so Loading, Unloading, WMS and NO WMS in one row
     * would make *"unloading AND no WMS"* unaskable, which is the precise failure
     * `WorkFilterBar` exists because of. Repeating it one row later would be a
     * remarkable thing to do.
     *   ** A SECOND QUESTION ABOUT A ROW IS A SECOND AXIS. FOLDING IT INTO THE
     *      FIRST DOES NOT CROWD THE FIRST, IT MAKES THE INTERSECTION
     *      UNREACHABLE. **
     *
     * ⭐ SAME SHAPE AGAIN — `Set<string>`, empty means no filter, one toggle. The
     * third control a scheduler meets teaches him nothing new.
     *
     * ⚠️ ITS SOURCE IS `a.hasWmsOrder`, WHICH THE **BOARD QUERY** COMPUTES — not
     * the orders panel. The panel answers "which orders have appointments"; this
     * asks the opposite question of every appointment on screen, including the
     * ones no order mentions, and a panel that only lists matches can never name
     * those. See `appointmentRepository.wmsApptPrelude`.
     *   ** A LIST OF WHAT MATCHED CANNOT ENUMERATE WHAT DID NOT. **
     */
    const [wmsFilter, setWmsFilter] = useState<Set<string>>(new Set());
    const toggleWms = (code: string) =>
        setWmsFilter((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    /**
     * NOTHING CHECKED = SHOW EVERYTHING, cancelled included.
     *
     * ⚠️ THIS WAS CHANGED TO HIDE CANCELLED ON 2026-08-22 AND PETE CHANGED IT BACK
     * THE SAME DAY: *"don't like not seeing cancelled."* ⛔ DO NOT "FIX" IT AGAIN.
     * The reasoning for hiding them was that a cancelled truck is not happening and
     * should not hold a lane — which is true of the LANE and false of the
     * SCHEDULER: a cancelled slot is exactly what somebody looks for when asking
     * whether a time is genuinely free or was given away and handed back.
     *
     * ⭐ THE LANE COST IS ALREADY SOLVED ELSEWHERE. A day-only appointment no
     * longer spans the day (see effectiveSpans in GanttCalendar), so thirty
     * cancelled rows no longer mean thirty full-width bars — which is what made
     * them intolerable on screen in the first place. Hiding them was treating the
     * symptom of a different bug.
     */
    /**
     * ---- ⭐⭐ THE PARTY SCOPE — PAGE STATE, AND DELIBERATELY NOT `railFocus` -----
     *
     * Decision 188 increments 3/5. Set by choosing a party in the check-in panel.
     *
     * ⛔⛔ IT IS **NOT** `railFocus`, AND THAT SEPARATION IS THE WHOLE POINT.
     * `railFocus` latches on a 350ms DWELL over a driver card or a grid chip. If the
     * calendar filtered on it, sliding the mouse across the rail would empty the
     * schedule — and worse, it would silently discard a party the scheduler had
     * deliberately chosen.
     *
     *   ** A DELIBERATE SELECTION AND A HOVER ARE DIFFERENT LIFETIMES. ONE STATE
     *      CANNOT HOLD BOTH WITHOUT THE SHORTER ONE DESTROYING THE LONGER. **
     *
     * ⚠️ THE RAIL STILL FOLLOWS IT: the panels receive `railFocus ?? partyScope`, so
     * choosing Hellman scopes all four, and pointing at one of his trucks narrows
     * them further. Clearing the truck falls back to Hellman rather than to
     * everything — which is what the toolbar pill on screen still says.
     *
     * ⛔ CLEARED ON FACILITY CHANGE. A client code is only meaningful inside the
     * facility it was read from; carrying `HELLMAN` from Denver to Brighton would
     * filter Brighton's grid by a code that may not exist there, producing an empty
     * schedule with a pill blaming a client who has nothing to do with it.
     */
    const [partyScope, setPartyScope] = useState<RailFocus | null>(null);
    /**
     * ---- ⭐⭐ MOVED UP FROM ~1790 ON 2026-09-27, AND IT HAD TO BE -------------
     *
     * ⛔⛔ `filterAppts` NOW READS IT, and `filterAppts` is defined two hundred
     * lines below this and a thousand above where this used to live. A `useCallback`
     * dependency array is evaluated WHEN THE HOOK IS CALLED, not when the callback
     * runs — so naming `railFocus` there while the `const` sat below would be a
     * temporal-dead-zone ReferenceError on the first render, not a stale value.
     *   ** A HOOK'S BODY IS DEFERRED. ITS DEPENDENCY ARRAY IS NOT. **
     *
     * ⭐ ONLY THE DECLARATION MOVED. `railFocusRef`, `clearRailFocus` and the whole
     * argument for why this is separate from `partyScope` stay where they were —
     * moving a comment away from the code it explains costs more than it saves.
     *
     * ⭐ AND IT SITS BESIDE `partyScope` DELIBERATELY. The two are the pair the
     * §1AV.174 argument is about — a deliberate selection and its lifetime — and a
     * reader who finds one should find the other.
     */
    const [railFocus, setRailFocus] = useState<RailFocus | null>(null);
    /**
     * ---- ⭐⭐⭐ THE WARNING SCOPE — NARROWER THAN A PARTY, AND **ONLY** THE BOARD --
     *
     * > Pete, 2026-09-23: *"when you hover over them, looks like they are highilgting
     * > teh appointments but they need to highlight the appointmets with the warnings
     * > and then when you click on the warning, it only shows the appointments with the
     * > warnings in the calendar."*
     *
     * ⛔⛔ A **THIRD** STATE, AND THE ARGUMENT IS THE ONE DIRECTLY ABOVE, ONE LEVEL IN.
     * `partyScope` was separated from `railFocus` because a deliberate selection and a
     * hover are different lifetimes. This is separated from `partyScope` because they
     * have different REACH: a party scopes the board **and** the five rails; a warning
     * scopes the board **alone**, by Pete's words — *"in the calendar"*.
     *   ** TWO SELECTIONS THAT DIFFER IN WHAT THEY AFFECT CANNOT SHARE A VARIABLE. THE
     *      PANELS READ `railFocus ?? partyScope`, SO ANYTHING WRITTEN THERE REACHES
     *      THEM WHETHER OR NOT IT WAS MEANT TO. **
     *
     * ⚠️ AND THE REASON IT MUST NOT REACH THEM IS PRACTICAL, not tidiness: the rails
     * are where a scheduler finds out WHY a load is overdue. Filtering the board to the
     * problems while filtering the panels to the same set removes the explanation at the
     * moment it is wanted.
     *
     * ⛔ IT HOLDS IDS, NOT A PREDICATE. The card computed which loads are overdue
     * (`PartyRow.atRiskIds`); re-deriving it here would be the second expression this
     * codebase keeps proving drifts. The `key` is only for drawing the pressed box.
     */
    const [warningScope, setWarningScope] =
        useState<{ key: string; ids: string[]; label: string } | null>(null);
    /* ⚠️ ORDER: `setPartyScope` LAST. `tenancy-row3-guard` pins the literal
       `setPartyScope(null); }, [facilityId])` - a spelling, not a rule - and putting the
       new call first keeps that assertion meaningful instead of retuning it. */
    useEffect(() => { setWarningScope(null); setPartyScope(null); }, [facilityId]);
    /**
     * ---- ⛔⛔ A **DATE** CHANGE CLEARS THE SCOPE TOO (Pete, 2026-09-20) -------------
     *
     * ⭐ *"Moved the date on the search bar up top but it kept the Driver information
     * across the panels across the dates. Should only keep it for that date."*
     *
     * ⭐⭐ THE SAME FAULT AS THE CHIP, ONE AXIS OVER, AND THE SAME ARGUMENT SETTLES IT:
     * a scope is a statement about **what is on screen**. PETER LOMBARDO checked in on
     * the 11th; carrying him onto the 12th scopes five panels to a man who is not there
     * and filters a board he has no load on. ⚠️ The facility clear above has said exactly
     * this since increment 3 — *"a client code is only meaningful inside the facility it
     * was read from"* — and the date is the second coordinate of the same idea.
     *   ** A SCOPE IS BOUND TO THE SLICE IT WAS CHOSEN IN. EVERY COORDINATE OF THAT
     *      SLICE MUST CLEAR IT. **
     *
     * ⚠️ NO FIRST-RENDER GUARD NEEDED, unlike the collapse counter this replaces in
     * spirit: on mount every one of these is ALREADY null, so the effect is a no-op the
     * first time. A ref here would be machinery guarding nothing.
     *
     * ⛔ AND IT IS WIRED TO `date`, NOT TO THE FIVE PLACES THAT SET IT — the picker, the
     * arrows, `Today`, the search's jump, and the week/month drill-down.
     *   ** WIRE THE EFFECT TO THE STATE THAT CHANGED, NOT TO EVERY DOOR IT CAME IN BY. **
     */
    useEffect(() => {
        setPartyScope(null);
        /* ⚠️ AND THE WARNING SCOPE, for the same reason and more sharply: it is a LIST
           OF APPOINTMENT IDS from one day. Carried onto the next date it matches nothing
           and blanks the board — the empty-board trap, with no pill to blame. */
        setWarningScope(null);
        setRailFocus(null);
        setAttachedPaperwork(null);
    }, [date]);
    /* ⛔ ONE WRITER, like `chooseSubject`. The chips, the party cards and (later) the
       search all set this, and "also clear the status filter" must not be something
       two of the three remember. */
    const chooseParty = useCallback(
        (kind: PartyKind, code: string, name: string | null, appts: AppointmentDto[]) => {
            const f = focusFromParty(kind, code, name, appts);
            setPartyScope(f);
            /**
             * ---- ⛔⛔⚤ THE LINE THAT WAS MISSING, AND IT COST FOUR BUG REPORTS --
             *
             * ⭐ Pete, 2026-09-20: *"It doesn't scope at all when I click a carrier,
             * client or consignee - which it should."* · *"when I click a client, the
             * hightlight stays on one appointment???"* · *"When I click on the FedEx
             * panel in the search, it doesn't ... do anything on the screen."*
             *
             * ⛔⛔ THE RAIL READS `railFocus ?? partyScope`. Writing only the second
             * leaves an earlier LOAD focus latched, and the `??` hands the panels that
             * load forever — the party click lands on the calendar and is invisible
             * everywhere else. The screenshot said it in one frame: line 1
             * `Clients · TRAFFIX`, line 2 `Load · A00000315`, five rails `(load)`.
             *
             *   ** TWO STATES WITH DIFFERENT LIFETIMES ARE RIGHT. A GESTURE THAT
             *      WRITES ONLY ONE OF THEM IS NOT. **
             *   ** A `??` CHAIN IS A PRIORITY ORDER, NOT A CLEAR. THE LOWER-PRIORITY
             *      STATE IS ONLY VISIBLE WHEN SOMEBODY CLEARS THE HIGHER ONE, AND
             *      "SOMEBODY" HAS TO BE NAMED. **
             *
             * ⛔ AND THE TWO STATES ARE **NOT** MERGED, which was the obvious fix and
             * is wrong. Pete, asked before it was built: clicking one load must leave a
             * standing party on the board — *"board stays whole; chip gets the ring"*.
             * The separation §1AV.174 argued for is correct; only the writes were short.
             */
            setRailFocus(f);
            /* ⛔ AND THE TRAY CANNOT SURVIVE IT. Attached Paperwork is one load's
               documents; a party has no single load, so keeping the previous one would
               file TRAFFIX's eleven trucks under whichever truck was last clicked.
                 ** A PANEL THAT CANNOT ANSWER THE NEW QUESTION MUST NOT KEEP SHOWING
                    ITS ANSWER TO THE OLD ONE. ** */
            setAttachedPaperwork(null);
            /* ⛔ AND THE WARNING FILTER GOES. It is a subset of a PARTICULAR party's
               appointments; keeping it across a party change would show TRAFFIX's card beside a
               board filtered to Hellman's overdue trucks. */
            setWarningScope(null);
        }, []);
    /* ⚠️ `clearParty` IS WHAT Ⓡ REACHES (via `CheckInPanel`'s `resetAll`) and what the
       toolbar pill calls. A reset that left the board filtered would be the third of
       §1AV's "resets a third of the screen" faults. */
    const clearParty = useCallback(() => { setPartyScope(null); setWarningScope(null); }, []);
    /**
     * ---- ⭐⭐⭐ **ONE** Ⓡ, AND IT LIVES ON THE BOARD (Pete, 2026-09-27) ----------
     *
     * > *"think we should put the circle R to the right of XX Appointments instead of
     * > Driver Check-INs"* → asked how far to take it: **"One Ⓡ in the board header,
     * > retire both."**
     *
     * ---- ⛔⛔ THERE WERE **TWO**, AND THEY DID DIFFERENT AMOUNTS OF WORK --------
     *
     * ~~`CheckInPanel`'s Ⓡ~~ (Day only) called `resetAll` — rail focus, party scope
     * AND collapse the arrival cards. ~~This file's toolbar Ⓡ~~ (Week/Month only)
     * called `clearParty` alone: it left `railFocus` latched. They were never on
     * screen together, which is the only reason the difference survived — and
     * `CheckInPanel` had already written the rule it was breaking:
     *   ** IF TWO CONTROLS CLAIM TO DO THE SAME THING, THEY MUST BE THE SAME
     *      FUNCTION. TWO LISTS OF CALLS IS TWO BEHAVIOURS WEARING ONE NAME. **
     *
     * ⭐ THE TALLY IS WHY THE MERGE IS FREE. `XX appointments` is drawn in the board
     * header, which exists in ALL THREE views — so Pete's placement dissolves the
     * Day-vs-the-rest split that forced two buttons in the first place.
     *
     * ---- ⭐ WHY THE CARDS COLLAPSE THROUGH A COUNTER -------------------------
     *
     * ⛔ `collapseAllCards` IS `CheckInPanel`'s OWN STATE and cannot be called from
     * here. A counter prop it watches is the same shape as `refreshKey` and
     * `paperworkKey`, which this page already uses to say "do your thing now" to a
     * child — so it is the established seam rather than a new one.
     *   ** A PARENT ASKS A CHILD TO ACT BY CHANGING SOMETHING THE CHILD IS
     *      WATCHING. AN IMPERATIVE HANDLE IS FOR WHEN THAT IS IMPOSSIBLE. **
     *
     * ⚠️ IT DOES NOT CLEAR `attachedPaperwork`, matching what Ⓡ did before — the
     * tray is a target, not a scope, and `clearRailFocus` never touched it either.
     */
    const [collapseKey, setCollapseKey] = useState(0);
    const resetAllScopes = useCallback(() => {
        setRailFocus(null);
        setPartyScope(null);
        setWarningScope(null);
        setCollapseKey(k => k + 1);
    }, []);
    /**
     * ⭐ ONE WRITER FOR THE WARNING FILTER, and it is a TOGGLE: the panel passes `null`
     * when the pressed warning is pressed again.
     * ⛔ IT DOES NOT TOUCH `partyScope` OR `railFocus`. Clicking a warning on a card
     * that is not the chosen party must not silently choose it — that is the fall-through
     * the inert `<div>` was causing, reintroduced deliberately.
     */
    const chooseWarningScope = useCallback(
        (key: string | null, ids: string[], label: string) =>
            setWarningScope(key ? { key, ids, label } : null),
        []);
    /**
     * ---- ⭐⭐ THE MASTER NAMES, FROM A PAYLOAD THIS PAGE ALREADY HAD -------------
     *
     * ⭐ Pete: *"Not giving me the full name GEODIS"*. The booking stores its own copy
     * of the carrier/client name and GEODIS's bookings have it blank.
     * ✅ `lookups.carriers` (`dock.Carrier`) and `lookups.clients`
     * (`dock.vw_KnownClients`) were being fetched for the dropdowns and nothing else.
     *
     *   ** THE FIX FOR A MISSING NAME WAS A PAYLOAD ALREADY ON THE PAGE, USED FOR
     *      SOMETHING ELSE. ASK WHAT THE EXISTING RESPONSE CARRIES. **
     *
     * ⛔ ONE MEMOISED MAP, NOT A FUNCTION PROP. A resolver rebuilt each render is a
     * new identity every time and would bust the panel's `useMemo` on every keystroke
     * elsewhere on the page (§4.15: a dependency must be an identity, not an object).
     * ⚠️ CONSIGNEES ARE ABSENT ON PURPOSE — they are not in `getLookups`, and a map
     * missing that axis makes the fall-through explicit rather than silently wrong.
     */
    const partyNames = useMemo(() => {
        const m = new Map<string, string>();
        for (const c of lookups?.carriers ?? []) {
            if (c.CarrierCode && c.CarrierName) m.set(partyNameKey('carrier', c.CarrierCode), c.CarrierName);
        }
        for (const c of lookups?.clients ?? []) {
            if (c.ClientCode && c.ClientName) m.set(partyNameKey('client', c.ClientCode), c.ClientName);
        }
        return m;
    }, [lookups]);
    /**
     * ---- ⛔⛤ THERE IS NO `previewParty`, AND THERE WAS FOR ABOUT TEN MINUTES -----
     *
     * ⭐ Pete: *"hovering does nothing for the panels to the right"* — then, when I
     * asked him how hover should behave: ***"Why are you asking me about the hover
     * over client or carrier. I designed this specifically in our session. It
     * definitely should be in the master arch or session doc"***.
     *
     * ✅ HE WAS RIGHT AND IT IS: **§1AR, added 2026-09-10 from his own instruction.**
     * *"Hovering SETS the scope and only ALL clears it. Anything else makes the rail
     * change under a scheduler who looked away."* — and the proof he gives there is
     * that he asked for an `ALL` control unprompted: **a scope that released itself on
     * mouse-out would never need a reset.**
     *
     * ⛔⛔ I ASKED A SETTLED QUESTION AND THEN DESIGNED THE ANSWER HE HAD ALREADY
     * REJECTED — a hover-preview that restores on mouse-out is exactly the behaviour
     * §1AR exists to forbid.
     *   ** §0.7 IS NOT ABOUT KNOWING THE RULES, IT IS ABOUT LOOKING THEM UP BEFORE
     *      ASKING. A QUESTION PUT TO SOMEBODY WHO ALREADY ANSWERED IT COSTS MORE THAN
     *      THE READ WOULD HAVE. **
     *
     * ✅ So there is no third state: a party card's dwell goes through
     * `onHoverCardShown` — the SAME seam a driver card uses — and latches `railFocus`.
     * Nothing new was needed, which is usually the sign that the existing design was
     * right.

    /* ⛔⚤ MOVED UP 2026-09-20. `filterAppts` below now reads `subject` — the chip
       narrows the board to the appointments its cards account for — and a dependency array is
       evaluated during RENDER, so a `const` declared later is a temporal dead zone crash.
       ⭐ `tsc` caught this one, unlike the `resetAll` case in CheckInPanel where the
       reference sat inside a callback BODY and compiled happily.
         ** A DEPENDENCY ARRAY RUNS AT RENDER TIME. IT CAN ONLY NAME WHAT HAS ALREADY
            BEEN DECLARED. ** */
    /**
      * ---- ⛔⛔ THE PANEL SUBJECT IS PAGE STATE, BESIDE THE RAIL FOCUS -------------
      *
      * Decision 188 (§1AV.171-2). ⚠️ It lived in `CheckInPanel` for one increment.
      * Two things moved it here, and either would have been enough:
      *
      *   1. ⛔ THREE ENTRY POINTS — the chips, the panel's dropdowns, and the page's
      *      search box (Pete: *"Client and Carrier search in the search box above
      *      should do the same"*). Only one of the three is inside that panel.
      *   2. ⛔ THE PANEL IS DAY-ONLY. `{viewMode === 'day' && ... <CheckInPanel/>}`
      *      below. Pete: *"when you goto WEEK or MONTH view, that should just show the
      *      client or carrier selected"* — state that outlives the component cannot
      *      live in it.
      *
      *   ** A SCOPE WITH MORE ENTRY POINTS THAN CONSUMERS IS PAGE STATE WEARING A
      *      PANEL'S CLOTHES. **
      *
      * ⛔⛔ RE-READ ON FACILITY CHANGE. The key is per facility and a lazy `useState`
      * initialiser runs ONCE. ⚠️ §1AV.170 records that exact defect being found and
      * fixed TWICE in one evening, in two different files; `readDoorOrder` below is
      * the pattern this copies.
      */
    const [subject, setSubject] = useState<PanelSubject>(() => readPanelSubject(facilityId ?? ''));
    useEffect(() => { setSubject(readPanelSubject(facilityId ?? '')); }, [facilityId]);

    /**
     * ---- ⭐⭐ THE ONE CHOKEPOINT, AND THE PARTY SCOPE JOINS IT HERE -------------
     *
     * ⛔ Day, Week and Month all draw `filterAppts(...)`, so a bound added here is
     * inherited by all three BY CONSTRUCTION — which is the whole of increment 5.
     * ⚠️ Adding it at the three call sites instead would be three copies of one rule
     * and the first refinement would land in one of them (§1AV.170).
     *
     * ---- ⛔⛔ BLOCKS SURVIVE THE PARTY BOUND, DELIBERATELY ---------------------
     *
     * A block is a DOOR OUTAGE — facility state, not a party's load. Scoping to
     * Hellman and having the out-of-service doors vanish would show a scheduler
     * capacity that does not exist, and he would book into it.
     *
     *   ** A FILTER OVER "WHOSE WORK" MUST NOT REMOVE "WHAT IS BROKEN". THE SECOND IS
     *      TRUE OF THE BUILDING, NOT OF THE SELECTION. **
     *
     * ⚠️ The STATUS filter still governs blocks, exactly as before — that is a
     * deliberate choice by the user about blocks themselves.
     */
    const filterAppts = useCallback(
        (list: AppointmentDto[]) => {
            const code = partyScope?.partyCode;
            /**
             * ---- ⭐⭐ A DRIVER IS A BOARD SCOPE TOO (decision 194) ----------------
             *
             * ⭐ Pete, 2026-09-20: *"When I single click a driver, it doesn't change
             * the Driver Checkin to Driver - Name and it doesn't change the schedule to
             * show on only the appointment on the calendar for that driver."*
             *
             * ⛔ A DRIVER IS NOT A `PartyKind`, so it fell straight through the guard
             * below and the board never moved. ⭐ It does not need one: a driver focus
             * already carries `appointmentIds` — the set built precisely so a party
             * could be "eleven loads" and a driver "a set of one" without a second code
             * path. The filter just had to read it.
             *
             * ⛔⛔ AN EMPTY SET FALLS THROUGH TO THE WHOLE BOARD ON PURPOSE. A driver
             * who has checked in with no load yet would otherwise blank the schedule and
             * blame him for it — the empty-board trap, and the panels still scope to him
             * correctly meanwhile.
             *   ** A FILTER WITH NOTHING TO FILTER ON MUST SHOW EVERYTHING, NEVER
             *      NOTHING. AN EMPTY RESULT IS AN ANSWER; AN EMPTY BOARD IS AN ACCUSATION. **
             *
             * ⚠️ AND A CHIP CLICK NEVER REACHES HERE. It writes `railFocus` alone, so
             * `partyScope` is untouched and the board keeps whatever it was showing —
             * Pete's *"board stays whole; chip gets the ring"*.
             */
            const scopedIds = partyScope && !isPartyKind(partyScope.kind)
                ? partyScope.appointmentIds
                : null;
            /**
             * ⭐⭐ AND WITH **NO** PARTY CHOSEN, THE CHIP ITSELF NARROWS THE BOARD to the
             * loads its cards account for (Pete, 2026-09-20): *"It should not be showing
             * any calender appts here because no consignees are on these appts."*
             * ⛔ NOT A CONSIGNEE SPECIAL CASE — one rule, three outcomes. Clients covers
             * every load, consignees covers almost none, carrier sits between.
             *   ** A CHIP SHOWS WHAT ITS CARDS ACCOUNT FOR. AN EMPTY CARD LIST BESIDE A
             *      FULL BOARD IS THE SCREEN CONTRADICTING ITSELF. **
             * ⚠️ `subject` IS A DEPENDENCY NOW. Without it in the array below the board
             * would keep the previous chip's filter — stale by one click, which is the
             * hardest kind of wrong to see.
             */
            const chipKind = isPartyKind(subject) ? subject : null;
            const scoped = partyScope && code && isPartyKind(partyScope.kind)
                ? list.filter((a) => a.direction === 'BLOCK'
                    || apptMatchesParty(a, partyScope.kind as PartyKind, code))
                : scopedIds && scopedIds.length > 0
                    ? list.filter((a) => a.direction === 'BLOCK'
                        || scopedIds.includes(String(a.appointmentId)))
                    : chipKind
                        ? list.filter((a) => a.direction === 'BLOCK'
                            || apptHasPartyKind(a, chipKind))
                        : list;
            /**
             * ---- ⭐⭐ THE WARNING FILTER, APPLIED LAST AND AS AN INTERSECTION -------
             *
             * ⛔⛔ LAST, DELIBERATELY. It is a narrowing OF whatever is on the board, not
             * a replacement for it — so a chosen party plus a warning reads as "Hellman's
             * overdue loads", and the two controls compose instead of overriding each
             * other. Putting it first would make a warning click silently widen the board
             * back out to every party.
             *   ** A SECOND FILTER IS AN INTERSECTION UNTIL SOMEBODY DECIDES OTHERWISE,
             *      AND NOBODY DECIDED OTHERWISE. **
             *
             * ⚠️ BLOCKS SURVIVE IT, exactly as they survive the party bound above and for
             * the identical reason: a door outage is the building's state, and hiding it
             * shows capacity that does not exist.
             *
             * ⛔ AN EMPTY `ids` NEVER REACHES HERE — the panel only makes a warning
             * clickable when it has loads. ⚠️ The `length > 0` test is kept anyway, and it
             * is NOT belt-and-braces: without it a future caller with an empty list would
             * blank the board rather than fail to filter, which is §1AV's empty-board
             * trap again.
             */
            const warned = warningScope && warningScope.ids.length > 0
                ? scoped.filter((a) => a.direction === 'BLOCK'
                    || warningScope.ids.includes(String(a.appointmentId)))
                : scoped;
            /**
             * ---- ⭐⭐⭐ A CHIP CLICK NARROWS THE BOARD — DECISION 194 REVERSED ------
             *
             * > Pete, 2026-09-27, on the grid: *"when we single click an appointment and
             * > narrow down - which isn't working btw (just highlights)"* → asked whether
             * > this was the flip of his own decision, he answered **"Yes — narrow the
             * > board, second click restores."**
             *
             * ⛔⛔ §0.3 — WHAT THIS REVERSES, IN HIS OWN WORDS, SO IT CAN BE UNDONE:
             *   ~~*"Board stays whole; chip gets the ring"* — clicking one load scopes
             *     the panels and leaves the calendar showing everything, or the standing
             *     party if one is chosen. Only the ring says which.~~
             * The ring is UNCHANGED and still says which; what changed is that the board
             * now follows it. ⚠️ `showAttachedFor`'s note on the two argument shapes still
             * describes a real asymmetry — the card writes `partyScope`, the chip does
             * not — and that asymmetry is exactly what makes this safe. Read on.
             *
             * ---- ⛔⛔ IT READS `railFocus`, AND DOES **NOT** WRITE `partyScope` -------
             *
             * ⭐ THE OBVIOUS IMPLEMENTATION IS THE WRONG ONE. Having the chip call
             * `setPartyScope` would narrow the board in one line — and would DESTROY THE
             * STANDING PARTY, which is the state the second click restores TO. The
             * chip's own toggle says so: *"deliberately leaving `partyScope` standing,
             * because the party was chosen by a different gesture and nothing here
             * revokes it."*
             *   ** A NEW BEHAVIOUR THAT OVERWRITES THE STATE ITS OWN UNDO DEPENDS ON
             *      HAS NO UNDO. **
             *
             * ⭐⭐ SO THE BOARD READS THE LATCH INSTEAD, AND NO NEW STATE EXISTS. The
             * second click already sets `railFocus` to null, so "second click restores"
             * falls out of the mechanism rather than being a second thing to remember:
             *   · chip click    → `railFocus` = that appointment  → board narrows here
             *   · same chip     → `railFocus` = null              → board falls back to
             *                                                       whatever `partyScope`
             *                                                       was showing
             *   · driver / party → they write `partyScope` too, and are bounded ABOVE by
             *                       `scopedIds`; this step sees `kind !== 'appointment'`
             *                       and does nothing.
             *
             * ⚠️ `kind === 'appointment'` IS THE WHOLE GUARD. A driver focus also carries
             * `appointmentIds`, and without the kind test this would bound the board a
             * second time with the same set — harmless today, and wrong the moment a
             * driver focus covers something the party bound does not.
             *
             * ⛔ AN EMPTY SET FALLS THROUGH TO WHAT WAS ALREADY THERE, the same rule the
             * party bound states two hundred lines up and the warning bound states
             * directly above: ** AN EMPTY RESULT IS AN ANSWER; AN EMPTY BOARD IS AN
             * ACCUSATION. **
             *
             * ⚠️ BLOCKS SURVIVE IT, exactly as they survive all three bounds above, and
             * for the identical reason: a door outage is the BUILDING's state, and hiding
             * it shows capacity that does not exist.
             *
             * ⚠️ AND IT IS AN INTERSECTION, APPLIED AFTER THE OTHERS — so "TRAFFIX's
             * overdue loads, and this one" composes instead of overriding.
             */
            const focused = railFocus
                    && railFocus.kind === 'appointment'
                    && railFocus.appointmentIds.length > 0
                ? warned.filter((a) => a.direction === 'BLOCK'
                    || railFocus.appointmentIds.includes(String(a.appointmentId)))
                : warned;
            const byStatus = statusFilter.size === 0
                ? focused
                : focused.filter((a) =>
                      // Blocks are their own category (they carry a status code too,
                      // so keep them out of the status buckets to avoid cross-showing).
                      a.direction === 'BLOCK' ? statusFilter.has('BLOCK') : statusFilter.has(a.statusCode),
                  );
            /**
             * ---- ⭐⭐⭐ THE WORK FILTER, LAST, AS AN INTERSECTION (2026-09-24) ------
             *
             * ⛔⛔ LAST AND ANDING, for the same reason the warning filter is: it
             * NARROWS whatever is on the board rather than replacing it, so "Hellman's
             * arrived trucks that are being unloaded" is one sentence built from three
             * controls instead of three controls fighting.
             *   ** A SECOND FILTER IS AN INTERSECTION UNTIL SOMEBODY DECIDES
             *      OTHERWISE, AND NOBODY DECIDED OTHERWISE. **
             *
             * ⚠️ BLOCKS SURVIVE IT, exactly as they survive the party and warning
             * bounds above, and for the identical reason recorded there: a door outage
             * is the BUILDING's state, not a load's, and hiding it shows capacity that
             * does not exist — Pete would book into it. ⛔ The STATUS filter still
             * governs blocks, because that is a deliberate choice about blocks
             * themselves; this one is not.
             *
             * ⛔ `workKind` DECIDES, NOT A LOCAL EXPRESSION. The chip counts above call
             * the same function, so the number on the chip and the loads on the board
             * cannot disagree.
             */
            const byWork = workFilter.size === 0
                ? byStatus
                : byStatus.filter((a) => {
                      if (a.direction === 'BLOCK') return true;
                      const k = workKind(a.direction, a.laborActive, a.laborPaused);
                      return k !== null && workFilter.has(k);
                  });
            /**
             * ---- ⭐⭐⭐ THE WMS AXIS, THIRD AND ALSO ANDING (2026-09-27) ---------
             *
             * ⛔ SAME PLACE IN THE CHAIN AND SAME RULES AS THE WORK AXIS ABOVE, for
             * the same reasons recorded there: it NARROWS, blocks survive it, and the
             * chip counts below use the identical predicate so the number on the chip
             * and the loads on the board cannot disagree.
             *
             * ⚠️ `hasWmsOrder === null` MATCHES NEITHER CHIP, deliberately. The board
             * queries always compute it, so on this screen it should never be null —
             * but if a payload ever arrives without it, the honest behaviour is that a
             * truck nobody asked about is not claimed by either answer, rather than
             * being silently counted as "no WMS".
             *   ** NOT ASKED IS NOT THE SAME ANSWER AS NONE. **
             */
            return wmsFilter.size === 0
                ? byWork
                : byWork.filter((a) => {
                      if (a.direction === 'BLOCK') return true;
                      if (a.hasWmsOrder === null || a.hasWmsOrder === undefined) return false;
                      return wmsFilter.has(a.hasWmsOrder ? 'HAS_WMS' : 'NO_WMS');
                  });
        },
        /* ⚠️ `railFocus` JOINED THIS LIST 2026-09-27 with the chip bound above. Omitting
           it is the stale-memo bug `boardApptIds` already warns about, and here it would
           read as "the first chip I clicked is the only one that narrows". */
        [statusFilter, workFilter, wmsFilter, partyScope, warningScope, railFocus, subject],
    );

    const [calendar, setCalendar] = useState<CalendarDayDto | null>(null);
    const [rangeData, setRangeData] = useState<CalendarRangeDto | null>(null);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState<string | null>(null);

    /**
     * ---- ⭐⭐⭐ WHAT IS ON THE BOARD, AS A SET — SO THE RAIL CAN AGREE ----------
     *
     * > Pete, 2026-09-25: *"when you click loading, it sets the filter correctly on
     * > the calendar but it doesn't filter the drivers/client/carrier/consignee and
     * > the right side panels to those appointments"* — then, a minute later:
     * > *"I guess that is the same with all the status' too"*.
     *
     * ⛔⛔ HE IS RIGHT TWICE, AND THE SECOND ONE IS THE REAL FINDING. The rail has
     * NEVER followed the board — not for status, not for warnings, not for the work
     * kind. `<CheckInPanel appointments={calendar.appointments} />` passed the RAW
     * day list, so `partyRows` counted every appointment of the day however the board
     * was filtered.
     *   ** THE NEW CONTROL DID NOT BREAK IT. IT ASKED THE FIRST QUESTION THE OLD
     *      CONTROLS NEVER MADE ANYONE ASK. ** Nobody filters by status and then reads
     *      the party cards as a set; "show me what is being unloaded" is the first
     *      filter whose answer the cards obviously ought to share.
     *
     * ---- ⛔⛔ AND IT IS A SET, NOT A FILTERED LIST. READ THIS BEFORE "SIMPLIFYING" -
     *
     * ⚠️ THE OBVIOUS CHANGE IS WRONG AND I NEARLY MADE IT. Passing
     * `filterAppts(calendar.appointments)` as the `appointments` prop is one
     * character of work and it breaks the panel, because that prop is not the panel's
     * VIEW — its own docstring says *"Today's appointments, for the attach picker"*,
     * and ScanFolderPanel's says *"what a document can be filed onto"*. It feeds
     * `apptById` (the status word beside every driver's name), `attachable`, and the
     * signing lookup. Narrow it and a truck whose appointment is filtered off the
     * board can no longer be ATTACHED to it, and its card loses its status word.
     *   ** A FILTER THAT NARROWS WHAT YOU CAN SEE MUST NOT NARROW WHAT YOU CAN DO. **
     *   ** A PROP WHOSE DOCSTRING NAMES A CAPABILITY IS NOT A VIEW, HOWEVER MUCH IT
     *      LOOKS LIKE ONE FROM THE CALL SITE. **
     * So `appointments` stays whole and this rides beside it — a second axis, which
     * is the same answer WorkFilterBar itself reached about the status row.
     *
     * ⚠️ `boardFiltered` IS SEPARATE FROM THE SET AND IS NOT `size < total`. A filter
     * that happens to match everything is still ON, and the panel has to say so —
     * CheckInPanel already carries the rule (*"a filter you cannot see is a filter you
     * do not know is on"*) and this must not become the exception to it.
     */
    /* ⚠️ THE CHIP BOUND COUNTS TOO, from 2026-09-27. A board narrowed to one load is a
       narrowed board, and a driver click has always flagged this via `partyScope` — a
       chip click now narrows by the same amount and must not be the one bound that
       says nothing. ** A FILTER YOU CANNOT SEE IS A FILTER YOU DO NOT KNOW IS ON. ** */
    const boardFiltered =
        statusFilter.size > 0 || workFilter.size > 0 || wmsFilter.size > 0
        || warningScope !== null || partyScope !== null
        || (railFocus !== null && railFocus.kind === 'appointment');

    /**
     * ⛔ DAY ONLY, because CheckInPanel is day only. Week and month draw
     * `filterAppts(rangeData.appointments)` and have no rail beside them.
     * ⚠️ IT RUNS `filterAppts` AGAIN RATHER THAN SHARING THE RENDER'S RESULT. The
     * render calls it inline at three sites; reaching into one of those would make
     * this depend on which view is mounted, which is exactly the bug class above.
     */
    const boardApptIds = useMemo(
        () => new Set<string>(
            (calendar ? filterAppts(calendar.appointments) : [])
                .map(a => String(a.appointmentId)),
        ),
        [calendar, filterAppts],
    );

    // Status counts for the legend/filter, from the CURRENT view's dataset — so
    // they re-populate when switching Day/Week/Month.
    const { statusCounts, totalCount } = useMemo(() => {
        const src = viewMode === 'day' ? (calendar?.appointments ?? []) : (rangeData?.appointments ?? []);
        const m: Record<string, number> = { REQUESTED: 0, APPROVED: 0, ARRIVED: 0, DEPARTED: 0, NOSHOW: 0, CANCELLED: 0, BLOCK: 0 };
        for (const a of src) {
            if (a.direction === 'BLOCK') m.BLOCK += 1;
            else if (a.statusCode in m) m[a.statusCode] += 1;
        }
        return { statusCounts: m, totalCount: src.length };
    }, [viewMode, calendar, rangeData]);

    /**
     * ⭐⭐ THE WORK CHIPS' COUNTS — OVER THE **UNFILTERED** VIEW, exactly as above.
     *
     * ⛔ NOT over `filterAppts(...)`. §1AV.157 records what that costs: the Gantt's
     * old header counted the FILTERED set, so choosing one chip drove every other
     * count to zero.
     *   ** A COUNT COMPUTED FROM WHAT SURVIVED THE FILTER CANNOT TELL YOU WHAT THE
     *      FILTER IS HIDING, WHICH IS THE ONLY QUESTION IT IS ON SCREEN TO ANSWER. **
     *
     * ⛔ AND IT USES `workKind`, THE SAME FUNCTION `filterAppts` USES. A count and
     * the filter it labels must be one predicate called twice — the argument
     * `lib/partyRows.ts` opens with, and the reason `apptMatchesParty` is exported.
     */
    const workCounts = useMemo(() => {
        const src = viewMode === 'day' ? (calendar?.appointments ?? []) : (rangeData?.appointments ?? []);
        const m: Record<string, number> = { LOADING: 0, UNLOADING: 0 };
        for (const a of src) {
            const k = workKind(a.direction, a.laborActive, a.laborPaused);
            if (k) m[k] += 1;
        }
        return m;
    }, [viewMode, calendar, rangeData]);

    /**
     * ⭐⭐ THE WMS CHIPS' COUNTS — OVER THE **UNFILTERED** VIEW, a third time.
     *
     * ⛔ NOT over `filterAppts(...)`, for the reason §1AV.157 records and the block
     * directly above repeats. ⭐ AND THE KEYS COME FROM `WMS_FILTER_KINDS`, not from
     * two string literals typed here: the bar reads the same table to draw the chips,
     * so a code renamed in one place cannot leave a chip permanently reading 0.
     *   ** A COUNT KEYED BY A LITERAL THAT DUPLICATES A CONTRACT FAILS SILENTLY,
     *      BECAUSE ZERO IS A PLAUSIBLE ANSWER. **
     *
     * ⚠️ BLOCKS ARE NOT COUNTED. A door outage has no load, so it has no order to
     * match — counting it under NO WMS would put the building's state in a tally of
     * trucks, and the filter itself lets blocks through both chips untouched.
     */
    const wmsCounts = useMemo(() => {
        const src = viewMode === 'day' ? (calendar?.appointments ?? []) : (rangeData?.appointments ?? []);
        const m: Record<string, number> = {};
        for (const { code } of WMS_FILTER_KINDS) m[code] = 0;
        for (const a of src) {
            if (a.direction === 'BLOCK') continue;
            if (a.hasWmsOrder === null || a.hasWmsOrder === undefined) continue;
            m[a.hasWmsOrder ? 'HAS_WMS' : 'NO_WMS'] += 1;
        }
        return m;
    }, [viewMode, calendar, rangeData]);

    /**
     * ---- ⭐⭐ THE PARTIES ON THE BOARD, FOR THE SEARCH TO OFFER (2026-09-25) ----
     *
     * > Pete asked for typing a client name to scope the calendar to that client,
     * > "and that will actually be the same for carrier, consignee".
     *
     * ⛔⛔ MOST OF THAT ALREADY EXISTED AND WAS UNREACHABLE. `AppointmentSearch`
     * has offered "All <party> appointments" rows since decision 188, and
     * `onScopeParty` already takes a KIND. Two things stopped it working:
     *
     *   1. the offers were derived from the search HITS, and the memo opens with
     *      `if (needle.length < 2 || hits.length === 0) return []` — so a client
     *      with no matching appointment in the window offered nothing at all;
     *   2. it only ever read `clientCode` and `carrierCode`. **The consignee arm
     *      was never written**, despite the callback accepting one.
     *
     *   ** A FEATURE THAT ONLY WORKS WHEN THE SEARCH ALREADY SUCCEEDED CANNOT HELP
     *      THE CASE THE USER IS IN WHEN THEY NEED IT. **
     *
     * ⭐ THIS IS THE FALLBACK SOURCE, AND IT KEEPS THE ORIGINAL CONSTRAINT. That
     * memo's note insists on "no backend work and no second fetch" — these are the
     * appointments already on screen, so nothing is fetched and nothing is asked of
     * the server.
     *
     * ⚠️ AND IT IS THE RIGHT SET RATHER THAN A WIDER ONE. The board's own comment
     * on `onScopeParty` says a scope must be built from what the screen is showing,
     * because the search spans dates and the grid stands on one.
     *   ** A SCOPE MUST BE BUILT FROM WHAT THE SCREEN IS SHOWING. **
     *
     * ⚠️ CONSIGNEES ARE AN ARRAY, not a flat pair like client and carrier — one
     * appointment can carry several, which is why `ConsigneeDto` exists at all.
     */
    const boardParties = useMemo(() => {
        const src = viewMode === 'day' ? (calendar?.appointments ?? []) : (rangeData?.appointments ?? []);
        const seen = new Map<string, { kind: 'client' | 'carrier' | 'consignee'; code: string; name: string }>();
        const add = (kind: 'client' | 'carrier' | 'consignee', code: string | null, name: string | null) => {
            if (!code) return;
            const key = `${kind}:${code}`;
            if (!seen.has(key)) seen.set(key, { kind, code, name: name || code });
        };
        for (const a of src) {
            add('client', a.clientCode, a.clientName);
            add('carrier', a.carrierCode, a.carrierName);
            for (const c of a.consignees ?? []) add('consignee', c.consigneeCode, c.consigneeName);
        }
        return [...seen.values()];
    }, [viewMode, calendar, rangeData]);

    const [selectedAppt, setSelectedAppt] = useState<AppointmentDto | null>(null);
    /** ⭐ #306 — the load an admin is repairing the status of. Null = dialog shut. */
    const [overrideAppt, setOverrideAppt] = useState<AppointmentDto | null>(null);
    const [editingAppt, setEditingAppt] = useState<AppointmentDto | null>(null);
    /**
     * ---- ⭐⭐ DOUBLE CLICK ON WEEK / MONTH OPENS THE EDITOR (Pete, 2026-09-27) ----
     *
     * > *"Double-click on Week and Month view should pull the appointment into EDIT"*
     *
     * ⛔⛔ IT CLEARS `selectedAppt`, AND THAT IS NOT TIDINESS. A double click fires
     * the single click FIRST — twice — and on both boards the single click opens the
     * DETAIL dialog. Without this line the editor would open on top of a detail
     * dialog nobody asked for, and closing the editor would reveal it.
     *   ** A DOUBLE CLICK IS TWO SINGLE CLICKS PLUS AN EVENT. THE HANDLER THAT WINS
     *      HAS TO UNDO THE ONE THAT ALREADY RAN. **
     *
     * ⭐ AND IT UNDOES RATHER THAN DEBOUNCES. The usual answer — hold the single
     * click for 250ms to see whether a second arrives — puts a quarter-second of lag
     * on every ordinary click to serve the rarer gesture. Closing one dialog as the
     * other opens costs nothing and keeps the single click instant.
     *
     * ⚠️ DAY IS NOT CHANGED AND NOW DIFFERS. The Gantt's double click opens the
     * DETAIL dialog (decision 190/191, *"DOUBLE CLICK OPENS"*), so the same gesture
     * means two things depending on the view. That is what was asked for — Week and
     * Month have no other route to the editor but the right-click menu, while Day
     * has several — but it is an asymmetry, recorded here so it is a choice and not
     * a drift. ⬜ Levelling it either way is a one-line change at this callback.
     */
    const openEditFromBoard = useCallback((a: AppointmentDto) => {
        setSelectedAppt(null);
        setEditingAppt(a);
    }, []);

    // Admin-gated door editing from the calendar (door endpoints require admin).
    const [isAdmin, setIsAdmin] = useState(false);
    const [editingDoor, setEditingDoor] = useState<DoorAdminRow | null>(null);

    // Settings dialog
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsTab, setSettingsTab] = useState<
        'facilities' | 'doors' | 'capabilities' | 'carriers' | 'clients' | 'consignees' | 'users' | 'carrier-users' | 'facility-hours' | 'appointment-limits' | 'door-dedications' | undefined
    >(undefined);
    /** ⭐ F130: the record a party-card double-click asked Settings to open on. */
    const [settingsFocus, setSettingsFocus] = useState<string | null>(null);

    // Listen for the global gear-menu clicks in App.tsx. Each gear menu
    // item dispatches this event with a `tab` payload so we can open the
    // Settings dialog on the right tab.
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<{ tab?: typeof settingsTab }>).detail;
            if (detail?.tab) setSettingsTab(detail.tab);
            setSettingsFocus(null);
            setSettingsOpen(true);
        };
        window.addEventListener('dockscheduler:open-settings', handler);
        return () => window.removeEventListener('dockscheduler:open-settings', handler);
    }, []);

    // Switch the board live when the user changes their default facility in the
    // header (only if it's a facility they can see). Cleared default = no change.
    useEffect(() => {
        const onDefault = (e: Event) => {
            const code = (e as CustomEvent<{ facilityCode: string | null }>).detail?.facilityCode;
            if (!code) return;
            setLookups(lk => {
                if (lk && lk.facilities.some(f => f.FacilityCode === code)) setFacilityId(code);
                return lk;
            });
        };
        window.addEventListener('dockscheduler:default-facility-changed', onDefault);
        return () => window.removeEventListener('dockscheduler:default-facility-changed', onDefault);
    }, []);

    // New-appointment dialog
    const [newOpen, setNewOpen] = useState(false);
    // Mass-create day-only appointments (#66). Its own dialog rather than a mode
    // of New Appointment: that form asks for a door, a time and a PO, and all
    // three are the fields this feature exists to leave empty.
    const [massOpen, setMassOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    /**
     * ⭐ BACKING OUT THE DAY'S UPLOAD (#139). Pete: *"so if it still doesn't look
     * right, you can back it out."*
     *
     * ⛔ THE DAY ON SCREEN ONLY — his decision. `backoutFor` is the committed upload
     * found for THIS date, or null when there is none, and the button does not exist
     * when it is null. ⚠️ A control that appears and then says "nothing to do" trains
     * people to ignore it.
     */
    const [backoutFor, setBackoutFor] = useState<BackoutSourceDto | null>(null);
    const [backoutConfirm, setBackoutConfirm] = useState(false);
    const [backoutBusy, setBackoutBusy] = useState(false);
    const [backoutResult, setBackoutResult] = useState<UploadBackoutResult | null>(null);

    /**
     * The load whose paperwork is open (#144). ⭐ Carries the NUMBER and the
     * CARRIER as well as the id: the dialog titles itself with the appointment
     * number, and SignDocumentDialog scopes "where did this carrier's stamp go last
     * time" by carrier — passing only the id would mean two more lookups inside a
     * dialog that already has the row in hand.
     */
    /**
     * ⭐ ONE OF THE TWO IS SET, NEVER BOTH. A load's paperwork is gathered from its
     * DOSSIER (which already includes every arrival's documents); an arrival with
     * no load yet has only its own. Modelling it as two optional ids rather than
     * two pieces of state keeps the dialog's "which source?" decision in one place.
     */
    /**
     * ⭐ THE DIRECT SIGN PATH (#245). Pete asked for the intermediate screen to
     * appear only when a choice has to be made, so when there is exactly one
     * original this dialog opens on its own rather than behind the list.
     *
     * ⚠️ A SECOND MOUNT OF SignDocumentDialog, and that is safe because it is
     * self-contained: it takes a document and gives back a signed copy. The
     * documents dialog keeps its own instance for the choose-from-the-list path.
     */
    /**
     * ⭐ WHAT OCR MADE OF A DROPPED PAGE (#191/#221), held until the scheduler
     * answers. Only ever set for an arrival with NO load yet — Pete, 2026-08-25:
     * *"If not already associated"*.
     */
    const [suggested, setSuggested] = useState<
        { result: SuggestLoadDto; documentId: string; documentName: string; checkInId: string } | null
    >(null);
    const [signDirect, setSignDirect] = useState<
        { doc: DocumentDto; appointmentNumber: string | null; carrierCode: string | null } | null
    >(null);
    const [docsFor, setDocsFor] = useState<
        {
            appointmentId: string | null;
            checkInId?: string | null;
            appointmentNumber: string | null;
            carrierCode: string | null;
        } | null
    >(null);
    const [newPrefill, setNewPrefill] = useState<{
        dockDoorId: number | null;
        startLocal: string;
        endLocal: string;
        direction?: 'INBOUND' | 'OUTBOUND' | 'BLOCK';
        carrierCode?: string | null;
        poNumber?: string | null;
        referenceNumber?: string | null;
        driverName?: string | null;
        driverPhone?: string | null;
    } | null>(null);

    /**
     * The arrivals the panel is currently showing, by id.
     *
     * ⚠️ A REF, NOT STATE. It exists only so a DROP can look up the card behind
     * the id the grid hands over; re-rendering the page every time the panel
     * polls would be a cost for nothing.
     */
    const checkInsById = useRef<Map<string, CheckInDto>>(new Map());
    /**
     * ⭐⭐ WHO IS ON THIS LOAD — the join only this page can make (Pete, 2026-09-20).
     *
     * ⭐ *"When we are on the client or carriers tab, even if we click the appointment, we
     * dont see the driver."*
     *
     * ⛔ THE NAME LIVES ON THE **ARRIVAL**, not on the appointment. `CheckInPanel` owns the
     * arrivals and reports them up through `onListChanged`; the grid owns the loads. Neither
     * half can name the driver of a clicked chip on its own.
     * ⚠️ A REF, NOT STATE — it is read at click time, never rendered from, and the list it
     * mirrors already re-renders this page when it changes.
     */
    const driverNameForAppt = useCallback((appointmentId: string | number): string | null => {
        const id = String(appointmentId);
        for (const c of checkInsById.current.values()) {
            if (c.appointmentId != null && String(c.appointmentId) === id) return c.driverName?.trim() || null;
        }
        return null;
    }, []);

    /** Everything a check-in knows that a new appointment can start from. */
    const prefillFromCheckIn = (c: CheckInDto) => ({
        carrierCode: c.carrierCode,
        poNumber: c.poNumber,
        referenceNumber: c.referenceNumber ?? c.containerNumber,
        driverName: c.driverName,
        driverPhone: c.driverPhoneEntered,
        direction: (c.direction === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND') as 'INBOUND' | 'OUTBOUND',
    });

    // Right-click on empty door-row space -> "Block this door".
    const [doorMenu, setDoorMenu] = useState<RequestDoorMenuEvent | null>(null);

    // Right-click context menu
    const [contextMenu, setContextMenu] = useState<{
        appt: AppointmentDto;
        x: number;
        y: number;
    } | null>(null);

    // Right-click menu for a facility-limit window (turn off / edit).
    const [limitMenu, setLimitMenu] = useState<{
        bands: { capacityId: number; direction: string; startMinutes: number; endMinutes: number; maxConcurrent: number; effectiveStartDate: string | null; effectiveEndDate: string | null }[];
        x: number;
        y: number;
    } | null>(null);
    const [dedicationMenu, setDedicationMenu] = useState<RequestDedicationMenuEvent | null>(null);
    const [pendingLimitEdit, setPendingLimitEdit] = useState<EditLimitTimesEvent | null>(null);
    const [pendingDedicationEdit, setPendingDedicationEdit] = useState<EditDedicationTimesEvent | null>(null);

    // Send-directions dialog (opened from the right-click menu). Facility comes
    // from the appointment; recipient is entered ad-hoc (the driver(s)).
    const [sendDirFacility, setSendDirFacility] = useState<{
        code: string; label?: string;
        drivers?: { name: string; phone: string }[];
        /** Set when opened from an appointment, so the message can name it
         *  (2026-08-17). Only the id travels — the server resolves the number,
         *  window and PO. Unset when opened from Settings > Directions. */
        appointmentId?: number;
    } | null>(null);
    /**
     * ⭐ #324 — the delivery-pin dialog. ⚠️ Only the appointment id and the driver
     * phones travel; the message itself is composed server-side from the address
     * book, so nothing here can author text that lands on a driver's phone.
     */
    const [pinAppt, setPinAppt] = useState<{
        appointmentId: string;
        drivers: { name: string; phone: string }[];
    } | null>(null);
    const [driverAssignAppt, setDriverAssignAppt] = useState<AppointmentDto | null>(null);

    // Toast for transient errors (mostly reschedule conflicts)
    const [toast, setToast] = useState<{ kind: 'error' | 'success'; msg: string } | null>(null);
    /**
     * ⭐ #150 — the paperwork sliver's own refresh signal and in-flight flag.
     *
     * ⚠️ SEPARATE FROM `refreshKey`. That one reloads the arrivals panel on a 30-second
     * poll of its own; bumping it to refresh the sliver would restart that poll and
     * make the driver list jump every time a document was filed.
     */
    const [paperworkKey, setPaperworkKey] = useState(0);
    const [paperworkBusy, setPaperworkBusy] = useState(false);
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    // In-app "notify?" prompt, opened after a notifiable action (move/resize,
    // approve, cancel, no-show). Both the carrier email and the driver text are
    // opt-in, and as of 2026-08-17 they are chosen SEPARATELY.
    //
    // `drivers` and `carrierName` are carried so the prompt can name exactly who
    // is about to be contacted. Both come off the AppointmentDto we already
    // hold — no extra fetch, and no prompt that says "text drivers" for an
    // appointment that hasn't got any.
    const [notifyPrompt, setNotifyPrompt] = useState<{
        appointmentId: number;
        appointmentNumber: string;
        event: NotifyEvent;
        drivers: AppointmentDriverDto[];
        carrierName: string | null;
    } | null>(null);

    // Facility-cap warning on a drag move — offer to move anyway.
    const [capOverride, setCapOverride] = useState<{
        ev: MoveAppointmentEvent;
        appointmentNumber: string;
        title: string;
        detail: string;
    } | null>(null);
	
	const [assignDoor, setAssignDoor] = useState<{ appt: AppointmentDto } | null>(null);
    const [assignBusy, setAssignBusy] = useState(false);
    /**
     * ⛔ An outbound load the server refused to release until its warnings are
     * acknowledged (#97). Same shape as the missing-door fallback above: the
     * action is attempted, the server says what is wrong, and a dialog collects
     * what it needs rather than the UI trying to predict the refusal.
     */
    const [releaseFor, setReleaseFor] = useState<{ appt: AppointmentDto } | null>(null);
    const [releaseBusy, setReleaseBusy] = useState(false);
    /**
     * The appointment whose driver is being texted his dock door.
     *
     * ⭐ A STRING ID, NOT THE APPOINTMENT OBJECT. The dialog re-reads everything
     * from the server — door, recipients, message — so handing it a snapshot of
     * a row that may be several polls old would only invite it to render one
     * door and send another.
     */
    const [doorTextFor, setDoorTextFor] = useState<string | null>(null);
    /** Which of the two messages the dialog is about. */
    const [doorTextKind, setDoorTextKind] = useState<DoorTextKind>('door');
    // Quick action awaiting in-app confirmation (cancel / no-show / reject).
    const [pendingQuick, setPendingQuick] = useState<{
        appt: AppointmentDto;
        label: 'cancel' | 'noshow' | 'reject';
    } | null>(null);

    // Load lookups once on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await getToken();
                const [data, me] = await Promise.all([getLookups(token), getMe(token).catch(() => null)]);
                if (cancelled) return;
                setLookups(data);
                if (me) setIsAdmin(me.isAdmin);
                if (data.facilities.length > 0 && facilityId === null) {
                    const codes = data.facilities.map((f) => f.FacilityCode);
                    const preferred = me?.defaultFacilityCode && codes.includes(me.defaultFacilityCode)
                        ? me.defaultFacilityCode
                        : data.facilities[0].FacilityCode;
                    setFacilityId(preferred);
                }
            } catch (err) {
                if (cancelled) return;
                setLookupsError(formatError(err));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch window for week/month. Week = Sun–Sat containing `date`. Month =
    // the full 6-week visible grid (so adjacent-month cells are counted too).
    const rangeBounds = useMemo(() => {
        const d = parseISO(date);
        if (viewMode === 'week') {
            const s = startOfWeek(d, { weekStartsOn: 0 });
            return { start: format(s, 'yyyy-MM-dd'), end: format(addDays(s, 6), 'yyyy-MM-dd') };
        }
        // month
        const gridStart = startOfWeek(startOfMonth(d), { weekStartsOn: 0 });
        return { start: format(gridStart, 'yyyy-MM-dd'), end: format(addDays(gridStart, 41), 'yyyy-MM-dd') };
    }, [date, viewMode]);

    /**
     * ---- ⛔⛔ A FACILITY CHANGE CLEARS THE BOARD BEFORE IT REFILLS IT --------
     *
     * ⛔ MEASURED 2026-09-22. Brighton (262) has ONE limit band -- Inbound,
     * 15:00-17:00, max 4, effective 09/10 only. On 09/18 the board drew
     * `Limits IN OUT 7:00 AM-8:00 AM: max 3`: wrong direction, wrong time, wrong
     * max, and a date the band does not cover. `IN OUT max 3` is exactly what
     * DENVER's first band renders as. Pressing Refresh cleared it, which is the
     * correct answer for Brighton on that date.
     *
     * ⚠️ Pete read it as a per-ROW fault -- *"appointment limits isn't working on
     * the first record in the list... noticed this on dock door assignments
     * too"* -- because the row that looked wrong was the first row OF THE
     * PREVIOUS FACILITY, and the dedications come from the same payload.
     *   ** STALE DATA UNDER A NEW HEADING LOOKS LIKE A BUG IN THE ROWS. **
     *
     * ⛔ THE FETCH BELOW ALREADY DEPENDS ON `facilityId`; what it did not do was
     * DISCARD what it had while the new answer was in flight. This clears first
     * so a payload can never be drawn under a facility it did not come from.
     * ⚠️ The cost is a blank board for the length of one request. That is the
     * right trade: an empty board is obviously empty, and another site's limits
     * are not obviously another site's.
     *
     * ⚠️ DELIBERATELY ITS OWN EFFECT, KEYED ON THE FACILITY ALONE. Folding it
     * into the fetch below would clear on every DATE change too, and the board
     * would flash empty every time somebody stepped a day.
     */
    useEffect(() => {
        setCalendar(null);
        setRangeData(null);
    }, [facilityId]);

    // Load calendar on facility/date change
    useEffect(() => {
        if (facilityId === null) return;
        let cancelled = false;
        setCalendarLoading(true);
        setCalendarError(null);
        (async () => {
            try {
                const token = await getToken();
                if (viewMode === 'day') {
                    const data = await getCalendar(token, facilityId, date);
                    if (cancelled) return;
                    setCalendar(data);
                } else {
                    const data = await getCalendarRange(token, facilityId, rangeBounds.start, rangeBounds.end);
                    if (cancelled) return;
                    setRangeData(data);
                }
            } catch (err) {
                if (cancelled) return;
                setCalendarError(formatError(err));
                if (viewMode === 'day') setCalendar(null);
                else setRangeData(null);
            } finally {
                if (!cancelled) setCalendarLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, date, viewMode, rangeBounds.start, rangeBounds.end]);

    // Open the door editor from the calendar's door label. We build the door's
    // admin row from the calendar data we already have (capability codes -> ids
    // via lookups; IsActive is implicitly true since the calendar only renders
    // active doors), so no extra fetch is needed.
    const handleEditDoor = useCallback((dockDoorId: number) => {
        if (!calendar || !lookups) return;
        const door = calendar.doors.find((d) => d.dockDoorId === dockDoorId);
        if (!door) return;
        const codeToId = new Map(lookups.capabilities.map((c) => [c.Code, c.CapabilityId]));
        const row: DoorAdminRow = {
            DockDoorId:       door.dockDoorId,
            Code:             door.code,
            Name:             door.name,
            DoorGroup:        door.doorGroup,
            DisplayOrder:     door.displayOrder,
            MaxConcurrent:    door.maxConcurrent,
            AllowedDirection: door.allowedDirection,
            IsActive:         true,
            CapabilityIds:    door.capabilities
                .map((code) => codeToId.get(code))
                .filter((x): x is number => x != null),
        };
        setEditingDoor(row);
    }, [calendar, lookups]);

    const isToday = useMemo(() => dfIsToday(parseISO(date)), [date]);
    const todayStr = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

    // The Sunday of the viewed week (for the agenda columns).
    const weekStart = useMemo(
        () => format(startOfWeek(parseISO(date), { weekStartsOn: 0 }), 'yyyy-MM-dd'),
        [date],
    );

    // Prev/next step by the active unit: 1 day, 1 week, or 1 month.
    const shiftDate = (dir: number) => {
        const d = parseISO(date);
        if (viewMode === 'day') setDate(format(addDays(d, dir), 'yyyy-MM-dd'));
        else if (viewMode === 'week') setDate(format(addDays(d, dir * 7), 'yyyy-MM-dd'));
        else setDate(format(addMonths(d, dir), 'yyyy-MM-dd'));
    };

    // Jump to a specific day AND drop into the day board (from week/month click).
    const openDay = (d: string) => {
        setDate(d);
        setViewMode('day');
    };

    const reload = useCallback(async () => {
        if (facilityId === null) return;
        setCalendarLoading(true);
        setCalendarError(null);
        try {
            const token = await getToken();
            if (viewMode === 'day') {
                const data = await getCalendar(token, facilityId, date);
                setCalendar(data);
            } else {
                const data = await getCalendarRange(token, facilityId, rangeBounds.start, rangeBounds.end);
                setRangeData(data);
            }
        } catch (err) {
            setCalendarError(formatError(err));
        } finally {
            setCalendarLoading(false);
        }
    }, [facilityId, date, viewMode, rangeBounds.start, rangeBounds.end, getToken]);

    // Silent variant for the 30s auto-refresh. Unlike `reload`, it does NOT flip
    // the loading spinner or set `calendarError` — a transient poll failure must
    // not blow away the current view with an error pane or flicker the toolbar.
    // On success it swaps in fresh data; on failure it leaves the last-good view
    // in place and lets the next tick (or a manual Refresh) recover.
    /**
     * ---- ⛔⛔ WHY A BACKGROUND RELOAD MUST BE ABLE TO THROW ITS OWN ANSWER AWAY ----
     *
     * Pete, 2026-08-25: *"You place it in a time slot, sits there for a second or two,
     * then it moves back to its original position... then, about 10 seconds later the
     * appointment appears where its suppose to be."*
     *
     * ⭐ THAT IS ONE BUG WEARING TWO COSTUMES, AND THIS COUNTER IS THE FIRST HALF.
     *
     * A move applies an OPTIMISTIC patch, then awaits the server. That await is slow —
     * when the door changed it also sends the driver his door text, seven database
     * round trips and a Pipeline POST, all awaited before the 204 (see patchSchedule).
     * A 30-second poll firing anywhere in that window fetches the day as it was BEFORE
     * the write, comes back, and calls setCalendar with it — wiping the optimistic
     * patch and snapping the chip home. Nothing is broken on the server; the screen is
     * simply showing an answer that was already stale when it arrived.
     *
     * ⛔ SO A RELOAD REMEMBERS WHICH GENERATION IT WAS ASKED IN, and if any mutation
     * has happened while it was in flight it DISCARDS what it got. A stale read that
     * loses a user's action is worse than no read at all.
     *
     * ⚠️ NOT A LOCK. Reloads still run freely; they just cannot overwrite something
     * newer than themselves.
     */
    const mutationSeq = useRef(0);
    /** ⭐ True from the moment a write starts until it settles — suppresses polls. */
    const mutatingRef = useRef(false);

    const backgroundReload = useCallback(async () => {
        if (facilityId === null) return;
        const seq = mutationSeq.current;
        try {
            const token = await getToken();
            if (viewMode === 'day') {
                const data = await getCalendar(token, facilityId, date);
                if (mutationSeq.current !== seq) return;
                setCalendar(data);
            } else {
                const data = await getCalendarRange(token, facilityId, rangeBounds.start, rangeBounds.end);
                if (mutationSeq.current !== seq) return;
                setRangeData(data);
            }
        } catch {
            /* silent — see comment above */
        }
    }, [facilityId, date, viewMode, rangeBounds.start, rangeBounds.end, getToken]);

    // Live ref of "is the user mid-interaction?" — read inside the polling
    // interval without making it a dependency, so the 30s timer stays stable
    // instead of being torn down and recreated every time a dialog opens/closes.
    const interactingRef = useRef(false);
    interactingRef.current =
        // ⛔ A WRITE IN FLIGHT IS AN INTERACTION. Without this a poll can start during
        // the await and race the optimistic patch — the generation counter above then
        // has to catch it, and belt-and-braces is cheap here.
        mutatingRef.current ||
        selectedAppt !== null ||
        editingAppt !== null ||
        newOpen ||
        contextMenu !== null ||
        limitMenu !== null ||
        dedicationMenu !== null ||
        pendingLimitEdit !== null ||
        pendingDedicationEdit !== null ||
        assignDoor !== null ||
        sendDirFacility !== null ||
        driverAssignAppt !== null ||
        notifyPrompt !== null ||
        settingsOpen;

    // Auto-refresh the dock schedule every 30s so new carrier requests and
    // status changes from other schedulers appear without a manual Refresh.
    // A tick is skipped while a dialog or the context menu is open so a
    // background refresh never yanks state out from under an in-progress action.
    useEffect(() => {
        if (facilityId === null) return;
        const id = setInterval(() => {
            if (interactingRef.current) return;
            void backgroundReload();
        }, 30000);
        return () => clearInterval(id);
    }, [facilityId, backgroundReload]);

    // Refresh as soon as the Settings dialog closes, so door / hours / limit /
    // dedication edits show on the board without a manual Refresh. (Polls are
    // skipped while Settings is open, so we'd otherwise wait up to 30s.)
    const prevSettingsOpen = useRef(false);
    useEffect(() => {
        if (prevSettingsOpen.current && !settingsOpen) void backgroundReload();
        prevSettingsOpen.current = settingsOpen;
    }, [settingsOpen, backgroundReload]);

    // Refresh when the user returns to the tab / window (e.g. after creating an
    // appointment elsewhere or coming back from another app).
    useEffect(() => {
        const refresh = () => { if (!interactingRef.current) void backgroundReload(); };
        const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [backgroundReload]);

    // ---- Create flow ----
    /**
     * Blocking a door is reactive work, so it starts where the scheduler already
     * is — the door's own row — not three clicks into a dialog. The block dialog
     * opens with this door and a two-hour window already filled in; multi-door
     * and the reason field are the same Block Time form as before.
     */
    const handleRequestDoorMenu = (ev: RequestDoorMenuEvent) => setDoorMenu(ev);

    /**
     * An unmatched arrival dropped onto a driverless appointment. The grid has
     * already refused every appointment that should not accept one, so by the
     * time this runs the only question left is whether the write succeeds.
     */
/**
 * ---- ⛔⛔⛔ EVERY ANSWER A DROP HAS ALREADY BEEN GIVEN -------------------
 *
 * ⛔⛤ ~~THREE POSITIONAL BOOLEANS~~ — REPLACED 2026-09-23, AND IT FIXED A LIVE
 * LOOP RATHER THAN TIDYING A SIGNATURE.
 *
 * A drop can raise more than one question, and each prompt used to re-call the
 * handler with only ITS OWN answer — so the answers already given were dropped
 * back to their defaults. Dragging an ALREADY-ATTACHED arrival onto a load that
 * has a booked driver could not be completed at all:
 *
 *   move prompt -> confirmed  ->  driver conflict -> driver prompt
 *   driver prompt -> confirmed  ->  `confirmedMove` back to false -> move prompt
 *   …and round again, forever, with each dialog looking like it had worked.
 *
 * ⚠️ IT LOOKED LIKE A DOUBLE-CLICK, NOT A BUG. Each prompt is correct in
 * isolation; only the SECOND lap shows anything is wrong, and by then a scheduler
 * assumes they mis-clicked and tries again.
 *   ** A FLAG THAT ONLY THE CALLER REMEMBERS IS FORGOTTEN BY EVERY OTHER CALLER.
 *      CARRY THE ANSWERS, NOT ONE ANSWER. **
 *
 * ⭐ SO EACH PROMPT NOW STORES THE ANSWERS IT WAS RAISED WITH and re-issues them
 * all, plus its own. Adding #73's third question to the old shape would have made
 * a three-way carousel out of a two-way one.
 */
type AttachAnswers = {
    /** ⭐ #300 — set only by the driver prompt's Confirm. */
    replaceBookedDriver?: boolean;
    /** ⭐ Set only by the move prompt's Confirm — see `moveAsk`. */
    confirmedMove?: boolean;
    /**
     * ⭐ #73 — ⛔ THREE STATES. `undefined` means nobody has been asked and the
     * server should stop and ask; `false` is a person declining, which PROCEEDS
     * with the attach and leaves the booking's numbers alone.
     */
    overwriteVehicleNumbers?: boolean;
    /**
     * ⭐⭐ THE SECOND OVERWRITE ANSWER (2026-09-23) — ⛔ THREE STATES, same as the
     * one above, and ⛔⛔ A SEPARATE FIELD RATHER THAN A SHARED ONE.
     *
     * Pete refused a combined prompt: *"A booking's PO is the thing the whole load
     * is identified by downstream; a trailer number is a description of the box in
     * the yard. Putting them behind one button would make the cheap mistake and the
     * expensive one cost the same click."*
     *   ** THE FIELDS A PROMPT COVERS ARE PART OF THE PROMPT'S MEANING. **
     * ⚠️ One shared flag would mean confirming a trailer number silently confirmed
     * a PO overwrite — exactly the click he separated them to prevent.
     */
    overwritePaperworkNumbers?: boolean;
};

    const handleDropCheckInOnAppointment = async (
        ev: DropCheckInOnAppointmentEvent,
        answers: AttachAnswers = {},
    ) => {
        const { replaceBookedDriver = false, confirmedMove = false } = answers;
        /**
         * ---- ⛔⛔ MOVING AN ARRIVAL IS NOT COPYING IT (Pete, 2026-09-10) -----
         *
         * ⚠️ ASKED BEFORE ANYTHING IS WRITTEN. `attachCheckIn` re-points the
         * arrival, so the load it came from is left with NO arrival — and that is
         * invisible from the grid, because an appointment with no truck looks
         * exactly like one whose truck has not turned up yet.
         *
         * ⭐ AND THE RIGHT ANSWER IS USUALLY A SECOND CHECK-IN. Pete, 2026-09-10:
         * *"A driver should always have a driver checkin per load/unload… if a
         * driver delivers three inbounds today, he should have 3 driver
         * checkins."* That is what keeps the arrival queue and the truck numbers
         * meaningful, and the returning-driver path already reuses his licence so
         * checking in again costs him nothing.
         */
        if (ev.fromAppointmentNumber
            && ev.fromAppointmentNumber !== ev.appt.appointmentNumber
            && !confirmedMove) {
            setMoveAsk({ ev, answers });
            return;
        }
        try {
            const res = (await attachCheckIn(
                await getToken(), ev.checkInId, String(ev.appt.appointmentId), replaceBookedDriver,
                /* ⛔ PASSED THROUGH UNDEFINED WHEN UNANSWERED. `?? false` here would
                   tell the server "a person said no" before anybody had been asked,
                   and the prompt would never appear. */
                answers.overwriteVehicleNumbers,
                answers.overwritePaperworkNumbers,
            )) as AttachResult | undefined;
            setToast({ kind: 'success', msg: `Driver attached to appointment ${ev.appt.appointmentNumber}.` });
            /* ---- ⛔⛤ `reload()` ALONE DOES NOT REFRESH THE RAIL (2026-09-23) ----

               ⚠️ `reload()` refetches the CALENDAR. `CheckInPanel` keeps its own
               arrival list from `getCheckIns` and reloads on `refreshKey`, whose
               deps are `[facilityCode, date, getToken, showVoided]` — none of which
               a successful attach changes. So the truck stayed on the unmatched
               list until something else happened to bump it.
                 ** TWO LISTS, TWO FETCHES, ONE EVENT — AND ONLY ONE OF THEM WAS
                    TOLD. **

               ⛔ EVERY OTHER CALLER ON THIS PAGE ALREADY PAIRS THEM
               (`{ setRefreshKey(k => k + 1); void reload(); }`, nine sites). This
               one did not, and the menu path now depends on it: the panel no longer
               reloads itself, because it no longer knows when the attach lands. */
            setRefreshKey(k => k + 1);
            void reload();
            /**
             * ---- ⭐⭐ THE PALLET DISAGREEMENT (Pete, 2026-09-23) ---------------
             *
             * > *"warnings should pop if pallet count doesn't match the appointment
             * > pallet count."*
             *
             * ⛔ THE ATTACH ALREADY HAPPENED. This is not a conflict prompt — nothing
             * is waiting on the answer and nothing is undone by dismissing it. The
             * server logs a `PalletVar` event either way, under the older rule that
             * *"the disagreement is the record"*.
             *
             * ⚠️ IT DEFERS THE DOOR TEXT RATHER THAN STACKING ON TOP OF IT. Two
             * dialogs opening in the same tick is a scheduler answering whichever one
             * happens to be on top.
             *   ** TWO DIALOGS FIRED FROM ONE EVENT IS ONE DIALOG AND AN ACCIDENT. **
             */
            const hints = res?.shapeHints ?? [];
            if (res?.palletVariance || hints.length > 0) {
                setAttachNotice({
                    appt: ev.appt,
                    variance: res?.palletVariance ?? null,
                    hints,
                });
            } else {
                // ⭐ THE MOMENT HE NEEDS TELLING. A truck just matched is a truck
                // whose driver is in the lot not knowing where to go. Offered as a
                // dialog, never sent on his behalf.
                setDoorTextKind('door');
                setDoorTextFor(String(ev.appt.appointmentId));
            }
        } catch (err) {
            /**
             * ⭐⭐ #300: "ALREADY BOOKED TO SOMEBODY" IS A QUESTION, NOT AN ERROR.
             *
             * Pete: *"it should pop a screen to ask if we overwrite it mentioning this
             * driver was not booked originally on this appointment."*
             *
             * ⛔ NOTHING HAS BEEN CHANGED WHEN THIS FIRES. The server checks before it
             * writes, so answering "no" leaves the load exactly as it was — which is
             * what makes it safe to ask rather than to undo.
             */
            const conflict = driverConflict(err);
            if (conflict) {
                setDriverOverwrite({ ev, answers, bookedDrivers: conflict.bookedDrivers });
                return;
            }
            /**
             * ⭐⭐ #73 — "THE BOOKING SAYS ANOTHER BOX" IS A QUESTION TOO.
             *
             * > Pete, 2026-09-23: *"When we drag the driver to an appointment, can we
             * > ask if we want to overwrite the container or trailer number on the
             * > appointment?"*
             *
             * ⛔ NOTHING HAS BEEN CHANGED WHEN THIS FIRES, exactly like #300 above:
             * the server compares before it writes, so BOTH answers are safe to ask
             * for. ⚠️ And "no" is a real answer here — it attaches the truck and
             * leaves the booking's numbers alone, which is the behaviour Pete chose
             * on 2026-08-20 and did not ask to remove.
             */
            const vehicle = vehicleNumberConflict(err);
            if (vehicle) {
                setVehicleOverwrite({ ev, answers, conflicts: vehicle.conflicts });
                return;
            }
            /**
             * ⭐⭐ AND THE SAME AGAIN FOR THE PAPERWORK (2026-09-23).
             *
             * ⚠️ A SECOND PROMPT IN SEQUENCE, not a second branch of the first. The
             * server asks about the box first and the paperwork second, so a
             * scheduler who answers both sees two dialogs — which is the price of
             * the expensive decision having its own click.
             * ⛔ NOTHING HAS BEEN CHANGED WHEN THIS FIRES: both checks run before the
             * UPDATE, so abandoning the second prompt leaves the load untouched.
             */
            const paper = paperworkNumberConflict(err);
            if (paper) {
                setPaperworkOverwrite({ ev, answers, conflicts: paper.conflicts });
                return;
            }
            // ⚠️ Say which appointment. "Attach failed" on a screen with forty
            // blocks on it tells a supervisor nothing they can act on.
            setToast({ kind: 'error', msg: `Couldn't attach that driver to ${ev.appt.appointmentNumber}: ${formatError(err)}` });
        }
    };

    /**
     * ---- ⭐ PAPERWORK DROPPED ON SOMETHING (#150) --------------------------------
     *
     * Pete, 2026-08-25: *"We will then drag the document to, or attach the document to
     * the driver or an appointment."*
     *
     * ⭐ ONE FUNCTION FOR BOTH TARGETS, BECAUSE THE HARD PART IS IDENTICAL. A scan has
     * to be pulled out of SharePoint before it can be filed anywhere, and duplicating
     * that in a grid handler and a panel handler is how the two quietly stop agreeing
     * about what happens when the ingest half succeeds and the file half does not.
     *
     * ⛔ AND IT LIVES HERE, NOT IN THE SLIVER. The drag SOURCE cannot own this: by the
     * time the drop fires the source may have been unmounted by a poll, and an error
     * raised inside a component that no longer exists is an error nobody sees.
     */
    /**
     * ⛔ ONE FETCH, ONE JUDGEMENT, TWO ACTIONS (#245/#245a).
     *
     * ⭐ THE PANEL DOES NOT DECIDE. It holds `paperworkCount` — a number, enough
     * to know whether to SHOW the icons and nothing like enough to know which
     * document was meant. Widening the day-list query to carry document ids would
     * put them on every truck on the board for the sake of one press, so the ids
     * are fetched here, on a deliberate click, where the wait is invisible.
     *
     * ⚠️ THE SOURCE DEPENDS ON THE SCOPE, and the two are genuinely different: an
     * arrival with no load has only its own paperwork, while a load's dossier
     * gathers the load's documents AND every arrival's. Reading the wrong one
     * would make the shortcut disagree with the list it falls back to.
     */
    /**
     * ⛔ ONE SCOPE TYPE, SO THE CARD AND THE RIGHT-CLICK CANNOT DISAGREE.
     *
     * ⚠️ These began as CheckInDto handlers, and the appointment menu would have
     * needed its own copy — which is exactly the drift #245a warns about, since
     * nothing fails when two copies of "which document did they mean" diverge.
     * Naming the SCOPE instead means both entry points run the same code.
     */
    type PaperworkScope = {
        appointmentId: string | null;
        checkInId: string | null;
        appointmentNumber: string | null;
        carrierCode: string | null;
    };

    const scopeOfCheckIn = (c: CheckInDto): PaperworkScope => {
        const a = c.appointmentId
            ? (calendar?.appointments ?? []).find(x => String(x.appointmentId) === c.appointmentId)
            : null;
        return {
            appointmentId: c.appointmentId,
            checkInId: c.appointmentId ? null : c.checkInId,
            appointmentNumber: a?.appointmentNumber
                ?? (c.driverName ? `${c.driverName} · arrival #${c.arrivalSeq ?? '?'}` : `arrival #${c.arrivalSeq ?? '?'}`),
            carrierCode: a?.carrierCode ?? c.carrierCode ?? null,
        };
    };

    const scopeOfAppointment = (a: AppointmentDto): PaperworkScope => ({
        appointmentId: String(a.appointmentId),
        checkInId: null,
        appointmentNumber: a.appointmentNumber,
        carrierCode: a.carrierCode,
    });

    /**
     * ⚠️ THE SOURCE DEPENDS ON THE SCOPE, and the two are genuinely different: an
     * arrival with no load has only its own paperwork, while a load's dossier
     * gathers the load's documents AND every arrival's. Reading the wrong one
     * would make the shortcut disagree with the list it falls back to.
     */
    const paperworkFor = async (scope: PaperworkScope): Promise<DocumentDto[]> => {
        const token = await getToken();
        if (!scope.appointmentId) {
            return (await getCheckInDocuments(token, scope.checkInId as string)).documents;
        }
        const r = await getAppointmentPaperworkDossier(token, scope.appointmentId);
        // ⛔ DEDUPED — the two lists overlap. See flattenDossierDocuments.
        return flattenDossierDocuments(r);
    };

    /** The list, when the shortcut cannot decide. */
    /** ⭐ #300 — the pending overwrite question, and the drop that raised it. */
    /** ⭐ A drop that would MOVE an already-attached arrival, awaiting a person. */
    const [moveAsk, setMoveAsk] = useState<
        { ev: DropCheckInOnAppointmentEvent; answers: AttachAnswers } | null
    >(null);

    /**
     * ---- ⭐⭐ A5 — AN ORDER DROPPED ONTO A LOAD (2026-09-28) ------------------
     *
     * > Pete, 2026-09-27: *"drag an order to an appointment an attach it … warning
     * > popup with the option to replace"*.
     *
     * ⭐ ASK THE SERVER WHERE THE ORDER ALREADY IS, THEN DECIDE. Nowhere else →
     * attach straight away. Somewhere else → the replace popup, nothing ticked.
     * ⚠️ The panel's `bookedOn` is not used for this: it mixes identifier matches
     * with links, and only links can be removed (PLAN §5).
     */
    const [orderAsk, setOrderAsk] = useState<
        { ev: DropWmsOrderOnAppointmentEvent; others: WmsOrderLinkDto[]; hiddenElsewhere: number } | null
    >(null);
    const [orderBusy, setOrderBusy] = useState(false);
    /** ⭐ D3 / Decision 238 — the order whose Match button opened the picker. */
    const [matchFor, setMatchFor] = useState<WmsOrderDragKey | null>(null);
    /** ⭐ The order's row, for the Match screen's right-hand side (Decision 241). */
    const [matchRow, setMatchRow] = useState<OrdersPanelRowDto | null>(null);
    /** ⭐ F107 — the Match screen in its No WMS list mode (null = closed). */
    const [noWmsList, setNoWmsList] = useState<{ rows: NoWmsRowDto[]; total: number } | null>(null);
    /** ⭐ Bumped after every attach, so the No WMS chip re-counts and an open list drops the matched load. */
    const [noWmsRefresh, setNoWmsRefresh] = useState(0);
    /** Fresh No WMS rows reach an OPEN list only; a closed one stays closed. */
    const onNoWmsRows = useCallback((rows: NoWmsRowDto[], total: number) => {
        setNoWmsList(prev => (prev ? { rows, total } : prev));
    }, []);

    const attachDroppedOrder = async (ev: DropWmsOrderOnAppointmentEvent, removeFrom: number[]) => {
        setOrderBusy(true);
        try {
            const res = await attachWmsOrder(await getToken(), ev.appt.appointmentId, ev.order, removeFrom);
            const off = res.removed.map(r => r.appointmentNumber);
            setToast({
                kind: 'success',
                msg: (res.attached
                    ? `Order ${ev.order.orderNumber} attached to ${res.appointmentNumber}.`
                    : `Order ${ev.order.orderNumber} was already attached to ${res.appointmentNumber}.`)
                    + (off.length ? ` Removed from ${off.join(', ')}.` : ''),
            });
            setOrderAsk(null);
            /* ⛔ BOTH, like every other attach on this page: `reload()` redraws the
               grid's WMS marks, `refreshKey` reloads the Orders panel's match list. */
            setRefreshKey(k => k + 1);
            setNoWmsRefresh(k => k + 1);
            void reload();
        } catch (err) {
            setToast({ kind: 'error', msg: `Could not attach ${ev.order.orderNumber}: ${err instanceof Error ? err.message : 'request failed'}` });
        } finally {
            setOrderBusy(false);
        }
    };

    const handleDropWmsOrder = async (ev: DropWmsOrderOnAppointmentEvent) => {
        try {
            const found = await getWmsOrderLinks(await getToken(), ev.order);
            const others = found.links.filter(l => l.appointmentId !== ev.appt.appointmentId);
            const already = found.links.some(l => l.appointmentId === ev.appt.appointmentId);
            if (others.length === 0 && found.hiddenElsewhere === 0) {
                if (already) {
                    setToast({ kind: 'success', msg: `Order ${ev.order.orderNumber} is already attached to ${ev.appt.appointmentNumber}.` });
                    return;
                }
                await attachDroppedOrder(ev, []);
                return;
            }
            setOrderAsk({ ev, others, hiddenElsewhere: found.hiddenElsewhere });
        } catch (err) {
            setToast({ kind: 'error', msg: `Could not check where ${ev.order.orderNumber} is attached: ${err instanceof Error ? err.message : 'request failed'}` });
        }
    };

    /**
     * ⭐ THE LOAD THE PAPERWORK TRAY IS CURRENTLY SHOWING, or null for the pile.
     * ⚠️ HELD HERE, NOT IN THE TRAY, because both trays need it and the grid is
     * what knows about the cursor — the same reason `hoverApptId` lives here.
     */
    const [attachedPaperwork, setAttachedPaperwork] = useState<AttachedTarget | null>(null);

    /**
     * ---- ⭐⭐ WHAT THE RAIL IS POINTED AT, AND IT LATCHES ------------------
     *
     * > Pete, 2026-09-10, on Communications, Labor, History and WMS Orders:
     * > *"We will need an ALL icon to reset it."*
     *
     * ⛔ THE ASK FOR A RESET CONTROL IS WHAT MAKES THIS A LATCH. A scope that
     * released itself when the cursor left would never need one. So hovering
     * SETS this and nothing but the ALL button clears it — see `lib/railFocus.ts`.
     *
     * ⚠️ IT LIVES HERE, ABOVE BOTH HOVER SOURCES AND ALL FIVE PANELS. The driver
     * rail and the grid each know half of it and none of the panels know either,
     * so anywhere lower would need it passed sideways.
     */
    /* ⛔ ONE WRITER. Every change goes through here so "remember it" cannot be
       forgotten at one of the three call sites that will exist. */
    /**
     * ---- ⛔⛔⚤ CHANGING THE CHIP **CLEARS THE SCOPE** — §1AV.174 REVERSED ---------
     *
     * ⭐ Pete, 2026-09-20, on a screenshot reading `Clients · A00000345 · PETER
     * LOMBARDO`: *"This is an error - showing the appt - driver not appt - client"*,
     * then: *"Since nothing was selected on client, just should say Client or similar
     * **but needs to clear the driver**"*.
     *
     * ⛔⚤ **THIS REVERSES A DECISION HE MADE THE DAY BEFORE**, §1AV.174:
     * ~~*"The chips keep the scope — what you look at and whose are two different
     * choices."*~~ ⭐ The original is a good argument and it was **tested by the screen
     * rather than by reasoning**, which is the only thing that could have settled it:
     * a driver scope surviving onto the Clients chip filtered the board to one load,
     * left all five rails reading `(driver)`, and put a man's name after the word
     * *Clients*. ⚠️ Two things being *different choices* does not make them
     * **independent** — the subject decides which scopes are even expressible.
     *   ** A DECISION THAT SOUNDS RIGHT IN PROSE IS STILL A HYPOTHESIS. THE SCREEN IS
     *      THE EXPERIMENT. **
     *
     * ⚠️ GUARDED ON AN ACTUAL CHANGE. Clicking the chip you are already on must not
     * become a second, undocumented reset — Ⓡ and the second click on a card are the
     * two resets, and a third one nobody named is how they drift apart.
     *
     * ⛔ THE ARRIVAL CARDS ARE **NOT** COLLAPSED HERE. Pete asked to clear the scope,
     * not to fold the rail — and Ⓡ is the gesture that touches cards (decision 194).
     *   ** BUILD THE SENTENCE THAT WAS SAID, NOT THE ONE IT RESEMBLES. **
     */
    /**
     * ---- ⭐⭐⭐ THE CHIP NO LONGER CLEARS THE SCOPE (D2, Decision 239, 2026-09-28) --
     *
     * > Pete, 2026-09-27: *"When I click driver, client, carrier, etc - it doesn't hold
     * > the scope"* — so you can pick a client and then see its drivers, carriers and
     * > consignees. Popup → *"Hold any scope, label it clearly (Recommended)"*.
     *
     * ⛔⤴ ~~`setPartyScope(null); setRailFocus(null); setAttachedPaperwork(null);`~~ —
     * the 2026-09-20 clear, REVERSED BY PETE with the reason it existed stated to him:
     * a driver scope carried onto Clients printed his name after the word *Clients*.
     * ⭐ THAT FAULT WAS THE LABEL, NOT THE HOLD, and the label is fixed where it is
     * drawn: line 1 now says `Clients · for driver Peter L.` (`panelHeadlineParts`'
     * third argument). The board and the cards stay narrowed to the chosen scope's
     * loads, which is exactly the drill-down asked for.
     *   ** WHEN A FIX REMOVED A BEHAVIOUR TO HIDE A WRONG LABEL, THE BEHAVIOUR COMES
     *      BACK THE DAY THE LABEL IS RIGHT. **
     * ⚠️ The two resets are unchanged: Ⓡ, and a second click on the chosen card.
     */
    const chooseSubject = useCallback((next: PanelSubject) => {
        if (next === subject) return;
        setSubject(next);
        writePanelSubject(facilityId ?? '', next);
    }, [facilityId, subject]);

    /* ⛔⤴ ~~`const [railFocus, setRailFocus] = useState<RailFocus | null>(null);`~~
       — MOVED 2026-09-27 to sit beside `partyScope`, because `filterAppts` now names
       it in a dependency array and would have hit the temporal dead zone here. §0.3:
       recorded rather than silently relocated, since everything below reads as though
       the declaration were still on this line. The full reason is at the new site. */
    /**
     * ⭐⭐ THE CURRENT FOCUS, READABLE FROM A `[]` CALLBACK. `showAttachedFor` is
     * built once and reads the board through refs; the second-click toggle
     * (decision 195 item 26) has to know what is ALREADY focused, and a state
     * value closed over at mount would answer for the first frame forever.
     * ⛔ Assigned during render, exactly like `apptsRef` twenty lines down — not
     * in an effect. An effect runs AFTER the click handler that reads it.
     *   ** A REF THAT IS WRITTEN IN AN EFFECT IS ONE EVENT BEHIND EVERY HANDLER
     *      THAT READS IT. **
     */
    const railFocusRef = useRef<RailFocus | null>(null);
    railFocusRef.current = railFocus;
    /** ⭐ The ALL button on every scoped panel. The ONLY way back to unscoped. */
    const clearRailFocus = useCallback(() => setRailFocus(null), []);

    /**
     * ---- ⭐⭐ THE LATCHED SCOPE HAS TO BE VISIBLE ON THE BOARD (Pete, 2026-09-19) --
     *
     * ⭐ *"The appointment also needs to stay highlighted after the mouse moves off the
     * hover so we know its still selected by looking at the border/highlight and what
     * is on the panels for appointment number"*.
     *
     * ⛔⛔ THE FOCUS ALREADY LATCHED; ONLY THE HIGHLIGHT DID NOT. §1AR settled the
     * behaviour on 2026-09-10 — *"hovering SETS the scope and only ALL clears it"* —
     * but `hoverApptId` is written by `onMouseEnter` and wiped by `onMouseLeave`, so
     * the moment the cursor left the chip the board went blank while all five panels
     * carried on showing that load. The screen stopped agreeing with its own state,
     * and the only way to find out WHICH load you were scoped to was to read the
     * panel headings and go hunting for the number.
     *   ** IF A GESTURE LATCHES, ITS HIGHLIGHT MUST LATCH WITH IT. **
     *
     * ⛔ A DERIVED VALUE, NOT A FOURTH PIECE OF STATE. `railFocus` is already the one
     * true answer to "what am I scoped to"; storing the id again would create two
     * things that must agree, and the one that lags is the one on screen.
     *
     * ⚠️ `kind === 'appointment'` GUARDS IT, not `appointmentId != null`. A DRIVER
     * focus also carries an `appointmentId` once the driver is matched to a load —
     * outlining the chip for a driver hover is arguably right, but it is a different
     * decision from the one Pete made here, and a truthy-id test would make it
     * silently, without either of us choosing it.
     *   ** A CONDITION THAT HAPPENS TO SELECT THE RIGHT ROWS IS NOT THE SAME AS ONE
     *      THAT SAYS WHAT YOU MEAN. **
     */
    /* ⛔⚤ ~~const latchedApptId = railFocus?.kind === 'appointment' ? ...~~ — SUPERSEDED
       2026-09-20 by decision 190's TWO RINGS. A single id could not express a CLIENT
       scope, which covers several appointments, and it lived here only because the old
       `hoverApptId ?? latchedApptId ?? focusApptId` chain was one value.
       ⭐ Replaced by `selectedApptIds`, declared AFTER `focusApptId` — it reads that
       state, and a memo placed here would be a temporal-dead-zone crash on first
       render. ** A DERIVED VALUE MUST BE DECLARED BELOW EVERYTHING IT DERIVES FROM. ** */

    /**
     * ---- ⭐⭐ ONE HANDLER, EVERY PLACE A LOAD CAN BE HOVERED ----------------
     *
     * > Pete, 2026-09-10: *"Also not working when I hover the driver card"*.
     *
     * ⛔ IT WAS WRITTEN INLINE ON `<GanttCalendar>` AND THAT IS EXACTLY HOW THE
     * DRIVER RAIL WAS MISSED. An inline handler is invisible to the question
     * "who else should have this?" — it is not a thing with a name to be passed
     * somewhere else, so nobody notices there is a somewhere else.
     *
     * ⚠️ THIS IS THE THIRD TIME (§4.14.2 five drag handlers, §4.14.3 two
     * paperwork trays). ⭐ Naming it makes the population countable, and the
     * frontend guard now asserts BOTH call sites rather than that one exists.
     *
     * ⛔ A BLOCK IS NOT A LOAD — it has no paperwork and never will, so dwelling
     * on one clears the tray rather than showing an empty list that reads as a
     * fault. The same is true of an arrival with no appointment yet, which is
     * why `CheckInPanel` passes null for those.
     */
    /**
     * ---- ⛔⛔ THE APPOINTMENTS COME FROM A REF, NOT FROM THE CLOSURE ---------
     *
     * ⚠️ THIS IS THE DEFENCE THAT WOULD HAVE MADE THE BUG HARMLESS. `GanttCalendar`
     * froze an old `showAttachedFor` in a `useCallback` whose deps omitted it, and
     * the copy it froze had `calendar === null` — the day had not loaded on first
     * render — so every appointment hover found no appointment and CLEARED the
     * tray. Fixing the deps fixes that instance; reading through a ref means a
     * stale capture of THIS function still resolves correctly.
     *
     * ⭐ AND IT MAKES THE IDENTITY STABLE. With `[calendar]` this function was new
     * on every reload, so any consumer that captures it is captured wrong within
     * seconds. With `[]` there is nothing to go stale.
     */
    const apptsRef = useRef<AppointmentDto[]>([]);
    apptsRef.current = calendar?.appointments ?? [];

    /**
     * ---- ⭐⭐ A SCAN DROPPED ON THE ATTACHED ICON ---------------------------
     *
     * > Pete, 2026-09-10: *"Can we drag a document from Scanned Paperwork to the
     * > paperwork toggle button (last appointment, driver in the history)?"*
     *
     * ⛔ IT GOES THROUGH `handleDropPaperwork`, THE SAME FUNCTION A DROP ON A
     * CHIP OR A DRIVER CARD USES. A second filing path would be a second set of
     * rules about where a document may land — and this one is reached by a
     * gesture, so the rules are exactly what nobody would re-read.
     *
     * ⚠️ REFUSED, NOT GUESSED, WITH NO TARGET. The icon is disabled until
     * something has been pointed at, and this checks again rather than trusting
     * that: a document filed against the wrong load is worse than one not filed.
     */
    const handleDropOnAttached = useCallback((e: React.DragEvent) => {
        const payload = readPaperworkDrag(e.dataTransfer);
        if (!payload) { setToast({ kind: 'error', msg: 'That drag carried nothing to file.' }); return; }
        if (!attachedPaperwork) {
            setToast({ kind: 'error', msg: 'Point at an appointment or a driver first — then drop it here.' });
            return;
        }
        void handleDropPaperwork(
            { items: payload },
            { appointmentId: attachedPaperwork.appointmentId },
            attachedPaperwork.appointmentNumber,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachedPaperwork]);

    /**
     * ---- ⭐⭐ A FILED DOCUMENT DROPPED BACK ON THE PILE ---------------------
     *
     * > Pete, 2026-09-10: *"so you can move it back from the Attached Paperwork
     * > to the Scanned Paperwork."*
     *
     * ⛔ ONLY DOCUMENTS, NEVER SCANS. A `scan` item is a file that is still in
     * SharePoint and was never filed anywhere — there is nothing to move it back
     * FROM, and calling unfile on it would be a 404 dressed up as a failure the
     * scheduler caused.
     *
     * ⚠️ EACH RESULT IS REPORTED, INCLUDING THE REFUSALS. A signed copy stays
     * with its load, and the server's sentence says so — swallowing it would make
     * a deliberate rule look like a dead control.
     */
    /**
     * ---- ⭐⭐ DROPPING ON THE PILE ICON: EVERYTHING THAT CAN GO THERE --------
     *
     * > Pete, 2026-09-10: *"Should let me move files between all three at
     * > anytime correct? Except when the Attached is not connected to an
     * > appointment or driver?"*
     *
     * ⭐ ALMOST EXACTLY RIGHT, and the one exception is not a design decision —
     * see `onDropLocal`. The pile takes THREE kinds of thing, because "put it
     * back in the queue" means something different for each:
     *
     *   a FILED DOCUMENT  → unfiled: it leaves the load and rejoins the queue.
     *   a LOCAL SCAN      → uploaded with NO load, which puts a file from the PC
     *                       into the queue without deciding whose it is.
     *   a SCAN ALREADY IN THE QUEUE → nothing to do, and it says so rather than
     *                       pretending to work.
     */
    const handleDropUnfile = useCallback((e: React.DragEvent) => {
        const items = readPaperworkDrag(e.dataTransfer);
        if (!items) { setToast({ kind: 'error', msg: 'That drag carried nothing to move.' }); return; }
        void (async () => {
            const token = await getToken();
            const moved: string[] = [];
            const refused: string[] = [];
            /** ⚠️ Uploaded, but still sitting on the disk — the same shape as
                `handleDropPaperwork`'s SharePoint `stuckInFolder`, on purpose. */
            const stuckInFolder: string[] = [];
            let alreadyThere = 0;
            for (const it of items) {
                try {
                    if (it.kind === 'document') {
                        await unfileDocumentApi(token, it.documentId);
                        moved.push(it.name);
                    } else if (it.kind === 'localScan') {
                        if (facilityId === null) throw new Error('no facility is selected.');
                        const local = lookupLocalScan(it.ref);
                        if (!local) throw new Error('that file is no longer in the scan folder.');
                        /* ⛔ NO appointmentId AND NO checkInId — that is what makes it
                           unfiled. `storeDocument` skips the conversion for exactly
                           this case: a scan nobody has claimed stays an image so the
                           tray has a picture to draw. */
                        await uploadDocument(token, facilityId, await local.getFile(), { source: 'Folder' });
                        moved.push(it.name);
                        /**
                         * ⛔⛔ AND THE ORIGINAL LEAVES THE FOLDER — Pete: *"Can we
                         * always make it move please?"* ⚠️ AFTER the upload returns,
                         * never before: `removeEntry` is unrecoverable and the
                         * upload is the only thing that proves Dock has the bytes.
                         *
                         * ⛔ A FAILURE HERE IS ITS OWN SENTENCE. The document IS in
                         * Dock; the file is merely still on the disk, where it will
                         * show greyed out as "already filed" rather than lost.
                         */
                        try {
                            await lookupLocalFolderWriter()?.remove(it.name);
                        } catch (rmErr) {
                            stuckInFolder.push(
                                `${it.name}: uploaded, but ${rmErr instanceof Error ? rmErr.message : 'could not be removed from the folder'}`,
                            );
                        }
                    } else {
                        alreadyThere++;
                    }
                } catch (err) {
                    refused.push(`${it.name}: ${err instanceof Error ? err.message : 'failed'}`);
                }
            }
            if (moved.length) {
                setToast({
                    kind: stuckInFolder.length ? 'error' : 'success',
                    msg: `Moved to Scanned Paperwork: ${moved.join(', ')}`
                        + (stuckInFolder.length ? ` — ${stuckInFolder.join('; ')}` : ''),
                });
            } else if (alreadyThere > 0 && !refused.length) {
                setToast({ kind: 'error', msg: 'Those are already waiting to be filed.' });
            }
            if (refused.length) setToast({ kind: 'error', msg: refused.join(' · ') });
            setPaperworkKey(k => k + 1);
            await reload();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getToken, facilityId]);

    /**
     * ---- ⭐⭐ BACK OUT OF DOCK AND INTO THE SCAN FOLDER (2026-09-10) ----------
     *
     * > Pete: *"Should let me move files between all three at anytime correct?"*
     * > … *"Still not letting me drag from sharepoint to local."*
     *
     * ---- ⛔⛔ THE OLD REFUSAL WAS BUILT ON A CLAIM THAT WAS NOT TRUE ---------
     *
     * ⛔ ~~"a web page cannot write into a folder on somebody's disk"~~. What was
     * actually checked was that OUR handle was opened `mode: 'read'`; what was
     * WRITTEN was that the browser forbids it. §4.14, exactly: a statement wider
     * than the scan behind it — and because the wide version sounded like a
     * platform limit, nobody went looking. ⚠️ Pete did, twice, by trying it.
     *
     * ⭐ THE FILE SYSTEM ACCESS API WRITES. `showDirectoryPicker({ mode:
     * 'readwrite' })`, `getFileHandle(name, { create: true })`,
     * `createWritable()`. The scheduler grants it once, per folder, in Chrome's
     * own prompt — see `lib/scanFolder.ts`.
     *
     * ---- ⚠️ IT COPIES. IT DOES NOT MOVE. --------------------------------------
     *
     * ⛔ THE DOCUMENT STAYS IN DOCK. Writing a copy into the folder and then
     * voiding the record would destroy the audit trail — who filed it, when, on
     * which load — to tidy up a tray. ⭐ So this is "give me a copy on the disk",
     * and the message says so rather than implying the pile shrank.
     *
     * ---- ⛔ AND IT NEVER OVERWRITES A SCAN ------------------------------------
     *
     * `writeNewFile` sidesteps a taken name (` (2)`) instead of truncating it.
     * An unfiled scan in that folder is the only copy of a piece of paper.
     */
    /**
     * ---- ⭐⭐ OUR DIALOG GOES FIRST (2026-09-10) -----------------------------
     *
     * > Pete, on Chrome's "Save changes to DockSchedulerScans?" prompt: *"Can we
     * > have a popup from our app and keep it consistent with our standards?"*
     *
     * ⛔ CHROME'S PROMPT CANNOT BE REMOVED, RESTYLED OR SUPPRESSED. It is browser
     * chrome, not page content — `requestPermission` draws it and no page has a
     * handle on it. ⚠️ That is a genuine platform boundary, unlike the "a page
     * cannot write to a disk" claim this same feature was built on and which was
     * simply wrong.
     *
     * ⭐ WHAT WE CONTROL IS WHAT IS READ FIRST. This dialog says, in our words and
     * our styling, WHICH files are going WHERE and what it will cost — and its
     * Continue button is the click that triggers Chrome's prompt.
     *
     * ⛔⛔ AND THAT ORDERING IS ALSO WHAT MAKES THE PERMISSION REQUEST RELIABLE.
     * `drop` is NOT an activation-triggering event in the HTML spec (click,
     * mousedown, mouseup, pointerup, keydown and touchend are), so asking from a
     * drop handler is asking outside a user gesture and Chrome may refuse
     * outright. A click on this dialog IS a gesture. The dialog is not decoration.
     *
     * ⚠️ THE PAYLOAD IS SNAPSHOTTED SYNCHRONOUSLY. `dataTransfer` is emptied the
     * moment the drop handler returns, so the items are read now and held here —
     * reading them when the dialog resolves would find nothing (#288's lesson,
     * in a new place).
     */
    const [localDrop, setLocalDrop] = useState<
        { items: PaperworkDragItem[]; folderName: string } | null
    >(null);

    const handleDropLocal = useCallback((e: React.DragEvent) => {
        const items = readPaperworkDrag(e.dataTransfer);
        if (!items) { setToast({ kind: 'error', msg: 'That drag carried nothing to move.' }); return; }
        /* ⛔ PUBLISHED BY `ScanFolderPanel` WHEN IT HAS A USABLE FOLDER. Null is
           a real answer with a real fix, so it gets a sentence naming it. */
        const writer = lookupLocalFolderWriter();
        if (!writer) {
            setToast({
                kind: 'error',
                msg: 'No scan folder is connected on this machine. Click the folder icon in '
                    + 'Scanned Paperwork and choose it, then try again.',
            });
            return;
        }
        setLocalDrop({ items, folderName: writer.folderName });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * ---- ⛔⛔ AND IT IS A MOVE — THE DOCUMENT LEAVES DOCK ---------------------
     *
     * > Pete: *"sometimes it moves, sometimes it copies. Can we always make it
     * > move please?"*
     *
     * ⭐ THE PRODUCT ALREADY AGREED WITH HIM. `ingestScan` has always moved a
     * SharePoint scan into `Filed/YYYY-MM` once Dock had it; only this leg
     * copied. One gesture with two verbs is what he was reading.
     *
     * ⛔⛔ WRITE FIRST, REMOVE SECOND, ALWAYS. The bytes are on the disk and
     * verified before anything is voided or deleted. Reversing that order loses
     * a document the first time the network blinks, and there is no undo on
     * either side worth relying on.
     *
     * ⚠️ THE TWO REMOVALS ARE NOT THE SAME ACT and the messages must not pretend
     * they are: a Dock document is VOIDED (row, audit trail and reason survive;
     * §110's history still shows who filed it), while a SharePoint scan goes to
     * that site's RECYCLE BIN for 93 days. Neither is `removeEntry`'s
     * unrecoverable unlink — that one is used in the OTHER direction.
     */
    const runLocalDrop = useCallback((items: PaperworkDragItem[]) => {
        const writer = lookupLocalFolderWriter();
        if (!writer) { setLocalDrop(null); return; }
        setLocalDrop(null);
        void (async () => {
            const token = await getToken();
            const saved: string[] = [];
            const refused: string[] = [];
            /** ⚠️ Written to the disk but NOT removed from Dock — its own sentence. */
            const stuckInDock: string[] = [];
            let alreadyThere = 0;
            for (const it of items) {
                try {
                    if (it.kind === 'localScan') {
                        /* ⭐ It came OUT of that folder. Writing it back would make a
                           second copy of a file already sitting there. */
                        alreadyThere++;
                        continue;
                    }
                    /**
                     * ⛔ THE BYTES COME THROUGH THE PREVIEW ROUTE, which is the one
                     * endpoint that already streams a document's content with the
                     * tenancy check on it (§0.0). ⚠️ It hands back an OBJECT URL, so
                     * this reads it once and revokes — an un-revoked one pins the
                     * whole file in memory for the life of the page.
                     */
                    const target: PreviewTarget = it.kind === 'document'
                        ? { kind: 'document', documentId: it.documentId }
                        : { kind: 'scan', facilityCode: facilityId ?? '', itemId: it.itemId };
                    if (it.kind === 'scan' && facilityId === null) {
                        throw new Error('no facility is selected.');
                    }
                    const { url } = await fetchPreviewBlob(token, target);
                    let blob: Blob;
                    try {
                        blob = await (await fetch(url)).blob();
                    } finally {
                        URL.revokeObjectURL(url);
                    }
                    const written = await writer.write(it.name, blob);
                    saved.push(written);

                    /**
                     * ⛔⛔ ONLY NOW. The bytes are on the disk, `writeNewFile` has
                     * returned the name it actually used, and nothing above threw.
                     * This is the line that makes it a move rather than a copy, and
                     * it is deliberately the LAST thing that happens.
                     */
                    try {
                        if (it.kind === 'document') {
                            /* ⚠️ VOID, not delete. The row and the audit trail
                               survive with a reason attached, which is what a
                               dispute is answered from. The server still refuses a
                               signed copy — that refusal lands in `stuckInDock`. */
                            await voidDocumentApi(token, it.documentId,
                                `Moved to the scan folder ${writer.folderName} on this machine.`);
                        } else {
                            if (facilityId === null) throw new Error('no facility is selected.');
                            /* ⚠️ SharePoint's own Recycle Bin — 93 days. */
                            await deleteScan(token, facilityId, it.itemId);
                        }
                    } catch (rmErr) {
                        /* ⛔ NOT A FAILURE OF THE MOVE'S FIRST HALF. The file IS in
                           the folder; saying "failed" would have somebody drag it
                           again and end up with two. */
                        stuckInDock.push(
                            `${written}: saved, but ${rmErr instanceof Error ? rmErr.message : 'could not be removed from Dock'}`,
                        );
                    }
                } catch (err) {
                    refused.push(`${it.name}: ${err instanceof Error ? err.message : 'failed'}`);
                }
            }
            if (saved.length) {
                setToast({
                    kind: stuckInDock.length ? 'error' : 'success',
                    msg: `Moved into ${writer.folderName}: ${saved.join(', ')}`
                        + (stuckInDock.length ? ` — ${stuckInDock.join('; ')}` : ''),
                });
            } else if (alreadyThere > 0 && !refused.length) {
                setToast({ kind: 'error', msg: 'Those are already in the scan folder.' });
            }
            if (refused.length) setToast({ kind: 'error', msg: refused.join(' · ') });
            setPaperworkKey(k => k + 1);
            await reload();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getToken, facilityId]);


    const showAttachedFor = useCallback((t: RailFocus | string | null) => {
        if (!t) { setAttachedPaperwork(null); return; }
        /**
         * ⭐ TWO SHAPES, ON PURPOSE. The grid knows only an id; the driver rail
         * knows the man's NAME, which is what Pete wants on the panel when the
         * cursor is on his card — *"Paperwork - Appt Number. or Driver Name,
         * depending on what is hovered over."*
         */
        /**
         * ---- ⭐ THE DRIVER RAIL SENDS A WHOLE FOCUS -------------------------
         *
         * ⛔⛔ AND IT SENDS ONE EVEN WHEN THE MAN HAS NO LOAD, which is the fix
         * for the hover that used to report `null` there. Paperwork genuinely
         * needs an appointment, so it is still skipped — but Communications and
         * History do not, and they now get their focus. ⚠️ Reusing paperwork's
         * limitation as the rule for five panels was the bug waiting to happen.
         */
        if (typeof t !== 'string') {
            setRailFocus(t);
            /**
             * ---- ⭐⭐⭐ THE TWO ARGUMENT SHAPES ARE THE TWO GESTURES (decision 194) --
             *
             * ⛔⛔ THE ASYMMETRY BELOW IS THE DESIGN, NOT AN OVERSIGHT. This branch is
             * reached from a CARD in the rail (`onChooseRecord` → `focusFromCheckIn`);
             * the string branch further down is reached from a CHIP on the grid. Pete
             * chose different board behaviour for them, asked before it was built:
             *
             *   ⭐ *"Board stays whole; chip gets the ring"* — clicking one load scopes
             *     the panels and leaves the calendar showing everything, or the standing
             *     party if one is chosen. Only the ring says which.
             *   ⭐ A driver card, by contrast, IS the board scope: *"it doesn't change
             *     the schedule to show on only the appointment ... for that driver"*.
             *
             * ⚠️ SO THE CARD WRITES `partyScope` AND THE CHIP DOES NOT, and the shape of
             * the argument is what already distinguishes them. ⛔ Do not "tidy" this by
             * giving the grid a focus object — the two paths would become one and the
             * board would collapse to a single chip on every click.
             *   ** WHEN TWO CALLERS NEED DIFFERENT BEHAVIOUR, THE DIFFERENCE BELONGS IN
             *      SOMETHING THE COMPILER CAN SEE, NOT IN A FLAG THE CALLER REMEMBERS. **
             *
             * ⚠️ GUARDED ON `kind` ANYWAY. `focusFromCheckIn` returns a driver focus
             * today, but a future caller handing an appointment focus through this seam
             * would silently start filtering the board, and nothing would say why.
             */
            if (t.kind !== 'appointment') setPartyScope(t);
            if (!t.appointmentId || !t.appointmentNumber) return;
            setAttachedPaperwork({
                appointmentId: t.appointmentId,
                appointmentNumber: t.appointmentNumber,
                label: t.label,
            });
            setTrayView('Attached');
            return;
        }
        /**
         * ---- ⭐⭐⭐ SECOND CLICK ON THE SAME CHIP GOES BACK TO THE STANDING PARTY --
         *
         * > Pete, 2026-09-20: *"A second click on a grid chip should return to the
         * > standing party."*
         *
         * ⭐ THIS IS THE THIRD RESET, AND IT IS NAMED. §1AV.180 warned that Ⓡ and the
         * second click on a CARD were the two resets and *"a third one nobody named is
         * how they drift apart"*. This is that third one, so here is how it differs:
         * Ⓡ clears EVERYTHING, a second card click clears the party, and this clears
         * ONLY the load — deliberately leaving `partyScope` standing, because the
         * party was chosen by a different gesture and nothing here revokes it.
         *   ** A RESET IS DEFINED BY WHAT IT LEAVES, NOT BY WHAT IT CLEARS. **
         *
         * ⛔ IT COMPARES `appointmentId`, NOT `sameFocus`. A party focus covering this
         * load carries `appointmentId: null`, so `sameFocus` would be false and the
         * click would fall through and scope to the load — which is right. Clicking a
         * chip INSIDE a client scope must narrow to that load, not toggle the client
         * off. Only a focus that IS this one load un-focuses.
         *
         * ⚠️ THE TRAY GOES WITH IT. Leaving the paperwork target pointed at a load the
         * panels no longer name is the state where the heading and the tray disagree.
         */
        const cur = railFocusRef.current;
        if (cur && cur.kind === 'appointment' && cur.appointmentId === t) {
            setRailFocus(null);
            setAttachedPaperwork(null);
            return;
        }
        const a = apptsRef.current.find(x => String(x.appointmentId) === t);
        /**
         * ⛔ A BLOCK IS NOT A LOAD — no paperwork, ever.
         *
         * ⚠️ IT CLEARS THE PAPERWORK TARGET BUT LEAVES THE FOCUS ALONE, and the
         * two differ on purpose. Paperwork has always cleared here. The focus is
         * a LATCH the scheduler set deliberately, and dropping her scope because
         * the cursor crossed a blocked window on the way somewhere else would
         * undo work she did not ask to undo — Pete's ALL button is the way out.
         */
        if (!a || a.direction === 'BLOCK') { setAttachedPaperwork(null); return; }
        /**
         * ---- ⭐⭐ THE LABEL SAYS WHICH TRUCK, NOT JUST WHICH LOAD -----------
         *
         * > Pete, 2026-09-10: *"Under attached paperwork - A00000310 - 'Should
         * > have Driver Name or Phone here if the select driver' or 'DockDoor XXX
         * > and time' if appointment."*
         *
         * ⛔ THE LOAD NUMBER ALONE IS NOT AN IDENTIFICATION. A scheduler filing
         * paperwork is deciding whether THIS pile belongs to THAT truck, and
         * "A00000311" is the one fact on screen that does not help them — every
         * load has one and they all look alike. The door and the time are what a
         * person on a dock actually recognises a load by.
         *
         * ⭐ THE NUMBER STAYS IN FRONT. It is what the documents are named after
         * and what a dispute is looked up by; the detail is added, not swapped.
         */
        /* ⛔ `dockDoorCode`, not `doorCode`. AppointmentDto calls it the first;
           CheckInDto calls it the second, and reading the wrong one off the wrong
           DTO is a compile error here rather than a blank chip on a dock. */
        const when = a.scheduledStartUtc
            ? new Date(a.scheduledStartUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
            : null;
        const detail = [a.dockDoorCode, when].filter(Boolean).join(' · ');
        const label = detail ? `${a.appointmentNumber} · ${detail}` : a.appointmentNumber;
        /* ⭐ ONE LABEL, BOTH USES. `focusFromAppointment` takes it rather than
           recomposing it, so the sentence a scheduler reads on the paperwork tray
           and on the four scoped panels cannot drift apart. */
        setRailFocus(focusFromAppointment(a, label, driverNameForAppt(a.appointmentId)));
        setAttachedPaperwork({
            appointmentId: String(a.appointmentId),
            appointmentNumber: a.appointmentNumber,
            label,
        });
        /* ⭐ A DWELL IS A REQUEST TO SEE THAT LOAD'S PAPERWORK, so it also
           SWITCHES the tray. Setting the target without the view was the state
           where the panel held the right load and showed the wrong list. */
        setTrayView('Attached');
    }, []);

    /**
     * ---- ⭐⭐ A CLICK ON A GRID CHIP: SCOPE, OR OPEN (F121, Decision 303) --------
     *
     * > Pete, 2026-09-29: *"when we single click an appointment, we get here with the
     * > scope in play. when I single click here, it should go into that popup that
     * > shows me the summary and the edit buttons"* · *"I can no longer double click
     * > an appointment to bring up that summary popup"* · *"have the action wait a
     * > very short time to see if the single click was actually a double click?"*
     *
     * ⛔ WHY THE DOUBLE CLICK BROKE. Since the chip click NARROWS the board, the first
     * click of a double-click re-laid the board and the second was a "second click on
     * the same chip", which un-scoped it again — the board jumped twice under the
     * cursor and the double-click never reached the chip. Decision 190 had rejected a
     * delay (*"a delay on the common path to tidy up the rare one is the wrong
     * trade"*) before the click narrowed anything; Pete now asks for it himself.
     *
     * ⭐ THREE CASES, ONE HANDLER:
     *   · a click on the load that IS the scope → opens its summary (the popup with the
     *     edit buttons), at once;
     *   · a second click on the same chip inside `SCOPE_CLICK_DELAY_MS` → a double
     *     click: the pending scope is dropped and the summary opens, board untouched;
     *   · otherwise → the scope is applied once the delay passes with no second click.
     * ⚠️ So the grid's "second click returns to the standing party" is retired here —
     * that click now opens the load; Ⓡ is the way back. `showAttachedFor` keeps the
     * toggle for its other callers.
     */
    const pendingScopeRef = useRef<{ id: string; timer: number } | null>(null);
    const cancelPendingScope = useCallback(() => {
        const p = pendingScopeRef.current;
        if (p) window.clearTimeout(p.timer);
        pendingScopeRef.current = null;
    }, []);
    useEffect(() => cancelPendingScope, [cancelPendingScope]);

    const handleGridChipClick = useCallback((a: AppointmentDto) => {
        const id = String(a.appointmentId);
        const cur = railFocusRef.current;
        if (cur && cur.kind === 'appointment' && cur.appointmentId === id) {
            cancelPendingScope();
            setSelectedAppt(a);
            return;
        }
        if (pendingScopeRef.current?.id === id) {
            cancelPendingScope();
            setSelectedAppt(a);
            return;
        }
        cancelPendingScope();
        const timer = window.setTimeout(() => {
            pendingScopeRef.current = null;
            showAttachedFor(id);
        }, SCOPE_CLICK_DELAY_MS);
        pendingScopeRef.current = { id, timer };
    }, [cancelPendingScope, showAttachedFor]);

    /**
     * ⭐ F130 (Decision 308): a double-click on a client / carrier / consignee card opens
     * that party's record in Settings — Carriers opens the carrier's editor, Clients and
     * Consignees open filtered to it (a consignee's own form when it is in the book).
     * ⛔ Those three tabs are ADMIN tabs; a scheduler is told so rather than bounced to
     * Facility Hours without a word.
     */
    const openPartyRecord = useCallback((kind: 'client' | 'carrier' | 'consignee', code: string) => {
        if (!isAdmin) {
            setToast({ kind: 'error', msg: `The ${kind} records are in Settings, which only an admin can open.` });
            return;
        }
        setSettingsTab(kind === 'carrier' ? 'carriers' : kind === 'client' ? 'clients' : 'consignees');
        setSettingsFocus(code);
        setSettingsOpen(true);
    }, [isAdmin]);

    /** The native double-click opens too — and must not let a pending scope land behind the popup. */
    const handleGridChipOpen = useCallback((a: AppointmentDto) => {
        cancelPendingScope();
        setSelectedAppt(a);
    }, [cancelPendingScope]);

    /**
     * ---- ⛔⛔ WHICH SCAN SOURCE THE TRAY IS SHOWING (Pete, 2026-09-10) ------
     *
     * ⛔ §1R.16 ("only one source is ever consulted") IS DELIBERATELY REVERSED —
     * see the long note in `ScanFolderPanel`. `ScanSourceType` now decides which
     * view OPENS FIRST; this is the scheduler's live choice.
     *
     * ⚠️ HELD HERE BECAUSE NEITHER TRAY OWNS IT. Each renders null when the other
     * is selected, so the state has to sit above both or they would argue.
     * ⭐ `null` until the config answers, so nothing flashes before we know which
     * view a site starts on.
     */
    const [sourceView, setSourceView] = useState<'SharePoint' | 'LocalFolder' | null>(null);

    /**
     * ---- ⭐⭐ WHICH OF THE THREE THE TRAY IS SHOWING (Pete, 2026-09-10) ------
     *
     * > *"It should be the 3 icons on every view period."*
     *
     * ⛔ THIS IS **NOT** DERIVED FROM `attachedPaperwork != null`, and that
     * distinction is the whole feature. Derived, switching back to SharePoint
     * would have to THROW AWAY the load you were looking at — so the Attached
     * icon would go dead again the moment you left it, and the only way back
     * would be to hover the chip a second time.
     *
     * ⭐ SO THE TARGET AND THE VIEW ARE SEPARATE: the last thing hovered is
     * remembered, and the third icon stays live so you can return to it.
     */
    const [trayView, setTrayView] = useState<'SharePoint' | 'LocalFolder' | 'Attached'>('SharePoint');

    /**
     * ⛔ SWITCHING TO A FOLDER CHANGES THE VIEW **AND** THE SOURCE; switching to
     * Attached changes only the view. ⚠️ Both go through here rather than through
     * two setters at the call site, because the two panels each render for one
     * source — set one without the other and neither would be mounted.
     */
    /**
     * ⭐⭐ CLICKING "THIS MACHINE" WHILE ALREADY ON IT RE-OPENS THE PICKER.
     *
     * > Pete, 2026-09-10: *"this Change is the same as the click of the Local
     * > button correct?, if so, remove it, not necessary."*
     *
     * ⛔ IT WAS NOT THE SAME, AND THAT IS WHY THE LINK EXISTED. The icon only
     * opened a picker when NO folder was remembered; with one remembered it just
     * switched view, so "Change" was the only way to pick a different folder.
     * ⭐ Making Pete's sentence TRUE is the fix — one control, both jobs — rather
     * than deleting the link and leaving no way to change folders.
     *
     * ⚠️ A NONCE, BECAUSE THE STATE DOES NOT CHANGE. Setting `trayView` to the
     * value it already holds re-renders nothing and fires no effect; a counter is
     * the smallest thing that says "this happened again".
     */
    const [localPick, setLocalPick] = useState({ n: 0, force: false });

    const onTrayView = useCallback((v: 'SharePoint' | 'LocalFolder' | 'Attached') => {
        setTrayView(prev => {
            if (v === 'LocalFolder') {
                /**
                 * ⛔⛔ THE NONCE IS RAISED ONLY BY A CLICK, AND THAT IS THE POINT.
                 *
                 * > Pete, 2026-09-10: *"It only locked up When it was on the Denver
                 * > facility for some reason."*
                 *
                 * ⚠️ THAT "SOME REASON" WAS A DESIGN FAULT OF MINE. The picker used
                 * to open on any CHANGE OF SOURCE, and changing facility reloads the
                 * document config, which sets the source — so switching to Denver
                 * threw a folder picker at him unasked, and the next control he
                 * touched put a second one in flight and killed both (see `choose`).
                 *
                 * ⭐ A PICKER IS NOW ONLY EVER OPENED BY A DELIBERATE CLICK. Config
                 * loads, facility switches and restored state cannot reach it.
                 *
                 * `force` = clicked while ALREADY on this view, which is the gesture
                 * the deleted "Change" link used to be.
                 */
                setLocalPick(prevPick => ({ n: prevPick.n + 1, force: prev === 'LocalFolder' }));
            }
            return v;
        });
        if (v !== 'Attached') setSourceView(v);
    }, []);
    const [sharePointConfigured, setSharePointConfigured] = useState(true);
    useEffect(() => {
        if (!facilityId) return;
        let cancelled = false;
        void (async () => {
            try {
                const { config } = await getDocumentConfig(await getToken(), facilityId);
                if (cancelled) return;
                setSharePointConfigured(config.scanSourceType === 'SharePoint');
                const first = config.scanSourceType === 'SharePoint' ? 'SharePoint' : 'LocalFolder';
                setSourceView(first);
                /* ⛔ THE VIEW FOLLOWS THE SOURCE ON FIRST LOAD, or the tray would
                   open on 'SharePoint' at a local-folder site and show nothing. */
                setTrayView(first);
            } catch {
                /* ⛔ FAIL TO THE LOCAL VIEW. It needs no server config to be useful,
                   so an unreadable config leaves a working tray rather than an
                   empty one waiting on an answer that is not coming. */
                if (!cancelled) {
                    setSharePointConfigured(false);
                    setSourceView('LocalFolder');
                    setTrayView('LocalFolder');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [facilityId, getToken]);

    const [driverOverwrite, setDriverOverwrite] = useState<
        { ev: DropCheckInOnAppointmentEvent; answers: AttachAnswers; bookedDrivers: string[] } | null
    >(null);
    /**
     * ⭐ #73 — the container/trailer question, awaiting a person.
     *
     * ⚠️ IT HOLDS THE CONFLICTS, NOT A COUNT. The dialog has to say WHICH number
     * and WHICH two values, because "the numbers disagree" is not something a
     * scheduler can answer — the whole decision is which of the two is right.
     */
    const [vehicleOverwrite, setVehicleOverwrite] = useState<
        {
            ev: DropCheckInOnAppointmentEvent;
            answers: AttachAnswers;
            conflicts: { field: string; appointmentValue: string; driverValue: string; detail: string }[];
        } | null
    >(null);
    /**
     * ⭐ THE PAPERWORK PROMPT'S OWN STATE. ⛔ NOT A `kind` FIELD ON THE ONE ABOVE:
     * the two dialogs ask different questions, send different answers and can both
     * be pending on one drag, and a single slot could only hold one of them.
     */
    const [paperworkOverwrite, setPaperworkOverwrite] = useState<
        {
            ev: DropCheckInOnAppointmentEvent;
            answers: AttachAnswers;
            conflicts: { field: string; appointmentValue: string; driverValue: string; detail: string }[];
        } | null
    >(null);
    /**
     * ⭐⭐ THE POST-ATTACH NOTICE — ONE DIALOG, WHATEVER IS WORTH SAYING.
     *
     * ⛔⛤ THIS WAS `palletWarn` FOR ABOUT AN HOUR, and the shape hints arriving
     * made a second one tempting. Two dialogs firing off one successful drag is
     * two things to dismiss and no clarity about which mattered.
     *   ** TWO NOTICES FROM ONE EVENT IS ONE NOTICE AND AN ACCIDENT. **
     * ⚠️ It holds the APPOINTMENT, not just the numbers, because the useful next
     * action is opening that booking.
     */
    const [attachNotice, setAttachNotice] = useState<
        {
            appt: { appointmentId: number | string; appointmentNumber: string };
            variance: { appointmentCount: number; driverCount: number } | null;
            hints: { claimed: string; looksLike: string; detail: string }[];
        } | null
    >(null);

    const openPaperworkList = (scope: PaperworkScope) => setDocsFor(scope);

    const handleSignPaperwork = async (scope: PaperworkScope) => {
        try {
            const choice = choosePaperworkAction(await paperworkFor(scope));
            if (choice.needsTheList || !choice.signTarget) { openPaperworkList(scope); return; }
            setSignDirect({
                doc: choice.signTarget,
                appointmentNumber: scope.appointmentNumber,
                carrierCode: scope.carrierCode,
            });
        } catch {
            // ⚠️ A FAILED LOOKUP FALLS BACK TO THE LIST rather than to an error.
            // The scheduler's goal is reachable either way, and a toast telling
            // them to try again would be a dead end with a driver waiting.
            openPaperworkList(scope);
        }
    };

    const handlePrintPaperwork = async (scope: PaperworkScope) => {
        try {
            const choice = choosePaperworkAction(await paperworkFor(scope));
            if (choice.needsTheList || !choice.printTarget) { openPaperworkList(scope); return; }
            /**
             * ---- ⛔ THIS DOWNLOADED, IT DID NOT PRINT (Pete, 2026-09-10) --------
             *
             * > *"Print from the driver card downloads the document instead of
             * > bringing to paperwork popup."*
             *
             * ⛔ IT USED `getDocumentUrl` + `window.open`, and every SAS
             * `blobClient` mints is stamped `Content-Disposition: attachment` on
             * purpose — so opening one is a DOWNLOAD by definition, not a view
             * and certainly not a print.
             *
             * ⚠️ THE OLD COMMENT HERE SAID "we open the PDF; we do not choose the
             * reader". That was true and beside the point: it never opened
             * anything, it saved a file.
             *
             * ⭐ `printDocumentToDialog` fetches the bytes through the INLINE
             * preview route and opens the OS print dialog — the same path the
             * paperwork panel and the Edit screen now use. See
             * `lib/printDocument.ts` for why a browser cannot launch Acrobat.
             */
            const outcome = await printDocumentToDialog(
                getToken,
                { kind: 'document', documentId: choice.printTarget.documentId },
                choice.printTarget.fileName ?? 'document',
            );
            /* ⛔ IF IT COULD NOT BE PRINTED, FALL BACK TO THE LIST rather than
               leaving the click with nothing to show for it — the same fallback
               the catch below uses, for the same reason. */
            if (!outcome.ok) openPaperworkList(scope);
        } catch {
            openPaperworkList(scope);
        }
    };

    /**
     * ⭐⭐ FILES A WHOLE DRAG, WHICH MAY BE ONE DOCUMENT OR TEN (2026-08-27).
     *
     * Pete: *"add checkboxes to each of the Scanned Paperwork items to allow us to
     * drag all the checked attachments to a driver card or appointment."*
     *
     * ⛔⛔ THE HARD PART IS NOT THE LOOP — IT IS PARTIAL FAILURE. Five bills of
     * lading dragged, three filed, two rejected: a single "filed" toast would be a
     * lie and a single "failed" toast would send somebody to re-file three
     * documents that are already there. **Every item's outcome is tracked and the
     * message names the ones that did not land.** Same lesson as #288 — a drop that
     * half-works and says nothing is worse than one that fails loudly.
     *
     * ⚠️ SEQUENTIAL, NOT `Promise.all`. Each scan may need an ingest from SharePoint
     * first; ten at once is ten concurrent uploads through one connection pool, and
     * the failure that produces is a timeout nobody can attribute.
     */
    const handleDropPaperwork = async (
        payload: PaperworkDragPayload,
        target: { appointmentId: string } | { checkInId: string },
        where: string,
    ) => {
        const items = payload.items;
        if (items.length === 0) return;
        setPaperworkBusy(true);
        try {
            const token = await getToken();
            const filedNames: string[] = [];
            const failed: { name: string; why: string }[] = [];
            const stuckInFolder: string[] = [];
            let alsoLinked: 'checkIn' | 'appointment' | null = null;
            /** ⭐ Kept only when exactly one document is filed — see the note below. */
            let soleDocumentId: string | null = null;
            let soleFileName: string | null = null;

            for (const item of items) {
                try {
                    let documentId: string;
                    if (item.kind === 'scan') {
                        if (facilityId === null) throw new Error('No facility is selected.');
                        const ing = await ingestScan(token, facilityId, item.itemId);
                        documentId = ing.document.documentId;
                        /**
                         * ⚠️ A FAILED MOVE IS CARRIED, NOT SWALLOWED, AND NOT AN ERROR.
                         * The document IS filed — saying otherwise would have the
                         * scheduler file it again. But the original is still in the
                         * SharePoint folder, and somebody has to know or it will be
                         * ingested a second time on the next pass and greyed out for a
                         * reason nobody can explain.
                         */
                        if (!ing.moved) stuckInFolder.push(ing.document.fileName);
                    } else if (item.kind === 'localScan') {
                        /**
                         * ---- ⭐⭐ A FILE FROM THE SCANNER'S FOLDER ON THIS PC -----
                         *
                         * > Pete, 2026-09-10: *"cannot drag that .jpg file to the
                         * > appointment or driver."*
                         *
                         * ⛔ IT IS NOT IN DOCK YET, so it is UPLOADED and filed in one
                         * gesture — the same two steps the "File…" button took one at a
                         * time, which is why that button could go.
                         *
                         * ⭐ `uploadDocument` WITH THE TARGET ALREADY SET means the
                         * conversion runs on the way in (see `storeDocument`), so a
                         * dragged JPEG lands as a JPEG **and** a PDF exactly as a
                         * SharePoint one does.
                         *
                         * ⚠️ THE HANDLE CAN BE GONE. A scanner tidying its folder
                         * between the dragstart and the drop is normal, not an error to
                         * hide — the sentence names the file.
                         */
                        if (facilityId === null) throw new Error('No facility is selected.');
                        const local = lookupLocalScan(item.ref);
                        if (!local) {
                            throw new Error('that file is no longer in the scan folder.');
                        }
                        const file = await local.getFile();
                        const up = await uploadDocument(token, facilityId, file, {
                            ...( 'appointmentId' in target
                                ? { appointmentId: target.appointmentId }
                                : { checkInId: target.checkInId } ),
                            source: 'Folder',
                        });
                        documentId = up.document.documentId;
                        /**
                         * ---- ⛔⛔ THE ORIGINAL LEAVES THE FOLDER (2026-09-10) ----
                         *
                         * > Pete: *"sometimes it moves, sometimes it copies. Can we
                         * > always make it move please?"*
                         *
                         * ⭐ THIS IS THE LINE THAT MAKES THE TWO SOURCES AGREE. Three
                         * lines up, a SharePoint scan is ingested and `ing.moved`
                         * reports that Dock MOVED it into `Filed/YYYY-MM`. The local
                         * folder was the only source that left its original behind —
                         * so a scanning workstation's tray only ever grew, every row
                         * greyed out, and the same gesture meant two different things
                         * depending on which icon was lit.
                         *
                         * ⛔ AFTER THE UPLOAD, NEVER BEFORE. `removeEntry` has no
                         * Recycle Bin behind it (unlike SharePoint's 93 days), so the
                         * document id coming back is the only acceptable proof that
                         * removing the file is safe.
                         *
                         * ⚠️ AND IT REPORTS THROUGH `stuckInFolder`, the channel that
                         * already exists for exactly this — "filed, but the original
                         * is still there" is not a filing failure and must not read
                         * as one, or the scheduler files it twice.
                         */
                        try {
                            await lookupLocalFolderWriter()?.remove(item.name);
                        } catch {
                            stuckInFolder.push(up.document.fileName);
                        }
                    } else {
                        documentId = item.documentId;
                    }

                    const filed = await fileDocument(token, documentId, target, null);
                    filedNames.push(filed.document.fileName);
                    if (filed.alsoLinked) alsoLinked = filed.alsoLinked;
                    soleDocumentId = documentId;
                    soleFileName = filed.document.fileName;
                } catch (err) {
                    /**
                     * ⛔ ONE FAILURE DOES NOT ABANDON THE REST. The scheduler picked
                     * these deliberately; stopping at the first bad one would leave the
                     * remainder unfiled for a reason that has nothing to do with them.
                     */
                    failed.push({ name: item.name, why: formatError(err) });
                }
            }

            // ⭐ SAY WHEN THE OTHER SIDE WAS LINKED TOO. A link the scheduler did not
            // ask for and is not told about is a surprise found six weeks later.
            const also = alsoLinked === 'checkIn'
                ? ' It is on the driver at the gate as well.'
                : alsoLinked === 'appointment'
                    ? " It is on that driver's load as well."
                    : '';

            const moveWarning = stuckInFolder.length
                ? ` ${stuckInFolder.length === 1 ? 'The original is' : `${stuckInFolder.length} originals are`}`
                  + ' still in the scan folder — Dock could not move them.'
                : '';

            if (failed.length === 0) {
                /**
                 * ⚠️ ONE DOCUMENT IS STILL NAMED. "1 document filed" is a worse
                 * sentence than the filename, and this path is the common one.
                 */
                const what = filedNames.length === 1
                    ? filedNames[0]
                    : `${filedNames.length} documents`;
                setToast({
                    kind: moveWarning ? 'error' : 'success',
                    msg: `${what} filed on ${where}.${also}${moveWarning}`,
                });
            } else {
                /**
                 * ⛔ THE FAILURES ARE NAMED, NOT COUNTED. "2 failed" out of five means
                 * checking all five by hand; naming them means checking two.
                 */
                const lead = filedNames.length
                    ? `${filedNames.length} of ${items.length} filed on ${where}. `
                    : `Nothing was filed on ${where}. `;
                setToast({
                    kind: 'error',
                    msg: lead + failed.map(f => `${f.name}: ${f.why}`).join('; ') + moveWarning,
                });
            }

            setPaperworkKey(k => k + 1);
            setRefreshKey(k => k + 1);
            void reload();

            /**
             * ---- ⭐ AND ONLY NOW, ASK WHAT LOAD IT IS (#191/#221) ---------------
             *
             * ⛔ ONLY WHEN THERE IS NOTHING TO ASSOCIATE TO. Pete: *"If not already
             * associated"*. Reading a page for a load somebody already identified
             * produces a second opinion nobody asked for, and a disagreement
             * between OCR and a human decision is a support call.
             *
             * ⛔⛔ AND ONLY WHEN EXACTLY ONE DOCUMENT WAS FILED (2026-08-27). The
             * suggestion opens a dialog asking "is this the load?" — five of them
             * queued behind each other is not a feature, it is a modal stampede.
             * ⚠️ With a multi-select the scheduler has already decided what these
             * belong to; that is what the drag WAS.
             *
             * ⚠️ AFTER THE FILING, NEVER BEFORE IT. The documents are stored and the
             * toast has already said so, so a slow or broken read costs the
             * scheduler nothing — which is the whole reason OCR can be an
             * accelerator rather than something to wait on.
             */
            if ('checkInId' in target && filedNames.length === 1 && soleDocumentId && soleFileName) {
                const docId = soleDocumentId;
                const docName = soleFileName;
                void (async () => {
                    try {
                        const r = await getSuggestLoad(await getToken(), docId, target.checkInId);
                        // ⛔ 'Unavailable' IS SILENT. Not news for somebody with a
                        // driver at the desk; the manual path is unchanged.
                        if (r.kind === 'Unavailable') return;
                        setSuggested({
                            result: r,
                            documentId: docId,
                            documentName: docName,
                            checkInId: target.checkInId,
                        });
                    } catch {
                        // Same reasoning: a failed suggestion is not a failed drop.
                    }
                })();
            }
        } catch (err) {
            // ⚠️ Say WHERE it was aimed. "Filing failed" on a screen with forty blocks
            // tells a supervisor nothing they can act on.
            setToast({ kind: 'error', msg: `Couldn't file that onto ${where}: ${formatError(err)}` });
        } finally {
            setPaperworkBusy(false);
        }
    };

    /**
     * An unmatched arrival dropped onto empty row space. This creates NOTHING on
     * its own -- it opens the ordinary create dialog prefilled with the door and
     * time it was dropped on, and the existing onCreated path attaches the
     * arrival once the appointment exists. Same route as the panel's Create
     * button, so there is one code path to the appointment, not two.
     */
    const handleDropCheckInInSlot = (ev: DropCheckInInSlotEvent) => {
        setPendingCheckInId(ev.checkInId);
        // The DROP supplies the door and the time; the CARD supplies who and what.
        // Without the second half the dialog opened with a slot and no driver —
        // the wrong half, since the scheduler chose the slot by dropping there.
        const c = checkInsById.current.get(ev.checkInId);
        setNewPrefill({
            dockDoorId: ev.dockDoorId, startLocal: ev.startLocal, endLocal: ev.endLocal,
            ...(c ? prefillFromCheckIn(c) : {}),
        });
        setNewOpen(true);
    };

    const handleCreateInSlot = (ev: CreateInSlotEvent) => {
        setNewPrefill({
            dockDoorId: ev.dockDoorId,
            startLocal: ev.startLocal,
            endLocal: ev.endLocal,
        });
        setNewOpen(true);
    };

    /**
     * A walk-up needs an appointment. This opens the ordinary create dialog
     * rather than creating anything.
     *
     * ⚠️ IT HAS TO BE THE ORDINARY DIALOG. dock.Appointment requires ClientCode
     * and DocumentType, and a driver at a gate does not know either -- that is
     * exactly why the kiosk does not create one (decided 2026-08-17). Opening
     * the dialog also keeps slot-minute, conflict and capacity validation, which
     * matters because a walk-up IS taking a real slot.
     *
     * The check-in is remembered so it can be attached once the appointment
     * exists; the arrival time is the truth, so it stays the arrival's, not the
     * appointment's.
     */
    const [pendingCheckInId, setPendingCheckInId] = useState<string | null>(null);
    /**
     * Which appointment is under the cursor, wherever the cursor is.
     *
     * ⚠️ LIVES HERE, NOT IN EITHER CHILD. The panel and the grid cannot see each
     * other, so the only place that can link "this arrival card" to "that block"
     * is their common parent. Hovering either outlines both.
     */
    const [hoverApptId, setHoverApptId] = useState<string | null>(null);
    /**
     * ---- ⛔⛔ WHERE THE APPOINTMENT HOVER CAME FROM (Pete, 2026-09-21) ---------
     *
     * > *"When you hover a labor card that has an appointment, instead of keeping
     * > the panel (all), it changes all the panels to the appointment number...
     * > That should only work for driver, client, carrier, consignee."*
     *
     * ⛔⛔ AND IT WAS A LOOP, NOT MERELY WRONG SCOPING. A rail row that re-scopes the
     * panels REPLACES THE LIST IT LIVES IN: the row under the cursor unmounts, the
     * browser fires `mouseenter` on whatever lands in its place, and it feeds itself.
     *   ** A HOVER THAT RE-SCOPES THE LIST CONTAINING THE HOVERED ROW IS A LOOP BY
     *      CONSTRUCTION. **
     *
     * ⚠️ THE GRID AND THE RAIL BOTH FED `hoverApptId`, so nothing downstream could
     * tell them apart - and they want opposite things:
     *   · a GRID chip populates the panels (Pete, 2026-09-20 - unchanged);
     *   · a RAIL row marks the board and leaves the panels at (ALL).
     *
     * ⭐ TWO PRIMITIVES, NOT ONE OBJECT. A single `{id, origin}` state would be a
     * fresh object on every hover and could never bail out of a re-render - which
     * is the same fault that makes this loop possible in the first place.
     *   ** TWO PRIMITIVE STATES BAIL OUT INDEPENDENTLY; ONE OBJECT STATE NEVER
     *      BAILS OUT AT ALL. **
     *
     * ! DEFAULTS TO 'grid', so the grid's call sites need no change. Only the
     *   exception names itself.
     */
    const [hoverApptOrigin, setHoverApptOrigin] = useState<'grid' | 'rail'>('grid');
    const handleHoverAppointment = useCallback(
        (id: string | null, origin: 'grid' | 'rail' = 'grid') => {
            setHoverApptId(id);
            setHoverApptOrigin(origin);
        }, []);
    /**
     * ---- ⭐⭐ WHAT THE CURSOR IS OVER, AS A FOCUS (Pete, 2026-09-20) -----------------
     *
     * ⛔ A SECOND PIECE OF STATE, NOT A WIDENING OF `hoverApptId`. That one is an ID and
     * exists to draw the weak ring on the grid; this one is a whole `RailFocus` and exists
     * to feed the panels. ⚠️ Collapsing them would make a party hover — which is not an
     * appointment at all — impossible to express.
     *   ** A PROP THAT HOLDS ONE VALUE IS A CLAIM THAT ONLY ONE THING CAN BE TRUE. **
     *   *(paid a third time; see §1AV.175 and §1AV.180)*
     */
    const [hoverPartyFocus, setHoverPartyFocus] = useState<RailFocus | null>(null);
    /**
     * ⭐ #145 — THE APPOINTMENT A SEARCH SENT US TO.
     *
     * ⛔ A SECOND PIECE OF STATE, NOT A REUSE OF hoverApptId, AND THE DIFFERENCE IS THE
     * POINT. `hoverApptId` is set by onMouseEnter and cleared by onMouseLeave — nothing
     * else ever writes it. Setting it from a search result would work for about half a
     * second, until the cursor crossed any chip and wiped it. A scheduler who jumped
     * three weeks back to find one load would arrive at a board with nothing marked.
     *
     * ⚠️ CLEARED ON AN EXPLICIT ACT, NEVER ON A TIMER. A highlight that fades on its own
     * is one the scheduler can miss entirely by looking away while the grid loads.
     */
    const [focusApptId, setFocusApptId] = useState<string | null>(null);

    /**
     * ---- ⭐⭐⭐ THE SEARCH RESULT'S FOCUS, ARMED NOW AND FIRED ON ARRIVAL -------
     *
     * > Pete, 2026-09-27: *"the calendar, driver, carrier, client, orders, labor,
     * > communications, history all follow … you just see that one order on the
     * > screen."*
     *
     * ⭐⭐ IT CALLS `showAttachedFor`, WHICH IS THE CHIP-CLICK PATH. A search result
     * therefore lands in a state byte-identical to clicking that chip on the board —
     * same focus, same label, same paperwork target, same tray view, and (since this
     * morning) the same narrowed board. ⛔ Building a second route to "focus this
     * load" is how two gestures that look alike start differing in ways nobody can
     * name.
     *   ** THE SECOND WAY TO REACH A STATE SHOULD BE A SECOND CALLER, NOT A SECOND
     *      IMPLEMENTATION. **
     *
     * ⛔⛔ IT WAITS FOR THE APPOINTMENT RATHER THAN GIVING UP. `calendar` arrives
     * asynchronously and may still hold the PREVIOUS day when this first runs, so an
     * `else { clear }` would throw the request away a frame before the data landed.
     * Absence is "not yet", not "never".
     *
     * ⛔ THE DATE GUARD IS WHAT MAKES WAITING SAFE. Without it a request that can
     * never be satisfied — a cancelled load, a stale search row — would sit armed and
     * fire days later on whatever board happened to contain that id. With it, the
     * moment the scheduler navigates anywhere else the request is void.
     *   ** AN ACTION THAT WAITS FOREVER NEEDS A CONDITION THAT CAN GO FALSE. **
     *
     * ⚠️ DAY VIEW ONLY, and that is not a limitation: `jumpToAppointment` forces Day.
     * `calendar` is the only payload that carries the appointment, which is the same
     * reason the edit dialog's gate had to be widened earlier today.
     */
    const [pendingFocus, setPendingFocus] = useState<{ appointmentId: string; localDate: string } | null>(null);
    useEffect(() => {
        if (!pendingFocus) return;
        /* ⛔ The scheduler moved on — the request is void, not pending. */
        if (pendingFocus.localDate !== date) { setPendingFocus(null); return; }
        const found = (calendar?.appointments ?? []).some(
            a => String(a.appointmentId) === pendingFocus.appointmentId,
        );
        /* ⚠️ NOT LOADED YET. Stay armed; the date guard above bounds the wait. */
        if (!found) return;
        /**
         * ⛔⛔ `showAttachedFor` IS A **TOGGLE** ON ITS SECOND CALL — decision 195's
         * *"a second click on a grid chip should return to the standing party"*. So
         * calling it for a load that is ALREADY focused would UN-focus it, and
         * searching for the truck you are currently looking at would clear the screen.
         *   ** REUSING A HANDLER MEANS INHERITING EVERY BRANCH OF IT, INCLUDING THE
         *      ONE THAT UNDOES THE THING YOU WANTED. **
         * ⭐ Already focused is already the asked-for state, so this is a no-op, not
         * a second path.
         */
        if (railFocusRef.current?.kind === 'appointment'
            && railFocusRef.current.appointmentId === pendingFocus.appointmentId) {
            setPendingFocus(null);
            return;
        }
        showAttachedFor(pendingFocus.appointmentId);
        setPendingFocus(null);
    }, [pendingFocus, date, calendar, showAttachedFor]);

    /**
     * ---- ⭐⭐ WHAT IS **SELECTED** ON THE BOARD (decision 190, 2026-09-20) --------
     *
     * ⭐ `railFocus.appointmentIds` IS ALREADY EXACTLY THIS LIST and has been since
     * 2026-09-10 — a driver's loads, a client's loads, or the one load that was
     * clicked. ⛔ Nothing new is computed here; the old code simply could not SHOW a
     * set because `highlightAppointmentId` held one value.
     *
     * ⚠️ THE SEARCH RESULT (#145) JOINS THE SET rather than sitting behind it. It is
     * a thing the scheduler deliberately navigated to, so it is marked for the same
     * reason the clicked scope is — and with hover no longer scoping, there is no
     * longer a chain in which it could be "third".
     */
    /**
     * ⭐⭐⭐ ONE PREVIEW, FROM TWO SOURCES, AND THE CARD WINS.
     *
     * ⚠️ A party card hover and a chip hover cannot both be live — the cursor is in one
     * place — but they clear on different events, so an order is needed rather than an
     * assumption. The CARD wins because the rail sits over the board: leaving a card can
     * land the cursor on a chip mid-transition.
     *
     * ⛔⛔ AND A **BLOCK** PREVIEWS NOTHING. A door outage is not a load; scoping five
     * panels to it would answer every one of them with an empty list and no reason why.
     *
     * ⚠️ READ FROM `apptsRef`, NOT FROM `calendar` — the ref is what the rest of the
     * hover path already uses, so the preview cannot disagree with the ring beside it.
     */
    const previewFocus = useMemo<RailFocus | null>(() => {
        if (hoverPartyFocus) return hoverPartyFocus;
        if (!hoverApptId) return null;
        /* ⛔ A RAIL ROW MARKS; IT DOES NOT SCOPE. Returning null leaves every panel
           at (ALL) while `highlightAppointmentId` still outlines the appointment on the
           board - which is what Pete asked for, and what breaks the loop, because
           the panel stops rebuilding the row the cursor is sitting on.
           ⭐ A PARTY hover is untouched: it returns above from `hoverPartyFocus`,
           whichever surface it came from. */
        if (hoverApptOrigin !== 'grid') return null;
        const a = apptsRef.current.find(x => String(x.appointmentId) === hoverApptId);
        if (!a || a.direction === 'BLOCK') return null;
        const when = a.scheduledStartUtc
            ? new Date(a.scheduledStartUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
            : null;
        const detail = [a.dockDoorCode, when].filter(Boolean).join(' · ');
        return focusFromAppointment(a, detail ? `${a.appointmentNumber} · ${detail}` : a.appointmentNumber,
                                    driverNameForAppt(a.appointmentId));
    }, [hoverApptId, hoverApptOrigin, hoverPartyFocus, driverNameForAppt]);

    /**
     * ---- ⭐⭐ THE PAPERWORK TRAY'S HEADER SCOPE (Pete, 2026-09-20, item 15) ------
     *
     * ⛔⛔ IT IS COMPUTED HERE AND NOT IN `CheckInPanel`, AND THAT IS FORCED. Both
     * tray components are built in THIS file and handed down as CheckInPanel's
     * `footer` prop, so they are outside the scope where `effFocus` / `effParty`
     * exist. Computing it there and threading it back through the footer would be
     * the same expression in two files.
     *
     * ⚠️ AND IT COLLAPSES TO THREE TERMS, WHICH IS NOT A SIMPLIFICATION — IT IS THE
     * SAME ANSWER. CheckInPanel's pair reads
     *   effFocus  = previewFocus ?? railFocus
     *   effParty  = previewFocus (non-appointment) ?? partyScope
     * and the rails take `effFocus ?? effParty`. When `previewFocus` is set it wins
     * the FIRST term, so the second never decides anything; when it is null the two
     * reduce to `railFocus ?? partyScope`. ⛔ The two variables exist there because
     * the HEADING needs to tell a preview from a commitment — this header does not.
     *   ** A DERIVED VALUE MAY BE SIMPLIFIED ONLY AGAINST THE USES IT HAS, AND THE
     *      OTHER USE IS WHY THE ORIGINAL IS NOT WRITTEN THIS WAY. **
     */
    const paperworkFocus = previewFocus ?? railFocus ?? partyScope;

    const selectedApptIds = useMemo(() => {
        const ids = new Set<string>(railFocus?.appointmentIds ?? []);
        if (focusApptId) ids.add(focusApptId);
        return ids;
    }, [railFocus, focusApptId]);

    /**
     * ---- ⭐⭐ THE PREVIEW SET — NOTHING NEW IS COMPUTED HERE EITHER -------------
     *
     * ⛔ Pete, 2026-09-21: *"When I hover a client, it is not highlighting the
     * appointments on the calendar its associated with."*
     *
     * `previewFocus.appointmentIds` has carried exactly this list since the hover
     * preview was built — a party card's loads, or the one load under the cursor.
     * ⛔⛔ It reached the RAIL and never the BOARD, because the board's only hover
     * input was `highlightAppointmentId`, which holds a single id.
     *
     * ⚠️ NOT FOLDED INTO `selectedApptIds`. The two sets must stay apart or a
     * passing cursor draws the same mark as a deliberate click — the confusion
     * decision 190 exists to prevent.
     *
     * ⛔ AND THE FILTER STILL DOES NOT MOVE. `filterAppts` reads the committed
     * scope and is untouched by this; S1AV.179's bound is intact.
     *   ** A BAN ON MOVING THE BOARD IS NOT A BAN ON MARKING IT. **
     */
    const previewApptIds = useMemo(
        () => new Set<string>(previewFocus?.appointmentIds ?? []),
        [previewFocus],
    );

    /** Appointments that already have an arrival attached — see ONE TRUCK = ONE APPOINTMENT. */
    const [claimedApptIds, setClaimedApptIds] = useState<ReadonlySet<string>>(new Set());
    /** Bumped by Refresh so the arrivals panel reloads with the grid. */
    const [refreshKey, setRefreshKey] = useState(0);

    /**
     * ⭐ #145 — JUMP THE BOARD TO A SEARCH RESULT.
     *
     * ⛔ THE DAY COMES FROM THE SERVER (`hit.localDate`), NOT FROM THE BROWSER. The
     * appointment's local day is a fact about the FACILITY's timezone; a scheduler
     * working from home in another zone would compute a different day for the same
     * truck and land on an empty board (arch §32).
     *
     * ⚠️ SETTING `date` IS ENOUGH TO NAVIGATE — the fetch effect keys on it. There is no
     * router or query-param involved, so nothing else needs touching.
     */
    function jumpToAppointment(localDate: string, appointmentId: string, facilityCode?: string) {
        setFocusApptId(appointmentId);
        /**
         * ---- ⭐⭐⭐ AND EVERYTHING FOLLOWS IT (Pete, 2026-09-27) ---------------
         *
         * > *"you should be able to single click this result and the calendar, driver,
         * > carrier, client, orders, labor, communications, history all follow, the
         * > search disappears and you just see that one order on the screen."*
         *
         * ⛔⛔ `setFocusApptId` ALONE ONLY DRAWS A RING. It feeds `selectedApptIds`
         * and nothing else — no `railFocus`, so the five rails kept showing the whole
         * day, and since the chip-narrow landed this morning the board did too. The
         * jump took you to the truck and then said nothing about it.
         *
         * ⛔ THE FOCUS CANNOT BE BUILT HERE. `focusFromAppointment` needs the
         * `AppointmentDto`, and at this instant the date has only just been set — the
         * fetch has not run, so the appointment is not in memory. It has to be armed
         * now and fired when the day arrives.
         *   ** A HANDLER THAT NAVIGATES CANNOT ALSO READ WHAT IT NAVIGATED TO. **
         *
         * ⚠️ IT CARRIES THE **DATE**, not just the id. A pending id alone would sit
         * there if the appointment never appeared — cancelled, moved, or the search
         * lying — and then latch onto whatever board eventually contained that id,
         * days later and unasked. The effect only fires while `date` still equals the
         * date we jumped to.
         *   ** A DEFERRED ACTION MUST CARRY THE CONDITIONS THAT MADE IT CORRECT. **
         */
        setPendingFocus({ appointmentId, localDate });
        /**
         * ---- ⭐⭐ A JUMP MAY NOW CROSS A FACILITY (decision 210) ---------------
         *
         * ⛔ An `exactHits` row can be at another site -- that is the whole point of
         * the exact pass. Jumping to its DATE while leaving the picker where it is
         * lands on an empty board and looks like the search lied.
         *
         * ⚠️ OPTIONAL, AND ONLY ACTED ON WHEN IT DIFFERS. Every scoped result is at
         * the current facility, so passing it always would fire a needless facility
         * change -- which now clears the board on purpose.
         * ⛔ The picker's own list is what the scheduler may see; the server bounded
         * the exact pass to the same set, so this cannot navigate somewhere they
         * have no access to.
         */
        if (facilityCode && facilityCode !== facilityId) setFacilityId(facilityCode);
        if (localDate !== date) setDate(localDate);
        /**
         * ⛔ DAY VIEW, ALWAYS. A result found while standing on a month view would
         * otherwise land on a month grid where one appointment is a sliver — the
         * scheduler asked to be taken TO this truck, not near it.
         */
        setViewMode('day');
    }

    /**
     * ⭐ IS THERE ANYTHING ON *THIS DAY* TO BACK OUT?
     *
     * ⛔ ASKS THE SERVER, WHICH ASKS THE APPOINTMENTS. This used to filter the UPLOAD
     * log client-side — and mass create never writes an upload row, so a mass-created
     * batch could never have a button. Pete, 2026-08-24: *"After I did a mass create
     * of 5 appointments, the undo batch button disappeared. Now I can't remove them.
     * It stayed for the 8 I uploaded."* Five appointments with no way back.
     *
     * ⚠️ THE NEWEST COMMITTED ONE WINS. Two uploads for the same day is a real case
     * (a partial file, then the rest), and backing out "the last one" is what Pete
     * asked for — not all of them at once.
     *
     * ⛔ A FAILURE HERE HIDES THE BUTTON RATHER THAN BREAKING THE PAGE. This is a
     * convenience on a screen whose job is the schedule; a lookup blip must not take
     * the calendar down.
     */
    useEffect(() => {
        let cancelled = false;
        if (facilityId === null) { setBackoutFor(null); return; }
        (async () => {
            try {
                const { sources } = await listBackoutSources(await getToken(), facilityId, date);
                if (cancelled) return;
                /**
                 * ⚠️ THE NEWEST WINS. Two batches on one day is a real case — a partial
                 * file then the rest, or an upload followed by a hand-typed mass create —
                 * and backing out "the last one" is what Pete asked for, not all of them.
                 * The server already sorts newest first.
                 */
                setBackoutFor(sources[0] ?? null);
            } catch {
                if (!cancelled) setBackoutFor(null);
            }
        })();
        return () => { cancelled = true; };
    }, [facilityId, date, refreshKey, getToken]);

    /**
     * ⛔ THE DESTRUCTIVE ACT. Deletes; does not cancel.
     *
     * ⚠️ THE SERVER'S OWN SENTENCE IS WHAT IS SHOWN — it accounts for every
     * appointment, including the ones it REFUSED to remove. Re-wording it here would
     * mean two versions of one fact, and the refused count is the half people skip.
     */
    const runBackout = useCallback(async () => {
        if (!backoutFor || facilityId === null) return;
        setBackoutBusy(true);
        try {
            /**
             * ⛔ TWO KINDS, ONE BUTTON. An upload is keyed by its UploadId, a mass-create
             * batch by its uuid — but both go through the same judge and the same
             * executor on the server, so the only thing that differs here is which
             * function is called.
             */
            const r = backoutFor.kind === 'Upload'
                ? await backOutUpload(await getToken(), Number(backoutFor.id), facilityId, date)
                : await backOutMassCreateBatch(await getToken(), backoutFor.id, facilityId, date);
            setBackoutResult(r);
            setRefreshKey(k => k + 1);
            void reload();
        } catch (err) {
            setBackoutResult({
                deleted: 0, cancelled: 0, kept: [],
                message: err instanceof ApiCallError ? err.details.error : 'The back-out failed.',
            });
        } finally { setBackoutBusy(false); }
    }, [backoutFor, facilityId, date, getToken, reload]);

    const handleCreateFromCheckIn = (c: CheckInDto) => {
        setPendingCheckInId(c.checkInId);
        // ⚠️ WAS setNewPrefill(null) — the dialog opened blank even though the
        // card it was launched from carried the driver, the carrier and his
        // paperwork. No door or time here: the scheduler is still choosing those.
        setNewPrefill({ dockDoorId: null, startLocal: '', endLocal: '', ...prefillFromCheckIn(c) });
        setNewOpen(true);
    };

    const handleNewClick = async () => {
        if (facilityId === null) return;
        setNewPrefill(null);
        // Week/month don't keep a day DTO loaded, and the create dialog needs the
        // day's doors + slot length. Ensure the anchor day is loaded first.
        if (!calendar || calendar.date !== date) {
            try {
                const token = await getToken();
                const data = await getCalendar(token, facilityId, date);
                setCalendar(data);
            } catch (err) {
                setToast({ kind: 'error', msg: `Couldn't prepare that day: ${formatError(err)}` });
                return;
            }
        }
        setNewOpen(true);
    };

    const handleAppointmentCreated = (appt: AppointmentDto) => {
        setCalendar((prev) =>
            prev ? { ...prev, appointments: [...prev.appointments, appt] } : prev,
        );
        reload();
        setToast({ kind: 'success', msg: `Appointment ${appt.appointmentNumber} created.` });
    };

    // ---- Move (reschedule + door change) ----
    // Patch/restore an appointment in whichever dataset holds it (day board and
    // week grid share this handler).
    const patchApptEverywhere = (id: number, patch: Partial<AppointmentDto>) => {
        setCalendar((prev) => prev ? { ...prev, appointments: prev.appointments.map((a) => a.appointmentId === id ? { ...a, ...patch } : a) } : prev);
        setRangeData((prev) => prev ? { ...prev, appointments: prev.appointments.map((a) => a.appointmentId === id ? { ...a, ...patch } : a) } : prev);
    };
    const restoreApptEverywhere = (id: number, original: AppointmentDto) => {
        setCalendar((prev) => prev ? { ...prev, appointments: prev.appointments.map((a) => a.appointmentId === id ? original : a) } : prev);
        setRangeData((prev) => prev ? { ...prev, appointments: prev.appointments.map((a) => a.appointmentId === id ? original : a) } : prev);
    };

    const handleMoveAppointment = async (ev: MoveAppointmentEvent, override = false) => {
        const original =
            calendar?.appointments.find((a) => a.appointmentId === ev.appointmentId) ??
            rangeData?.appointments.find((a) => a.appointmentId === ev.appointmentId);
        if (!original) return;

        // Optimistic update — move the appointment immediately
        const door = calendar?.doors.find((d) => d.dockDoorId === ev.dockDoorId);
        patchApptEverywhere(ev.appointmentId, {
            dockDoorId: ev.dockDoorId,
            dockDoorCode: door?.code ?? original.dockDoorCode,
            scheduledStartUtc: ev.newStartUtc,
            scheduledEndUtc: ev.newEndUtc,
            /**
             * ⛔⛔ MIRROR THE SERVER'S PROMOTION LOCALLY, OR THE CHIP SNAPS TO 7:30.
             *
             * ⚠️ Pete, 2026-08-24: *"always moves it to 7:30am first and then hangs
             * forever. Never moves it to the location you put it at until after it does
             * the time fix."* Every word of that is this line being absent.
             *
             * ⛔ THE OPTIMISTIC PATCH SET THE TIMES BUT NOT THE MODE. `rescheduleAppointment`
             * now writes SchedulingMode = 'TimeSlot' on the server — but locally the
             * appointment was still 'DayOnly', so effectiveSpans DISCARDED the new time
             * it had just been given and re-packed the chip at the facility's opening
             * time. 7:30 is not a coincidence; it is pack index 0.
             *
             * ⛔ AND THE "HANGS FOREVER" IS THE POLL BEING SUPPRESSED. The only thing
             * that used to correct it was the 30-second background reload, and
             * `interactingRef` deliberately holds that off while the notify dialog is
             * open. So the chip sat wrong until he dismissed the dialog — *"does the
             * notify, then moves it."*
             *
             * ⚠️ 'TimeSlot' IS THE TYPED VALUE. The server's mapper narrows the column to
             * 'DayOnly' or null, but the DTO type on this side is
             * 'TimeSlot' | 'DayOnly' | undefined — and isDayOnly only ever tests for
             * 'DayOnly', so any non-DayOnly value reads the same to the renderer. I
             * reached for null first and the compiler was right to refuse it.
             */
            schedulingMode: 'TimeSlot',
        });

        /**
         * ⛔ CLAIM THIS GENERATION BEFORE THE AWAIT, NOT AFTER.
         *
         * ⚠️ A poll that started BEFORE this drop is already in flight with a stale
         * day in it. Bumping the counter here means its result is discarded when it
         * lands, instead of stomping the patch above. Bumping it after the await would
         * be too late — the stale reload would have already won.
         */
        mutationSeq.current += 1;
        mutatingRef.current = true;

        try {
            const token = await getToken();
            const saved = await rescheduleAppointment(token, ev.appointmentId, {
                dockDoorId: ev.dockDoorId !== original.dockDoorId ? ev.dockDoorId : undefined,
                scheduledStartUtc: ev.newStartUtc,
                scheduledEndUtc: ev.newEndUtc,
                overrideWarnings: override,
            });

            /**
             * ⭐⭐ REDRAW THE CHIP AT THE LENGTH THAT WAS ACTUALLY STORED (#264).
             *
             * Pete, 2026-08-26: *"then after that, the appointment time is expanded
             * from 30 minutes to 1 hour."*
             *
             * ⛔ THE OPTIMISTIC SPAN ABOVE IS A GUESS AND CANNOT BE ANYTHING ELSE.
             * The grid draws from ONE `slotMinutes` for the whole day, which
             * `getCalendarDay` reads from the INBOUND window and its own comment
             * calls "a representative direction". An outbound truck promoted out of
             * the queue gets the OUTBOUND rule on the server. When those differ, the
             * chip is drawn 30 minutes long over a row that says 60.
             *
             * ⛔ THE FIX IS NOT A SECOND COPY OF THE SLOT RULE OVER HERE. Duplicating
             * it is what produced the disagreement in the first place; the server is
             * the only party that knows, so the server says, and this applies it.
             *
             * ⚠️ `saved` IS NULL AGAINST A BACKEND THAT STILL ANSWERS 204, and that
             * is not a failure — the reload below corrects the chip exactly as it
             * does today. The two halves deploy separately and the frontend must not
             * require the newer one.
             */
            if (saved) {
                patchApptEverywhere(ev.appointmentId, {
                    scheduledStartUtc: saved.scheduledStartUtc,
                    scheduledEndUtc: saved.scheduledEndUtc,
                    schedulingMode: saved.schedulingMode,
                });
            }

            // Move is saved. Offer to email the carrier the new time for any
            // upcoming appointment (REQUESTED or APPROVED) — the carrier is
            // expecting that slot regardless of who created it. Opt-in per move.
            // If there's no carrier email on file, notify resolves to a no-op
            // with an informative toast.
            if (original.direction !== 'BLOCK' && (original.statusCode === 'REQUESTED' || original.statusCode === 'APPROVED')) {
                setNotifyPrompt({
                    appointmentId: ev.appointmentId,
                    appointmentNumber: original.appointmentNumber,
                    event: 'rescheduled',
                    drivers: original.drivers ?? [],
                    carrierName: original.carrierName,
                });
            } else {
                setToast({ kind: 'success', msg: original.direction === 'BLOCK' ? 'Block moved.' : 'Appointment moved.' });
            }

            /**
             * ⭐⭐ RE-READ NOW, NOT WHENEVER THE 30-SECOND POLL NEXT FIRES.
             *
             * ⛔ THIS IS THE SECOND HALF OF PETE'S *"about 10 seconds later"*. Nothing
             * here used to refresh after a successful move — the optimistic patch was
             * the whole picture until a background poll happened along. And the notify
             * dialog makes it worse: it sets interactingRef, which SUPPRESSES the poll,
             * so the correction could not arrive until the dialog was dismissed. Hence
             * "click on that, then about ten seconds later it appears".
             *
             * ⚠️ AND IT IS NOW A CORRECTNESS NEED, NOT ONLY SPEED. Since this morning
             * the server sets the slot length itself when promoting a day-only truck,
             * so the end time it STORED may differ from the one the client optimistically
             * drew. The route answers 204 with no body, so a re-read is the only way the
             * screen can learn what was actually saved.
             *
             * ⭐ Deliberately NOT awaited before the notify dialog opens — the scheduler
             * should not wait on a refresh to be asked a question.
             */
            mutatingRef.current = false;
            void backgroundReload();
            /* ⛔⛔ AND THE **RAILS**, WHICH `backgroundReload` DOES NOT TOUCH (Pete,
               2026-09-20): *"When I dragged an appointment to a new spot, the history
               didn't update right away in the panel."*
               ⭐ `backgroundReload` re-reads the BOARD. Labour, Communications and History
               re-read on `refreshKey`, which only the 30-second arrivals poll was bumping —
               so a move showed instantly on the grid and up to half a minute later in the
               panel describing it.
                 ** A REFRESH THAT REACHES ONE HALF OF THE SCREEN IS A SCREEN THAT
                    DISAGREES WITH ITSELF FOR AS LONG AS THE POLL TAKES. **
               ⚠️ The note on `paperworkKey` warns that bumping this restarts the arrivals
               poll. That warning was about FILING A DOCUMENT, which does not move a truck.
               A move changes the arrival's door — restarting the poll is correct here. */
            setRefreshKey(k => k + 1);
        } catch (err) {
            // Rollback optimistic update
            restoreApptEverywhere(ev.appointmentId, original);
            if (err instanceof ApiCallError && err.details.conflicts?.length) {
                const warningsOnly = err.details.conflicts.every((c) => c.severity === 'warning');
                if (warningsOnly && !override) {
                    const types = err.details.conflicts.map((c) => c.type);
                    const title = types.includes('DoorDedicated')
                        ? 'Door reserved'
                        : types.includes('FacilityCapExceeded')
                            ? 'Over the facility limit'
                            : 'Heads up';
                    setCapOverride({
                        ev,
                        appointmentNumber: original.appointmentNumber,
                        title,
                        detail: err.details.conflicts.map((c) => c.detail).join(' '),
                    });
                    return;
                }
                setToast({
                    kind: 'error',
                    msg: `Couldn't move: ${err.details.conflicts.map((c) => c.detail).join('; ')}`,
                });
            } else {
                setToast({ kind: 'error', msg: `Couldn't move: ${formatError(err)}` });
            }
        } finally {
            // ⚠️ EVERY exit path, including the conflict-dialog early return above.
            // A flag left true would silence the poll for the rest of the session.
            mutatingRef.current = false;
        }
    };

    // ---- Notify-carrier prompt (after a notifiable action) ----
    // Past-tense verb per event, for toasts and the prompt copy.
    /**
     * ---- ⭐⭐⭐ `Record<NotifyEvent, string>` IS WHAT CAUGHT THE NEW EVENT ----
     *
     * Adding `'updated'` to `NotifyEvent` (decision 186) made this map fail to
     * compile — TS2741, *"Property 'updated' is missing"*. ⭐ **That is the
     * compiler enforcing a two-lists-must-agree rule**, and it found a second
     * event→text map that a read of the prompt copy had missed entirely.
     *
     *   ** `Record<Union, T>` IS A TWO-LISTS PROBLEM THE COMPILER SOLVES. THE SAME
     *      MAP TYPED `Record<string, T>` IS THE SAME PROBLEM UNSOLVED ** — it would
     *      have compiled clean and rendered `undefined` into a toast.
     *
     * ⚠️ `RANK_FOR` in `src/lib/auth.ts` is deliberately NOT of this shape (it holds
     * 2 of the gate names, not all), so it gets none of this protection — which is
     * exactly why a guard is owed for it. §1AV.165.
     *
     * ---- ⛔⛔ AND `updated: 'updated'` WOULD HAVE BEEN A COLLISION ----
     *
     * `handleDismissNotify` already falls back to the literal `'updated'` when
     * there is no prompt. Both render `Appointment ${verb}.` — so the toast for a
     * REAL details-edit would have been byte-identical to the toast for "no prompt
     * was showing at all".
     *
     *   ** A DEFAULT THAT COLLIDES WITH A REAL VALUE IS INDISTINGUISHABLE FROM IT
     *      AT THE MOMENT YOU MOST NEED TO TELL THEM APART. ** Same shape as the
     *      `'TBD'` / `NO_SELECTION` defect at §1R.8a: two distinct states, one
     *      sentence.
     *
     * ⛔ So the verb is **'details updated'** — matching the prompt's *"had its
     * details updated"*, and distinct from the fallback. ⛔ **Do not "tidy" these
     * two into agreement.**
     */
    const NOTIFY_VERB: Record<NotifyEvent, string> = {
        created: 'scheduled',
        rescheduled: 'moved',
        approved: 'approved',
        cancelled: 'cancelled',
        noshow: 'marked no-show',
        arrived: 'marked arrived',
        departed: 'marked departed',
        updated: 'details updated',
    };

    // Lazy fetch for the calendar hover timeline (GanttCalendar caches per appt).
    const fetchEvents = useCallback(async (appointmentId: number) => {
        const token = await getToken();
        return getAppointmentEvents(token, appointmentId);
    }, [getToken]);

    // Events that actually text a driver. MUST match DRIVER_NOTIFY_EVENTS in
    // src/functions/appointmentStatus.ts — offering "Text drivers" for an event
    // the server won't text on produces a button that reports success and sends
    // nothing, which is worse than not offering it. There is a deploy guard on
    // both lists.
    const DRIVER_TEXT_EVENTS: readonly NotifyEvent[] = ['approved', 'rescheduled', 'cancelled'];

    const handleNotify = async (targets: NotifyTarget[]) => {
        if (!notifyPrompt) return;
        const { appointmentId, event } = notifyPrompt;
        const verb = NOTIFY_VERB[event];
        const wantCarrier = targets.includes('carrier');
        setNotifyPrompt(null);
        try {
            const token = await getToken();
            const r = await notifyAppointment(token, appointmentId, event, targets);

            // Report each audience separately. Folding them into one "notified"
            // is how a driver text that never went out reads as a success.
            const parts: string[] = [];
            if (wantCarrier) {
                parts.push(r.carrierNotified ? 'carrier emailed' : 'no carrier email on file');
            }
            if (r.driversTexted) parts.push(`${r.driversTexted} driver${r.driversTexted === 1 ? '' : 's'} texted`);
            // An opt-out is not a failure — say so plainly so nobody re-sends.
            if (r.driversOptedOut) {
                parts.push(`${r.driversOptedOut} driver${r.driversOptedOut === 1 ? ' has' : 's have'} opted out of texts`);
            }
            if (r.driverNotifyFailed) parts.push('a driver text failed');

            setToast({
                kind: r.driverNotifyFailed ? 'error' : 'success',
                msg: parts.length
                    ? `Appointment ${verb} — ${parts.join(', ')}.`
                    : `Appointment ${verb}. Nobody to notify.`,
            });
        } catch (err) {
            setToast({
                kind: 'error',
                msg: `${verb.charAt(0).toUpperCase()}${verb.slice(1)}, but the notification failed: ${formatError(err)}`,
            });
        }
    };

    const handleDismissNotify = () => {
        const verb = notifyPrompt ? NOTIFY_VERB[notifyPrompt.event] : 'updated';
        setNotifyPrompt(null);
        setToast({ kind: 'success', msg: `Appointment ${verb}.` });
    };

    // ---- Right-click → context menu ----
    const handleRequestContextMenu = (ev: RequestContextMenuEvent) => {
        setContextMenu({ appt: ev.appt, x: ev.x, y: ev.y });
    };

    // ---- Facility-limit interactions ----
    const handleOpenLimits = () => {
        setSettingsTab('appointment-limits');
        setSettingsOpen(true);
    };
    const handleRequestLimitMenu = (ev: RequestLimitMenuEvent) => {
        setLimitMenu({ bands: ev.bands, x: ev.x, y: ev.y });
    };
    const makeLimitInactive = async () => {
        const menu = limitMenu;
        setLimitMenu(null);
        if (!menu) return;
        try {
            const token = await getToken();
            await Promise.allSettled(menu.bands.map((b) => setCapacityRuleActive(token, b.capacityId, false)));
            setToast({ kind: 'success', msg: 'Limit made inactive. Re-enable it under Appointment Limits.' });
            await reload();
        } catch {
            setToast({ kind: 'error', msg: 'Could not update the limit.' });
        }
    };
    const deleteLimit = async () => {
        const menu = limitMenu;
        setLimitMenu(null);
        if (!menu) return;
        try {
            const token = await getToken();
            await Promise.allSettled(menu.bands.map((b) => deleteCapacityRule(token, b.capacityId)));
            setToast({ kind: 'success', msg: 'Limit deleted.' });
            await reload();
        } catch {
            setToast({ kind: 'error', msg: 'Could not delete the limit.' });
        }
    };

    // ---- Drag a limit bar to resize/move ----
    const applyLimitEdit = async (ev: EditLimitTimesEvent) => {
        try {
            const token = await getToken();
            await Promise.allSettled(ev.bands.map((b) => setCapacityRuleTimes(token, b.capacityId, ev.startMinutes, ev.endMinutes)));
            setToast({ kind: 'success', msg: 'Limit window updated.' });
            await reload();
        } catch {
            setToast({ kind: 'error', msg: 'Could not update the limit.' });
        }
    };
    const handleEditLimitTimes = (ev: EditLimitTimesEvent) => {
        const b0 = ev.bands[0];
        const singleDate = !!b0?.effectiveStartDate && b0.effectiveStartDate === b0.effectiveEndDate;
        if (singleDate) { void applyLimitEdit(ev); return; }   // one calendar day → apply immediately
        setPendingLimitEdit(ev);                                // recurring / date-range → confirm first
    };

    // ---- Door-dedication interactions ----
    const handleOpenDedications = () => {
        setSettingsTab('door-dedications');
        setSettingsOpen(true);
    };
    const handleRequestDedicationMenu = (ev: RequestDedicationMenuEvent) => {
        setDedicationMenu(ev);
    };
    const makeDedicationInactive = async () => {
        const menu = dedicationMenu;
        setDedicationMenu(null);
        if (!menu) return;
        try {
            const token = await getToken();
            await setDedicationRuleActive(token, menu.dedicationId, false);
            setToast({ kind: 'success', msg: 'Dedication made inactive. Re-enable it under Door Dedications.' });
            await reload();
        } catch {
            setToast({ kind: 'error', msg: 'Could not update the dedication.' });
        }
    };
    const deleteDedication = async () => {
        const menu = dedicationMenu;
        setDedicationMenu(null);
        if (!menu) return;
        try {
            const token = await getToken();
            await deleteDedicationRule(token, menu.dedicationId);
            setToast({ kind: 'success', msg: 'Dedication deleted.' });
            await reload();
        } catch {
            setToast({ kind: 'error', msg: 'Could not delete the dedication.' });
        }
    };
    const applyDedicationEdit = async (ev: EditDedicationTimesEvent) => {
        try {
            const token = await getToken();
            await setDedicationRuleTimes(token, ev.dedicationId, ev.startMinutes, ev.endMinutes);
            setToast({ kind: 'success', msg: 'Dedication window updated.' });
            await reload();
        } catch {
            setToast({ kind: 'error', msg: 'Could not update the dedication.' });
        }
    };
    const handleEditDedicationTimes = (ev: EditDedicationTimesEvent) => {
        const singleDate = !!ev.effectiveStartDate && ev.effectiveStartDate === ev.effectiveEndDate;
        if (singleDate) { void applyDedicationEdit(ev); return; }
        setPendingDedicationEdit(ev);
    };

    // Quick status action from context menu — same as detail-dialog buttons,
    // but no dialog open/close round-trip.
// ----------------------------------------------------------------------------
// EDIT 3 — REPLACE the existing doQuickAction with this version.
//
// What changed: the 'arrive' branch now requires a door. If the appointment
// already has one, it arrives as before. If it doesn't (it's in the unassigned
// queue), we open the Assign Door dialog instead of firing a call we know will
// 400. We ALSO catch a 400 from the arrive call as a belt-and-suspenders
// fallback (e.g. the door was cleared between load and click) and open the
// dialog then too. depart/cancel are unchanged.
// ----------------------------------------------------------------------------
const doQuickAction = (
    appt: AppointmentDto,
    label: 'arrive' | 'depart' | 'cancel' | 'approve' | 'reject' | 'noshow',
) => {
    // Arrive requires a door. No door yet → open the dialog to assign one.
    if (label === 'arrive' && appt.dockDoorId == null) {
        setAssignDoor({ appt });
        return;
    }
    // Destructive / reason-bearing actions confirm in-app first (no browser prompt).
    if (label === 'cancel' || label === 'noshow' || label === 'reject') {
        setPendingQuick({ appt, label });
        return;
    }
    void runQuickAction(appt, label);
};

// Executes the action (after any confirmation) and handles post-action UX:
// approve/cancel/no-show open the opt-in notify prompt; reject auto-emails the
// decline; arrive/depart just toast.
const runQuickAction = async (
    appt: AppointmentDto,
    label: 'arrive' | 'depart' | 'cancel' | 'approve' | 'reject' | 'noshow',
    reason?: string,
    /** Set only when the scheduler has signed off an outbound release (#97). */
    releaseOverride?: ReleaseOverrideDto,
) => {
    try {
        const token = await getToken();
        if (label === 'arrive') await markArrived(token, appt.appointmentId);
        else if (label === 'depart') await markDeparted(token, appt.appointmentId, releaseOverride);
        else if (label === 'approve') await approveAppointment(token, appt.appointmentId);
        else if (label === 'reject') await rejectAppointment(token, appt.appointmentId, reason ?? '');
        else if (label === 'cancel') await cancelAppointment(token, appt.appointmentId);
        else if (label === 'noshow') await markNoShow(token, appt.appointmentId);
        reload();
        if (appt.direction === 'BLOCK') {
            // Blocks have no carrier — never offer to notify.
            setToast({ kind: 'success', msg: 'Block cancelled.' });
            return;
        }
        if (label === 'reject') {
            // Reject auto-emails the decline; no opt-in prompt.
            setToast({ kind: 'success', msg: `${appt.appointmentNumber} rejected — carrier notified` });
        } else {
            // Every other status change offers the opt-in carrier notification.
            const event: NotifyEvent =
                label === 'arrive' ? 'arrived'
                : label === 'depart' ? 'departed'
                : label === 'approve' ? 'approved'
                : label === 'cancel' ? 'cancelled'
                : 'noshow';
            setNotifyPrompt({
                appointmentId: appt.appointmentId,
                appointmentNumber: appt.appointmentNumber,
                event,
                drivers: appt.drivers ?? [],
                carrierName: appt.carrierName,
            });
        }
    } catch (err) {
        // Fallback: arrive rejected for a missing door → open the dialog.
        if (
            label === 'arrive' &&
            err instanceof ApiCallError &&
            err.details.status === 400
        ) {
            setAssignDoor({ appt });
            return;
        }
        // ⛔ The outbound release gate. Not an error — the server is telling us a
        // named human has to acknowledge something before this truck leaves.
        if (
            label === 'depart' &&
            err instanceof ApiCallError &&
            err.details.code === 'OutboundReleaseBlocked'
        ) {
            setReleaseBusy(false);
            setReleaseFor({ appt });
            return;
        }
        setToast({ kind: 'error', msg: `Couldn't ${label}: ${formatError(err)}` });
    } finally {
        setReleaseBusy(false);
    }
};

/**
 * The scheduler signed the release off. Run the SAME depart path, now carrying
 * the acknowledgement — so the post-departure UX (reload, carrier notify prompt)
 * is identical whether or not there were warnings.
 */
const handleReleaseConfirm = async (override: ReleaseOverrideDto) => {
    if (!releaseFor) return;
    const appt = releaseFor.appt;
    setReleaseBusy(true);
    await runQuickAction(appt, 'depart', undefined, override);
    setReleaseBusy(false);
    setReleaseFor(null);
};
 
// Confirm handler for the Assign Door dialog: arrive WITH the chosen door.
const handleAssignDoorConfirm = async (dockDoorId: number) => {
    if (!assignDoor) return;
    setAssignBusy(true);
    try {
        const token = await getToken();
        await markArrived(token, assignDoor.appt.appointmentId, dockDoorId);
        const appt = assignDoor.appt;
        setAssignDoor(null);
        reload();
        setNotifyPrompt({
            appointmentId: appt.appointmentId,
            appointmentNumber: appt.appointmentNumber,
            event: 'arrived',
            drivers: appt.drivers ?? [],
            carrierName: appt.carrierName,
        });
    } catch (err) {
        setToast({ kind: 'error', msg: `Couldn't mark arrived: ${formatError(err)}` });
    } finally {
        setAssignBusy(false);
    }
};
 

    /**
      * ⭐⭐ ONE NODE, HANDED TO BOTH BOARDS (Pete, 2026-09-19) ----------------
      *
      * > Pete: *"Why doesn’t it look the same and why is there space below the
      * > status bar on week and month? Should be formatted identical."*
      *
      * ⛔⛔ THE COMPONENT WAS SHARED; THE FRAME WAS NOT. This lived on the PAGE,
      * above the boards’ cards — full viewport width, no tint, no border, and an
      * `mb-3` beneath it. Day’s sits INSIDE the grid card as a tinted strip. Same
      * markup, same props, and it still did not look the same.
      *
      *   ** DEDUPLICATING A COMPONENT DOES NOT DEDUPLICATE ITS SURROUNDINGS, AND
      *      THE SURROUNDINGS ARE MOST OF WHAT "LOOKS THE SAME" MEANS. **
      *
      * ✅ It is now passed as `header` to WeekBoard and MonthGrid, which wrap it in
      * `BOARD_HEADER_STRIP_CLASS` — the same string the Gantt reads — as the first
      * child inside their own cards. ⛔ `mb-3` is GONE: the strip has its own
      * padding, and the gap it created is the "space below the status bar".
      *
      * ⛔ EXTRACTED TO A CONST rather than inlined at both call sites, because two
      * inline copies is the thing this component exists to prevent.
      * ⚠️ The `viewMode !== 'day'` guard is gone: the Gantt is never handed this
      * prop, so "which views show it" is now decided by which component receives it
      * rather than by a condition that can drift from the one below it.
      */
            /**
              * ---- ⭐ AND IT MOVED **BELOW THE TOOLBAR** (Pete, 2026-09-19) ----
              *
              * > Pete: *"still not showing correct status bar at top - should mirror
              * > Day"* — with a screenshot of each.
              *
              * ⛔⛔ THE BAR WAS IDENTICAL AND IN THE WRONG PLACE. It rendered ABOVE the
              * Facility/date/Day-Week-Month toolbar, i.e. the very top of the page. On Day
              * the same component sits INSIDE the Gantt header — which is BELOW that
              * toolbar and directly above the grid. Same markup, same props, same styling,
              * and it still did not mirror Day, because **position is part of a control's
              * identity and none of it lives in the component.**
              *
              *   ** TWO CALL SITES OF ONE COMPONENT ARE CONSISTENT IN EVERYTHING THE
              *      COMPONENT OWNS. WHERE THEY SIT IS NOT ONE OF THOSE THINGS. **
              *
              * ✅ Moved here verbatim — after the toolbar closes, before the main pane —
              * so all three views read toolbar → status bar → grid.
              * ⚠️ `mb-3` is what holds it off the grid; the toolbar above supplies its own
              * gap. ⛔ DO NOT move it back inside the toolbar row: the toolbar wraps at
              * narrow widths and the filter would wrap with it, landing between the search
              * box and the buttons.
              */
            /**
              * ---- ✅✅ DECISION 184 — **WEEK AND MONTH ONLY** (2026-09-18) ----
              *
              * > Pete: *"The top filter needs to be moved to replace the middle status
              * > between date and appointments - doors. Its rundandant … **Moves only on
              * > the day screen, not the week or month.**"*
              *
              * ⛔⛤ **THIS REVERSES DECISION 110**, which was Pete's own call five days
              * earlier — shown *"Day only"* and *"Day only, drop it elsewhere"*, he said
              * *"Same treatment — each view's own header gets it."* ⭐ The contradiction
              * was put back to him with 110 quoted, and he confirmed Day only.
              *
              *   ** A REVERSAL THAT IS NOTICED AND CONFIRMED IS A DECISION. A REVERSAL
              *      THAT IS QUIETLY OBEYED IS A DOCUMENT THAT NOW CONTRADICTS ITSELF AND
              *      NOBODY KNOWS WHICH HALF IS LIVE. **
              *
              * ⛔⛔ `viewMode !== 'day'` IS THE WHOLE RISK IN THIS CHANGE. Day renders the
              * same component inside the Gantt header (`<StatusFilterBar>`); without this
              * gate the filter draws TWICE on the one screen that got the new one.
              * ⚠️ 110 would have DELETED this block. 184 keeps it — Week and Month have no
              * header strip to host the bar, and measuring whether they could was 110's
              * open first step, which 184 made moot.
              */
            /* ---- Status key doubles as a multi-select status filter ---- */
            /**
              * ---- ⛔⛤⛤ THIS WAS A HAND-ROLLED COPY OF <StatusFilterBar> ----
              *
              * ⭐ Pete, 2026-09-19: *"Now we can make the All Status Appointments Doors
              * bar the same across all three views (day, week, month) so its consistent"*
              *
              * ⛔⛔ THEY DIVERGED BECAUSE THERE WERE TWO OF THEM. `StatusFilterBar.tsx`
              * says it was *"Extracted from pages/Calendar.tsx (the strip at 2467-2532)"*
              * on 2026-09-18 — and the strip was never deleted. What sat here until
              * tonight was a byte-for-byte duplicate of that component's markup: same
              * classes, same `ring-2 ring-foreground/50`, same BLOCK hatch, same comments.
              *
              *   ** AN EXTRACTION THAT DOES NOT DELETE THE ORIGINAL IS A COPY. **
              *
              * ⛔ The component exists to end *"two lists that must agree forever"* — and
              * it became the second one. Every refinement of 2026-09-19 (the dot swatch,
              * the parenthesis-free counts, the tick, `hideStatuses`) landed in ONE of the
              * two, which is exactly why Day and Week/Month drifted apart.
              * ⚠️ And no guard could see it: `tenancy-row3-guard` asserted that
              * GanttCalendar RENDERS the component, never that this file does not
              * hand-roll it. It does now.
              *
              * ---- ⭐ SAME STYLING, PER-VIEW CONTENT (Pete's call, 2026-09-19) ----
              *
              * `dense` — so all three views look identical.
              * ⛔ NO `hideStatuses` HERE, unlike the Gantt. Month DRAWS `BLK` blocks; a
              * filter for something visibly on screen has to exist.
              * ⛔ ~~NO `showOverlayLegend={false}` HERE, unlike the Gantt. The Gantt hides
              * it because its capacity-band row already draws `Limits`; nothing on Week
              * or Month does, so this is the only place that legend lives.~~
              * ⭐ STRUCK THE SAME NIGHT (§0.3). **The Gantt no longer passes it either** —
              * Pete: *"Put the Limits purple icon back on the top bar"*. The band row draws
              * the word `Limits` followed by the ACTUAL BANDS, and only when there are any;
              * that is a readout, not a key. ** A VALUE AND A COLOUR KEY ARE NOT THE SAME
              * THING. ** ✅ So all three views now show the `Limits` swatch, and this prop
              * is passed by nobody. ⚠️ The sentence was true for about an hour and would
              * have been read as current for months.
              *
              *   ** IDENTICAL STYLING IS CONSISTENCY. IDENTICAL CONTENT WOULD BE A LIE
              *      ABOUT WHAT EACH VIEW CONTAINS. **
              */
    /**
     * ---- ⭐⭐⭐ Ⓡ: **ONE ELEMENT**, MOUNTED IN TWO HEADERS --------------------
     *
     * ⛔⛔ DEFINED ONCE AND PASSED, NOT WRITTEN TWICE. Day draws its header inside
     * `GanttCalendar`; Week and Month draw `boardHeader` below. Every other control
     * on this strip is duplicated across those two files, and `GanttCalendar` itself
     * records the cost: ⛔ DEDUPLICATING A COMPONENT DOES NOT DEDUPLICATE ITS
     * SURROUNDINGS. Ⓡ is the one control where the whole point is that it looks and
     * behaves identically everywhere, so it is handed down as a node.
     *   ** THE FIX FOR MARKUP WRITTEN TWICE IS ONE ELEMENT WITH TWO MOUNTS, NOT TWO
     *      COPIES KEPT IN STEP BY DISCIPLINE. **
     * ⭐ It also means the size drift the old pair suffered — `h-4` in one file,
     * `h-6` in the other, invisible because they were never adjacent — cannot recur.
     *
     * ⛔ SHOWN ONLY WHEN SOMETHING IS SCOPED, and the condition is **any** scope, not
     * the one that happens to be named. That widening is itself a recorded fix: Pete,
     * *"lost the circle R button and no appointments on the date - so stuck"* — a
     * party scope with no loads that day leaves `railFocus` null while `partyScope`
     * still filters, and the only exit un-rendered itself exactly when it was the
     * only way out.
     *   ** A RESET MUST BE VISIBLE WHENEVER ANYTHING NEEDS RESETTING — NOT WHENEVER
     *      THE STATE YOU HAPPENED TO NAME IS SET. **
     *
     * ⚠️ `railFocus ?? partyScope` IS A PRIORITY, NOT A CHOICE OF WHAT TO CLEAR. The
     * click clears all three regardless; this only decides which one the tooltip
     * names, and the narrower one is the more useful sentence.
     *
     * ⚠️ `motion-safe:` — a pulsing control is what `prefers-reduced-motion` exists
     * for; the amber ring carries the signal without the animation.
     */
    const scopeForReset: RailFocus | null = railFocus ?? partyScope;
    const resetScopeButton = (scopeForReset || warningScope) ? (
        <button
            type="button"
            onClick={resetAllScopes}
            title={scopeForReset
                ? `Showing only ${scopeForReset.label} ${focusScopeLabel(scopeForReset)} — click to reset the board and the panels to everything`
                : `Showing only ${warningScope?.label} — click to reset the board and the panels to everything`}
            aria-label={`Reset the ${scopeForReset?.label ?? warningScope?.label} scope`}
            className="ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full
                       border border-amber-500 bg-amber-100 text-xs font-bold leading-none
                       text-amber-800 ring-1 ring-amber-400/60 transition-colors
                       hover:bg-amber-200 motion-safe:animate-pulse"
        >
            R
        </button>
    ) : null;

    /* ⭐ DECISION 266 — ONE ELEMENT FOR ALL THREE VIEWS, like `resetScopeButton`:
       Day hands it to the Gantt header, Week and Month draw it beside their tally.
       ⭐ `reload` after a mark, so the chip's red board marks clear at once. */
    /* ⭐ F106 / F107 — the other two open-item chips ride in the SAME element, so all three
       sit together in every view's header (Pete: *"next to 29 appointments"*), in the app's
       warning tone. ⭐ No WMS covers past visits AND the range on screen. */
    const openItemsRange = viewMode === 'day' ? { start: date, end: date } : rangeBounds;
    const possibleNoShowsChip = (
        <>
            <PossibleNoShowsChip facilityCode={facilityId} getToken={getToken} onChanged={() => { void reload(); }} />
            <PossibleNoDeparturesChip facilityCode={facilityId} getToken={getToken} onChanged={() => { void reload(); }} />
            <NoWmsChip
                facilityCode={facilityId}
                fromDate={openItemsRange.start}
                toDate={openItemsRange.end}
                getToken={getToken}
                refreshKey={noWmsRefresh}
                onOpen={(rows, total) => setNoWmsList({ rows, total })}
                onRows={onNoWmsRows}
            />
        </>
    );

    const boardHeader = (
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                    {/**
                      * ⭐⭐ THE TWO FILTERS ARE ONE OCCUPANT (Pete, 2026-09-25) — the same
                      * change made to the Gantt header on the same day, and for the same
                      * reason. `justify-between` had FOUR children here (status bar, rule,
                      * work bar, tally) and distributed all four, so the work chips floated
                      * in the middle of the strip with the rule stranded beside them.
                      *   ** justify-between DISTRIBUTES CHILDREN, IT DOES NOT ALIGN THEM. **
                      * ⚠️ `flex-wrap` MADE IT LOOK INTERMITTENT rather than wrong: at some
                      * widths the row wrapped and the chips landed under the status bar,
                      * which reads as a layout that is merely tight.
                      */}
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <StatusFilterBar
                        dense
                        statusFilter={statusFilter}
                        toggleStatus={toggleStatus}
                        setStatusFilter={setStatusFilter}
                        statusCounts={statusCounts}
                        totalCount={totalCount}
                        className="min-w-0"
                    />
                    {/**
                      * ⭐⭐ THE WORK AXIS, BESIDE THE STATUS ROW — Pete's own choice of
                      * shape: *"Separate control beside the status bar."*
                      * ⛔ SEPARATED BY A RULE, NOT BY A GAP. Two chip rows with nothing
                      * between them read as one row that wrapped, and a scheduler would
                      * expect Unloading to clear when he presses All Status. It does not:
                      * they are two filters that AND.
                      *   ** WHEN TWO CONTROLS COMPOSE RATHER THAN COMPETE, THE SCREEN
                      *      HAS TO SAY SO, OR EVERY SURPRISE READS AS A BUG. **
                      */}
                    <span aria-hidden="true" className="h-3 w-px shrink-0 bg-border" />
                    <WorkFilterBar
                        dense
                        workFilter={workFilter}
                        toggleWork={toggleWork}
                        workCounts={workCounts}
                        className="min-w-0"
                    />
                    {/**
                      * ⭐⭐ THE WMS AXIS, THIRD IN THE STRIP — Pete's own placement:
                      * *"the WMS NO WMS search filters that follow Loading/Unloading
                      * next to status."* `follow` is an ORDER, so it goes after the work
                      * row, not between it and the status row.
                      * ⛔ ITS OWN RULE, for the reason the one above gives: three chip
                      * rows with two rules between them read as three filters; three rows
                      * with one rule read as two, and a scheduler would expect the wrong
                      * thing to clear. ⭐ The rules are cheap and the confusion is not.
                      */}
                    <span aria-hidden="true" className="h-3 w-px shrink-0 bg-border" />
                    <WmsFilterBar
                        dense
                        wmsFilter={wmsFilter}
                        toggleWms={toggleWms}
                        wmsCounts={wmsCounts}
                        className="min-w-0"
                    />
                    </div>
                    {/**
                      * ⭐ THE SAME TALLY THE GANTT HEADER CARRIES, in the same place, so
                      * the three views read alike.
                      * ⚠️ `totalCount` is the UNFILTERED count for the current range — the
                      * same number "All Status" shows, and deliberately so: two counts on
                      * one line that disagree is worse than one count.
                      * ⚠️ The door count does not vary across a week or a month; it is the
                      * facility's doors, and it is here because the row it mirrors has it.
                      */}
                    {/* ⛔ ~~the door count~~ — REMOVED 2026-09-19, same as the Gantt's.
                        Pete: *"I don't need the Doors Count."* ⭐ Across a week or a month
                        it was doubly inert: the same number on every day of every range. */}
                    {/* ⭐ THE TALLY AND ITS UNDO, ADJACENT (Pete, 2026-09-27). ⚠️ Wrapped
                        so the count and Ⓡ stay one occupant of the `justify-between` row
                        — as two children they would be distributed, which is the exact
                        fault this row's own note records about the filter bars. */}
                    <div className="flex shrink-0 items-center gap-2">
                        {possibleNoShowsChip}
                        <div className="whitespace-nowrap text-xs text-muted-foreground">
                            {totalCount} appointment{totalCount === 1 ? '' : 's'}
                        </div>
                        {resetScopeButton}
                    </div>
                </div>
    );

    return (
        /* FULL WIDTH, deliberately uncapped. This was mx-auto with maxWidth 1280,
           which on a 2560px monitor threw away half the screen and forced the
           Gantt to scroll horizontally to reach the afternoon. A dock schedule's
           value scales with how many HOURS and doors you can see at once, so
           there is no width at which showing less is better. Padding widens a
           little on large screens so the card doesn't touch the bezel. */
        <div className="flex flex-col h-full w-full gap-4 py-6 px-4 sm:px-6 2xl:px-8">
            {/**
              * ⛔ THE PAGE TITLE LIVES IN THE HEADER NOW (#251). It used to be
              * <h1 class="page-title">Dock schedule</h1> right here, inside the
              * scrolling page.
              *
              * ⭐ MOVING IT INTO THE 64px BAR GAVE BACK ~56px ON EVERY SCREEN — the
              * bar was already there and had a hole in the middle of it. Pete:
              * *"It will give us more real estate too."* A dock schedule's value
              * scales with how many hours and doors fit on screen at once, which is
              * the same reason this container is uncapped.
              *
              * ⚠️ AND THE WORDS ARE CONFIGURABLE — dbo.Company.SchedulerTitle. A
              * second <h1> here would put the page's name on screen twice and make
              * one of them un-brandable.
              */}

            {/* ---- Toolbar ---- */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Facility</span>
                    {lookups ? (
                        <Select
                            value={facilityId !== null ? String(facilityId) : ''}
                            onValueChange={(v) => setFacilityId(v)}
                        >
                            {/**
                              * ⭐ 10.5rem, NOT w-56 (Pete, 2026-09-10: *"shorten its
                              * length by a quarter"* — 14rem minus a quarter, exactly).
                              *
                              * ⛔ AN ARBITRARY VALUE, BECAUSE `w-42` DOES NOT EXIST.
                              * Tailwind's scale jumps 40, 44, 48 — `w-42` compiles to
                              * NOTHING and the trigger would silently keep its natural
                              * width. That is the shape of failure this codebase keeps
                              * meeting: a class that reads correct and does nothing.
                              * ⚠️ The built CSS is checked for this value, not assumed.
                              *
                              * ⚠️ THE POINT IS THE ROW, NOT THE CONTROL. This toolbar
                              * wraps, so every pixel it gave up was a pixel Upload / Back
                              * out upload / Refresh needed to stay on one line at
                              * laptop width.
                              *
                              * ⛔ IT STILL HAS TO HOLD A NAME. "Nashville" and "Brighton"
                              * fit comfortably at 10.5rem; the SelectValue truncates
                              * rather than pushing the trigger wider, so a longer site
                              * name can never undo the space this frees.
                              */}
                            <SelectTrigger className="w-[10.5rem]">
                                <SelectValue placeholder="Select a facility" />
                            </SelectTrigger>
                            <SelectContent>
                                {lookups.facilities.map((f) => (
                                    <SelectItem key={f.FacilityCode} value={f.FacilityCode}>
                                        {f.DisplayName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : lookupsError ? (
                        <span className="text-sm text-destructive">{lookupsError}</span>
                    ) : (
                        /* ⛔ THE SKELETON MATCHES THE TRIGGER. A placeholder wider than
                           the control it stands in for makes the toolbar jump on load. */
                        <Skeleton className="h-9 w-[10.5rem]" />
                    )}
                </div>

                <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" onClick={() => shiftDate(-1)} aria-label="Previous">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {/**
                      * ⭐ Pete, 2026-09-19: *"Lets also make the date in the search
                      * standout alittle more, maybe BOLD?"*
                      *
                      * ⭐⭐ AND IT CARRIES MORE WEIGHT THAN IT DID YESTERDAY. The Gantt
                      * header's *"Sunday, September 20, 2026"* heading was removed the
                      * same evening as redundant — so this control is now the ONLY
                      * place on the Day view that says which day is on screen.
                      * ⛔ Restoring that heading is what would make this bold redundant,
                      * not the other way round.
                      *
                      * ⚠️ `font-semibold`, not `font-bold`: at 14px over `text-tabular`
                      * numerals a full bold sets tight and reads heavier than the
                      * Today/Day/Week buttons beside it. One word changes it if that
                      * is the wrong call.
                      */}
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-3 text-sm font-semibold shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-tabular"
                    />
                    <Button variant="outline" size="icon" onClick={() => shiftDate(1)} aria-label="Next">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    {!isToday && (
                        <Button variant="ghost" size="sm" onClick={() => setDate(format(new Date(), 'yyyy-MM-dd'))}>
                            Today
                        </Button>
                    )}
                </div>

                {/* Day / Week / Month view toggle */}
                <div className="inline-flex rounded-md border border-input bg-background p-0.5">
                    {(['day', 'week', 'month'] as const).map((m) => (
                        <button
                            key={m}
                            onClick={() => setViewMode(m)}
                            className={`rounded px-3 py-1 text-sm capitalize transition-colors ${
                                viewMode === m
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {m}
                        </button>
                    ))}
                </div>

                {/**
                  * ⭐ #145 — BEFORE THE SPACER, DELIBERATELY. Everything after the
                  * spacer is right-aligned and three of those five buttons are
                  * day-view-only; a search box among them would move or vanish as the
                  * scheduler switched views. Here it sits with the date navigation,
                  * which is what it is — another way of getting to a day.
                  */}
                {facilityId && (
                    <AppointmentSearch
                        /* ⭐ INCREMENT 4 — a pinned party row in the results scopes the
                           whole board. ⛔ THE APPOINTMENTS COME FROM THE DAY ON SCREEN,
                           not from the hits: the search spans dates and the board stands
                           on one, so a focus built from other days' appointments would name
                           trucks the grid cannot draw.
                             ** A SCOPE MUST BE BUILT FROM WHAT THE SCREEN IS SHOWING. ** */
                        onScopeParty={(kind, code, name) => {
                            /**
                             * ---- ⭐⭐⭐ THE CHIP FOLLOWS THE PARTY (Pete, 2026-09-20) ----
                             *
                             * > *"Clicking the pinned FedEx row should move the chip to
                             * > Carrier."*
                             *
                             * ⛔⛔ AND HE IS DESCRIBING AN INCONSISTENCY, NOT A CONVENIENCE.
                             * §1AV.180 made the chip the thing that DECIDES WHICH SCOPES ARE
                             * EXPRESSIBLE — that is why changing it clears the scope. A
                             * carrier scope standing under the Clients chip is precisely the
                             * state that rule exists to make impossible, and this seam was
                             * the one door left open into it: the search writes the scope
                             * from outside the chip row.
                             *   ** IF CHANGING A SUBJECT CLEARS THE SCOPE, THEN SETTING A
                             *      SCOPE MUST SET THE SUBJECT. THE RULE ONLY HOLDS IN ONE
                             *      DIRECTION IF SOMEBODY BUILT ONLY ONE DIRECTION. **
                             *
                             * ⛔ ORDER IS LOAD-BEARING. `chooseSubject` CLEARS `partyScope`
                             * and `railFocus`; `chooseParty` then writes the new one. Reverse
                             * these two lines and the search scopes the board and immediately
                             * unscopes it, with nothing on screen explaining why.
                             * ⚠️ It is a no-op when the chip is already right — `chooseSubject`
                             * returns early on an unchanged subject, so the clear does not run
                             * and `chooseParty` is not racing anything.
                             *
                             * ⭐ `kind` IS ALREADY A `PanelSubject`. `'client' | 'carrier'` is a
                             * subset of the four chip words, so there is no mapping table to
                             * keep in step — the compiler checks the join.
                             */
                            chooseSubject(kind);
                            chooseParty(
                                kind,
                                code,
                                name,
                                apptsRef.current.filter(a => apptMatchesParty(a, kind, code)),
                            );
                        }}
                        boardParties={boardParties}
                        facilityCode={facilityId}
                        anchorDate={date}
                        /* ⭐ #255 — the search covers what is on screen. */
                        getToken={getToken}
                        onJump={jumpToAppointment}
                        /* ⚠️ #254 — the board is stale after a bulk status change. */
                        onBulkChanged={reload}
                    />
                )}

                {/**
                  * ---- ⛔⛤ ~~THE `GEODIS (carrier) ×` PILL~~ — REPLACED BY THE `R` -------
                  *
                  * ⭐ Pete: *"GEODIS next to the search bar is not working. Doesn't really
                  * pop telling us what client is selected and the Refresh button not moved
                  * to the next line. Lets remove it for now"*.
                  *
                  * ⚠️ TWO SEPARATE FAULTS IN ONE SENTENCE, AND THE SECOND IS THE REAL ONE:
                  * the toolbar WRAPS (`flex-wrap`), so adding a variable-width pill pushed
                  * Refresh onto a second line. ** A ROW THAT WRAPS HAS NO SPARE WIDTH — IT
                  * HAS A COST PER CHARACTER, PAID BY WHATEVER IS LAST. **
                  *
                  * ⛔⛔ BUT THE SCOPE STILL OUTLIVES THE DAY VIEW, so it still needs an exit
                  * HERE. §1AV.171: *"a control scoped to one view cannot own state that
                  * outlives that view"* — `CheckInPanel`'s `R` is Day-only, and a scope set
                  * on Day survives into Week and Month.
                  * ✅ Pete's call: the SAME `R` glyph, same pulse, in the toolbar too. One
                  * control in two places beats two controls that must be learned separately
                  * — and a fixed-width glyph costs the wrapping row nothing measurable.
                  */}
                {/**
                  * ---- ⛔⚤ REMOVED FROM **DAY** ONLY (Pete, 2026-09-20) ---------------
                  *
                  * ⭐ Pete: *"don't think we need the circle R next to the search button.
                  * it makes the screen render incorrectly, wrapping the buttons."*
                  *
                  * ⚠️ THAT IS A LAYOUT COMPLAINT, AND DELETING THE CONTROL OUTRIGHT WOULD
                  * ANSWER IT WITH A TRAP. The note above records why this exists at all, and
                  * the reason has not changed: `CheckInPanel` — which carries the other Ⓡ —
                  * **renders only on Day**, and a party scope set on Day SURVIVES into Week
                  * and Month. Remove it everywhere and a scheduler who switches to Week is
                  * looking at a filtered board with no way out and nothing saying it is
                  * filtered. §1AV.171: *"a control scoped to one view cannot own state that
                  * outlives that view."*
                  *
                  * ⭐⭐ SO IT GOES WHERE IT WAS REDUNDANT AND STAYS WHERE IT IS THE ONLY EXIT.
                  * On Day the panel's Ⓡ does the same job three inches away and this one was
                  * costing the wrapping row its width; on Week and Month it is the only Ⓡ on
                  * screen.
                  *   ** A CONTROL THAT IS REDUNDANT ON ONE SCREEN AND THE ONLY WAY OUT ON
                  *      ANOTHER IS TWO CONTROLS. REMOVE THE ONE THAT IS REDUNDANT. **
                  *
                  * ⚠️ IF `CheckInPanel` EVER RENDERS OUTSIDE DAY, this condition is wrong
                  * and there will be two Ⓡ glyphs on one screen. That is the loud failure,
                  * deliberately, rather than a silent trap.
                  */}
                {/* ⚠️ `|| warningScope` ADDED 2026-09-23, and the note above is exactly
                    why: a warning filter is set from the Day rail and SURVIVES into Week
                    and Month, where `CheckInPanel` — and its Ⓡ — does not render. Without
                    this the board is filtered with nothing saying so and no way out.
                    ⛔ `clearParty` CLEARS BOTH, so one control still means one thing. */}
                {/**
                  * ---- ⛔⤴ ~~THE TOOLBAR Ⓡ~~ — RETIRED 2026-09-27 (§0.3) ---------------
                  *
                  * > Pete: **"One Ⓡ in the board header, retire both."**
                  *
                  * It read, verbatim:
                  *     ~~{(partyScope || warningScope) && viewMode !== 'day' && (~~
                  *     ~~    <button onClick={clearParty} … >R</button>~~
                  *     ~~)}~~
                  *
                  * ⛔⛔ AND IT WAS THE WEAKER OF THE TWO, which is the real reason it goes
                  * rather than just being moved: `clearParty` leaves `railFocus` LATCHED,
                  * so pressing Ⓡ in Week or Month cleared the board and left the five
                  * rails scoped to whatever load was last clicked. The Day Ⓡ cleared all
                  * three. Two buttons, one letter, two behaviours.
                  *
                  * ⚠️ `clearParty` ITSELF SURVIVES — the party cards and the toolbar pill
                  * still call it, and it is correct for them: choosing a different party
                  * is not a reset. Only this button is gone.
                  *
                  * ⚠️ AND THE `viewMode !== 'day'` GUARD GOES WITH IT. Its own note warned
                  * *"IF `CheckInPanel` EVER RENDERS OUTSIDE DAY … there will be two Ⓡ
                  * glyphs on one screen"* — that hazard is now structurally impossible,
                  * because there is one Ⓡ and the board header is the only thing that
                  * renders it.
                  */}

                <div className="flex-1" />

                <Button variant="default" size="sm" onClick={handleNewClick} disabled={!lookups || facilityId === null}>
                    <Plus className="h-3.5 w-3.5" />
                    New appointment
                </Button>

                {/* ⭐ Mass create (#66) — "20 trucks today, no times". Beside New
                    appointment rather than buried in a menu: it is the same
                    decision ("book something in") reached by a different route, and
                    a scheduler on the phone to a client should not have to hunt.
                    ⚠️ DAY VIEW ONLY. The batch lands on ONE day, and offering it
                    from a week or month grid invites a guess about which. */}
                {viewMode === 'day' && (
                    <Button
                        variant="outline" size="sm" onClick={() => setMassOpen(true)}
                        disabled={!lookups || facilityId === null}
                        title="Create several appointments for one client on this day, with no times and no doors"
                    >
                        <Layers className="h-3.5 w-3.5" />
                        Mass create
                    </Button>
                )}

                {/* ⭐ Upload (#159) — the same decision as Mass create, reached from
                    a client's spreadsheet instead of a form. Beside it for exactly
                    that reason.
                    ⚠️ DAY VIEW ONLY, same as Mass create: the screen supplies the
                    date for any row that left the Date column blank, and offering
                    that from a month grid invites a guess about which day.
                    ⛔ It does NOT need `lookups` — an uploaded row carries no client
                    or carrier picker, so gating on the lookup lists would disable a
                    button for a dependency it does not have. */}
                {viewMode === 'day' && (
                    <Button
                        variant="outline" size="sm" onClick={() => setUploadOpen(true)}
                        disabled={facilityId === null}
                        title="Create appointments from a client-supplied spreadsheet"
                    >
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                        Upload
                    </Button>
                )}

                {/* ---- ⭐ BACK OUT THE DAY'S UPLOAD (#139) ----
                     ⛔ SHOWN ONLY WHEN THERE IS ONE TO BACK OUT, and only on the day
                     view — the same reasoning as Upload above: a back-out from a month
                     grid invites a guess about which day.
                     ⚠️ `destructive` styling is deliberate. This DELETES; every other
                     undo in the app cancels. */}
                {viewMode === 'day' && backoutFor && (
                    <Button
                        variant="outline" size="sm"
                        onClick={() => { setBackoutResult(null); setBackoutConfirm(true); }}
                        disabled={facilityId === null || backoutBusy}
                        title={`Remove the appointments created by ${backoutFor.label}`}
                        className="border-destructive/40 text-destructive hover:bg-destructive/10"
                    >
                        {backoutBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        <Undo2 className="h-3.5 w-3.5" />
                        Back out upload
                    </Button>
                )}

                <Button
                    variant="outline" size="sm" disabled={calendarLoading}
                    /**
                      * ---- ⭐⭐⭐ THIS IS THE **SCREEN** REFRESH (Pete, 2026-09-27) -----
                      *
                      * > *"the Refresh button right below the sprocket should do a screen
                      * > refresh because individual refreshes on the panels will allow for
                      * > individual refresh there. So I think it refreshes the panels (like
                      * > it does now), refreshes the driver, clients, carrier and
                      * > consignees and the calender."*
                      *
                      * ⭐ THE DIVISION OF LABOUR IS HIS FIRST CLAUSE: every panel carries
                      * its own ↻ for "re-read just this", so THIS button is free to mean
                      * "re-read everything" rather than being a fourth way to do one panel.
                      *
                      * ⭐ THE PARTY ROWS COME FREE and are not a third call. Drivers,
                      * clients, carriers and consignees are `partyRows(...)` computed from
                      * the board's appointments plus the arrivals list — both of which the
                      * two calls below already refetch. ⛔ Adding a fetch for them would
                      * have been inventing work to satisfy a sentence.
                      *
                      * ⚠️ `reload()` ALONE LEFT THE RAIL STALE — the original note, kept:
                      * the arrivals panel keeps its own 30s poll and its own state, so a
                      * scheduler pressed Refresh, saw no new driver, and reasonably
                      * concluded the check-in had not landed.
                      *
                      * ⛔⛔ AND `paperworkKey` IS BUMPED HERE, WHICH IS NOT A CONTRADICTION
                      * OF ITS OWN DOCSTRING. That note forbids the REVERSE — bumping
                      * `refreshKey` to refresh the trays, which would restart the arrivals
                      * poll and make the driver list jump every time a document was filed.
                      * Bumping the paperwork key from the toolbar has no such effect.
                      *   ** A WARNING ABOUT ONE DIRECTION OF A COUPLING IS NOT A BAN ON
                      *      THE OTHER. **
                      */
                    onClick={() => {
                        setRefreshKey(k => k + 1);
                        setPaperworkKey(k => k + 1);
                        void reload();
                    }}
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${calendarLoading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>

                {/* Settings gear moved to global secondary nav (App.tsx).
                    Calendar listens for the 'dockscheduler:open-settings'
                    event and opens its dialog — see useEffect above. */}
            </div>


            {/* ---- Main pane: the grid, and the arrivals panel beside it ----
                 ⚠️ The panel only shows in DAY view. Arrival order and truck
                 numbers are facts about ONE day at ONE site; showing them beside
                 a week or month grid would invite reading them across days,
                 which they do not mean. */}
            <div className="flex-1 min-h-0 flex gap-4">
              <div className="flex-1 min-w-0">
                {calendarError ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8">
                        <AlertCircle className="h-10 w-10 text-destructive mb-3" />
                        <h3 className="text-lg font-semibold">Couldn't load calendar</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md">{calendarError}</p>
                        <Button onClick={reload} className="mt-4">Try again</Button>
                    </div>
                ) : facilityId === null ? (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                        Select a facility
                    </div>
                ) : viewMode === 'day' ? (
                    calendarLoading && !calendar ? (
                        <div className="h-full flex items-center justify-center">
                            <Skeleton className="h-full w-full" />
                        </div>
                    ) : calendar ? (
                        /**
                          * ---- ✅ DECISION 184: THE DAY FILTER LIVES IN THE GANTT HEADER ----
                          *
                          * ⛔⛔ `appointments` IS FILTERED; `statusCounts` / `totalCount` ARE
                          * NOT — and they are passed side by side on purpose. The header row
                          * this replaces counted `appointments.filter(...)`, i.e. the filtered
                          * set, so filtering to "Approved" drove every other count to 0.
                          *
                          *   ** A COUNT COMPUTED FROM WHAT SURVIVED THE FILTER CANNOT TELL YOU
                          *      WHAT THE FILTER IS HIDING, WHICH IS THE ONLY QUESTION IT WAS ON
                          *      SCREEN TO ANSWER. **
                          *
                          * ⭐ The `statusCounts` useMemo above reads the UNFILTERED source.
                          *
                          * ⛔⛤ AND THIS COMMENT IS A PLAIN `/* *\/` BLOCK, NOT `{/* *\/}`, BECAUSE THIS
                          * IS A TERNARY BRANCH — AN EXPRESSION POSITION, NOT CHILDREN. The first draft
                          * used the JSX form and `tsc` answered "')' expected" three lines later.
                          *
                          *   ** THE SAME MISTAKE AS THE DAY BEFORE, IN A NEW POSITION: I CHECKED THE
                          *      RULE I HAD JUST LEARNED INSTEAD OF THE CODE I WAS EDITING. "Children
                          *      position" was a label applied, not a property verified. **
                          */
                        <GanttCalendar
                            facilityCode={facilityId}
                            date={date}
                            doors={calendar.doors}
                            appointments={filterAppts(calendar.appointments)}
                            statusFilter={statusFilter}
                            toggleStatus={toggleStatus}
                            setStatusFilter={setStatusFilter}
                            statusCounts={statusCounts}
                            totalCount={totalCount}
                            /* ⛔⛔ THE WORK ROW GOES TO THE GANTT TOO, AND THAT IS NOT
                               OPTIONAL. Day renders its own header inside GanttCalendar;
                               Week and Month use `boardHeader` above. A control that exists
                               on two views out of three leaves a Day user filtered by
                               something with no control on screen to turn off — the same
                               fault `hideStatuses`' `!checked` guard exists to prevent one
                               level down.
                               ⚠️ AND THIS IS A PLAIN `/* *\/` BLOCK, NOT `{/* *\/}`: inside
                               an opening tag this is ATTRIBUTE position, not children. The
                               file already records the same mistake made in a ternary
                               branch a few hundred lines down.
                                 ** A COMMENT SYNTAX IS CHOSEN BY POSITION, AND "INSIDE JSX"
                                    IS NOT ONE POSITION. ** */
                            workFilter={workFilter}
                            toggleWork={toggleWork}
                            workCounts={workCounts}
                            /* ⛔⛔ AND SO DOES THE WMS ROW, FOR THE IDENTICAL REASON
                               STATED DIRECTLY ABOUT THE WORK ROW: Day draws its own
                               header inside GanttCalendar. A control on two views out
                               of three leaves a Day user filtered to NO WMS with
                               nothing on screen to turn it off. ⭐ The props are
                               REQUIRED on the component, so the compiler enforces
                               this rather than a comment. */
                            wmsFilter={wmsFilter}
                            toggleWms={toggleWms}
                            wmsCounts={wmsCounts}
                            /* ⭐ THE SAME ELEMENT Week and Month get — see `resetScopeButton`.
                               ⛔ A NODE, NOT A FLAG AND A HANDLER: passing `onReset` plus
                               `showReset` would let the Gantt draw its own R, and the pair
                               would drift the way the last pair did. */
                            resetButton={resetScopeButton}
                            headerAlert={possibleNoShowsChip}
                            capacityBands={calendar.capacityBands}
                            operatingWindow={calendar.operatingWindow}
                            noShowGraceMinutes={calendar.noShowGraceMinutes ?? 0}
                            isToday={isToday}
                            onSelectAppointment={handleGridChipOpen}
                            onCreateInSlot={handleCreateInSlot}
                            onMoveAppointment={handleMoveAppointment}
                            onRequestDoorMenu={handleRequestDoorMenu}
                            onRequestContextMenu={handleRequestContextMenu}
                            onRequestLimitMenu={handleRequestLimitMenu}
                            onOpenLimits={handleOpenLimits}
                            onEditLimitTimes={handleEditLimitTimes}
                            onRequestDedicationMenu={handleRequestDedicationMenu}
                            onOpenDedications={handleOpenDedications}
                            onEditDedicationTimes={handleEditDedicationTimes}
                            onEditDoor={isAdmin ? handleEditDoor : undefined}
                            onDropCheckInOnAppointment={handleDropCheckInOnAppointment}
                            onDropCheckInInSlot={handleDropCheckInInSlot}
                            onDropPaperworkOnAppointment={(ev) => void handleDropPaperwork(
                                ev.payload,
                                { appointmentId: String(ev.appt.appointmentId) },
                                ev.appt.appointmentNumber,
                            )}
                            /* ⭐ A5 — an order card dropped onto a load. */
                            onDropWmsOrderOnAppointment={(ev) => void handleDropWmsOrder(ev)}
                            /**
                             * ⚠️ HOVER WINS WHILE THE CURSOR IS ON A CHIP, and the search
                             * focus holds the rest of the time. ⛔ Not merged into one
                             * state: hovering must not silently discard the result the
                             * scheduler navigated to.
                             *
                             * ⛔ THE ORDER IS A PRECEDENCE CLAIM, NOT A LIST. The cursor
                             * wins while it is on a chip; when it LEAVES, the latched rail
                             * scope takes over — that is the whole of Pete's 2026-09-19
                             * ask — and the search result holds only when neither is live.
                             * The rail scope sits ahead of `focusApptId` because dwelling
                             * on a chip is a later deliberate act than the search that
                             * brought the scheduler here.
                             *
                             * ⚠️ A `{​/* *​/}` COMMENT CANNOT GO HERE. This is an attribute
                             * list, not a child list, and a comment container in it is a
                             * PARSE error (TS1005) — caught by `tsc`, and by nothing else.
                             * That is why every note in this list is a plain block comment.
                             */
                            /**
                             * ⭐⭐ TWO PROPS NOW, NOT ONE CHAIN (decision 190).
                             * ⛔ `highlightAppointmentId` GOES BACK TO MEANING WHAT ITS
                             * NAME SAYS — the chip under the cursor, nothing else. The
                             * old `hoverApptId ?? latchedApptId ?? focusApptId` was a
                             * one-ring design in which the hover REPLACED the selection;
                             * Pete asked to see both at once.
                             */
                            highlightAppointmentId={hoverApptId}
                            selectedAppointmentIds={selectedApptIds}
                            previewAppointmentIds={previewApptIds}
                            /* ⭐ A SINGLE CLICK SCOPES. `showAttachedFor` already does the
                               whole job — sets the focus, points the paperwork tray at the
                               load and switches the tray view — so the click reuses it
                               rather than growing a second, drifting copy. */
                            onScopeAppointment={handleGridChipClick}
                            onHoverAppointment={handleHoverAppointment}
                            /**
                              * ⭐⭐ THE PAPERWORK TRAY FOLLOWS THE DWELL, NOT THE CURSOR.
                              * ⛔ Fired from inside the hover card's OWN timer, so a fast
                              * sweep across 23 doors costs nothing — Pete, 2026-09-10:
                              * *"if the user is going really fast over appointments, it
                              * doesnt fire?"*
                              */
                            /* ⛔⚤ ~~onHoverCardShown={showAttachedFor}~~ — REMOVED
                               2026-09-20, decision 190. This was the hover LATCH: the grid's
                               dwell timer called the same handler a click now calls, so
                               sweeping across the board re-scoped five panels and the
                               calendar. ⭐ Hover still outlines the chip
                               (`onHoverAppointment` below); it no longer decides anything.
                               ** A TRANSIENT GESTURE MUST NOT SET A LATCHED STATE. **
                               ⚠️ The paperwork tray moved with it (§1AN amended): it follows
                               the click now, which is what §1AN's own objection — *"when you
                               click on the appointment, it opens the popup so annoying"* —
                               stopped applying to once the popup moved to double-click. */
                            // A refused drop says why. Silence here is what made
                            // the gesture feel broken rather than disallowed.
                            onDropRefused={(reason) => setToast({ kind: 'error', msg: reason })}
                            claimedAppointmentIds={claimedApptIds}
                            slotMinutes={calendar.slotMinutes}
                            fetchEvents={fetchEvents}
                        />
                    ) : (
                        <div className="h-full flex items-center justify-center">
                            <Skeleton className="h-full w-full" />
                        </div>
                    )
                ) : calendarLoading && !rangeData ? (
                    <div className="h-full flex items-center justify-center">
                        <Skeleton className="h-full w-full" />
                    </div>
                ) : rangeData ? (
                    viewMode === 'week' ? (
                        <WeekBoard
                            header={boardHeader}
                            weekStart={weekStart}
                            /* ⭐ The door order is keyed per facility and shared with the
                               Gantt. ✅ `facilityId` is narrowed to `string` by the
                               `facilityId === null` branch above -- the same narrowing the
                               GanttCalendar call site relies on, so no `?? ''` fallback that
                               would quietly write everyone's order under one blank key. */
                            facilityCode={facilityId}
                            doors={rangeData.doors ?? []}
                            capBands={rangeData.capBands ?? []}
                            appointments={filterAppts(rangeData.appointments)}
                            onSelectAppointment={setSelectedAppt}
                            /* ⭐ Pete, 2026-09-27 — see `openEditFromBoard`, which also
                               closes the detail dialog the preceding single click opened. */
                            onEditAppointment={openEditFromBoard}
                            onMoveAppointment={handleMoveAppointment}
                            onRequestContextMenu={handleRequestContextMenu}
                            onOpenDay={openDay}
                            todayStr={todayStr}
                            fetchEvents={fetchEvents}
                        />
                    ) : (
                        <MonthGrid
                            header={boardHeader}
                            monthAnchor={date}
                            appointments={filterAppts(rangeData.appointments)}
                            capBands={rangeData.capBands ?? []}
                            onSelectAppointment={setSelectedAppt}
                            /* ⭐ Pete, 2026-09-27 — same gesture, same handler as Week. */
                            onEditAppointment={openEditFromBoard}
                            onMoveAppointment={handleMoveAppointment}
                            onRequestContextMenu={handleRequestContextMenu}
                            onOpenDay={openDay}
                            todayStr={todayStr}
                            fetchEvents={fetchEvents}
                        />
                    )
                ) : (
                    <div className="h-full flex items-center justify-center">
                        <Skeleton className="h-full w-full" />
                    </div>
                )}
              </div>

              {viewMode === 'day' && facilityId !== null && calendar && (
                  <CheckInPanel
                      subject={subject}
                      onSubject={chooseSubject}
                      /* ⭐ Decision 188 increment 3 — the party cards and what is chosen. */
                      partyScope={partyScope}
                      onChooseParty={chooseParty}
                      onClearParty={clearParty}
                      onOpenPartyRecord={openPartyRecord}
                      facilityCode={facilityId}
                      date={date}
                      /* ⛔ THE WHOLE DAY, DELIBERATELY — this is the attach picker's
                         source and `apptById`'s, not the panel's view. The view is
                         `boardApptIds` below. See its declaration for what narrowing
                         this one costs. */
                      appointments={calendar.appointments}
                      /* ⭐ Pete 2026-09-25: the rail follows the board. */
                      boardApptIds={boardApptIds}
                      boardFiltered={boardFiltered}
                      getToken={getToken}
                      onChanged={reload}
                      onCreateFromCheckIn={handleCreateFromCheckIn}
                      /* ⭐ D3 / Decision 238 — Match on an unmatched order opens the picker. */
                      onMatchOrder={(key, row) => { setMatchRow(row); setMatchFor(key); }}
                      /**
                       * ---- ⭐⭐ THE WMS ORDERS PANEL'S SCOPE (#333, §1Z.1) --------
                       *
                       * ⭐ THE RANGE ALREADY ON SCREEN. `rangeBounds` is the very
                       * arithmetic the week/month fetch uses — including the month
                       * view's SIX-WEEK GRID, so the panel cannot show less than the
                       * page is showing (§1Z.1's own example of how re-deriving goes
                       * wrong).
                       *
                       * ⛔ NOT RE-COMPUTED IN THE PANEL. That maths is duplicated once
                       * already (the policy layer may import nothing, §1O) and pinned
                       * by `tools/search-scope-guard.cjs`; a third copy would need
                       * pinning too.
                       *
                       * ⚠️ IN DAY VIEW `rangeBounds` IS NOT USED BY THE FETCH, so the
                       * day is passed explicitly rather than read from it — a bound
                       * that is stale in one view is exactly the silent mismatch this
                       * whole section exists to prevent.
                       */
                      ordersScope={{
                          fromDate: viewMode === 'day' ? date : rangeBounds.start,
                          toDate: viewMode === 'day' ? date : rangeBounds.end,
                          /**
                           * ---- ⛔⛔ IT DESCRIBES THE **RANGE**, NOT THE VIEW MODE ---
                           *
                           * ⚠️ ~~`viewMode === 'day' ? 'today' : ...`~~ WAS WRONG AND
                           * IT COST AN AFTERNOON. It said "today" for WHICHEVER day
                           * the calendar was on. Pete paged to Sep 8, the panel
                           * filtered Sep 8 correctly and announced *"Showing today"*,
                           * and 200+ Sep-8 orders read as an impossible Tuesday:
                           * *"I find it hard to believe there are 200+ planned shipped
                           * or arrival dates for Brighton today."* He was right. It was
                           * not today.
                           *
                           * ⛔ AND IT BREAKS THE EXACT OBLIGATION THE LABEL EXISTS FOR.
                           * §1Z.2: narrowing a default makes "nothing found" more
                           * common, and the price is a TRUE scope sentence on every
                           * result. A FALSE one is worse than none — it converts a
                           * correct list into evidence that the panel is broken.
                           *
                           * ⭐ DERIVED FROM THE SAME `date`/`rangeBounds` THE FETCH
                           * USES. A label computed from a different input than the
                           * filter is how the two drift apart again.
                           */
                          scopeLabel: describeOrdersScope(
                              viewMode === 'day' ? date : rangeBounds.start,
                              viewMode === 'day' ? date : rangeBounds.end,
                          ),
                      }}
                      /**
                       * ---- ⭐⭐ THE APPOINTMENT MENU, ON THE DRIVER CARD (#341) ----
                       *
                       * > Pete, 2026-09-09: *"Why can't you give me a dropdown too that
                       * > has the same things as when you right click an
                       * > appointment??????? So i have all options????"*
                       *
                       * ⭐ THE SAME FUNCTION AND THE SAME HANDLERS the grid's right-click
                       * uses. Not a copy — literally `contextMenuItemsFor` with the
                       * identical closures, so the card can never offer less than the
                       * chip does.
                       *
                       * ⚠️ IT LOOKS THE APPOINTMENT UP BY ID from the day already on
                       * screen. ⛔ Returns an EMPTY LIST when the id is not in view — an
                       * arrival can be matched to a load on another DATE, and inventing a
                       * menu for an appointment this page has not loaded would produce
                       * entries whose handlers act on `undefined`.
                       */
                      appointmentMenuItems={(appointmentId) => {
                          const appt = calendar.appointments.find(
                              a => String(a.appointmentId) === appointmentId);
                          if (!appt) return [];
                          return contextMenuItemsFor(appt, {
                              onDetails: () => setSelectedAppt(appt),
                              onApprove: () => doQuickAction(appt, 'approve'),
                              onReject: () => doQuickAction(appt, 'reject'),
                              onArrive: () => doQuickAction(appt, 'arrive'),
                              onDepart: () => doQuickAction(appt, 'depart'),
                              onNoShow: () => doQuickAction(appt, 'noshow'),
                              onCancel: () => doQuickAction(appt, 'cancel'),
                              onSendDirections: () => setSendDirFacility({
                                  code: appt.facilityCode,
                                  drivers: appt.drivers.map(d => ({ name: d.name, phone: d.phone })),
                                  appointmentId: appt.appointmentId,
                              }),
                              /* ⭐ #324 — OUTBOUND ONLY, absent elsewhere. */
                              onSendConsigneePin: appt.direction === 'OUTBOUND'
                                  ? () => setPinAppt({
                                      appointmentId: String(appt.appointmentId),
                                      drivers: appt.drivers.map(d => ({ name: d.name, phone: d.phone })),
                                  })
                                  : undefined,
                              onTextDoor: () => { setDoorTextKind('door'); setDoorTextFor(String(appt.appointmentId)); },
                              onTextPaperwork: () => { setDoorTextKind('paperwork'); setDoorTextFor(String(appt.appointmentId)); },
                              onTextWork: () => { setDoorTextKind('work'); setDoorTextFor(String(appt.appointmentId)); },
                              /* ⭐ #306 — ADMIN ONLY, absent for everyone else. */
                              onOverrideStatus: isAdmin ? () => setOverrideAppt(appt) : undefined,
                              onPaperwork: () => setDocsFor(scopeOfAppointment(appt)),
                              onSignPaperwork: () => void handleSignPaperwork(scopeOfAppointment(appt)),
                              onPrintPaperwork: () => void handlePrintPaperwork(scopeOfAppointment(appt)),
                              onEditDriver: () => setDriverAssignAppt(appt),
                          });
                      }}
                      /**
                       * ⭐⭐ ATTACH A NEW FILE STRAIGHT ONTO AN ARRIVAL (2026-09-08).
                       *
                       * > Pete: *"lets add a quick button to the driver card ... that
                       * > does the same thing as 'Drop files Here, or Browse'."*
                       *
                       * ⛔ IT LIVES HERE, NOT IN THE PANEL, for the same reason
                       * `onDropPaperworkOnCheckIn` does: this page owns where a
                       * document lands. ⚠️ And it is TWO acts — `uploadDocument` puts
                       * the bytes in the tray, `fileDocument` puts them on the truck.
                       * A screen that did only the first would leave the scheduler
                       * looking at a card that still says nothing is filed.
                       *
                       * ⚠️ DIFFERENT FROM `handleDropPaperwork`, which takes a document
                       * that ALREADY EXISTS — dragged from the tray, or ingested from
                       * SharePoint. This one starts from a `File` on a disk.
                       *
                       * ⛔ NO `sourceRef` HERE, DELIBERATELY. That idempotency key
                       * belongs to the scan folder, where the same file is re-read on
                       * every poll and a double click would otherwise file a second
                       * copy of one bill of lading. A hand-picked file has no such
                       * loop behind it, and inventing a key from the filename would
                       * silently swallow a scheduler's deliberate second copy.
                       */
                      uploadToCheckIn={async (c, token, file) => {
                          if (facilityId === null) throw new Error('No facility is selected.');
                          const res = await uploadDocument(token, facilityId, file, { source: 'Upload' });
                          await fileDocument(token, res.document.documentId, { checkInId: c.checkInId }, null);
                      }}
                      // The same dialog the grid's right-click opens.
                      onTextDoor={(c) => { if (c.appointmentId) { setDoorTextKind('door'); setDoorTextFor(c.appointmentId); } }}
                      onTextPaperwork={(c) => { if (c.appointmentId) { setDoorTextKind('paperwork'); setDoorTextFor(c.appointmentId); } }}
                      onTextWork={(c) => { if (c.appointmentId) { setDoorTextKind('work'); setDoorTextFor(c.appointmentId); } }}
                      /**
                       * ⭐ THE SAME DIALOG THE APPOINTMENT'S MENU OPENS (#144), not a
                       * driver-flavoured copy of it. Documents hang off the
                       * APPOINTMENT (migration 145), so "this driver's paperwork" and
                       * "this load's paperwork" are the same set of rows — two views
                       * of it would eventually disagree about what is on file.
                       * ⚠️ The appointment is looked up from the day's list for its
                       * number and carrier; a matched arrival always has one.
                       */
                      /**
                       * ⭐ THE SAME SCOPE THE SIGN AND PRINT SHORTCUTS USE (#245a), so
                       * the list a fallback opens is always the list the shortcut
                       * just read. Two ways of naming the same target is how they
                       * come to disagree.
                       */
                      onPaperwork={(c) => setDocsFor(scopeOfCheckIn(c))}
                      /**
                       * ⛔ MARK DEPARTED GOES THROUGH `runQuickAction`, THE SAME PATH
                       * THE APPOINTMENT'S OWN BUTTON USES (#141).
                       *
                       * That is the whole point of routing it this way rather than
                       * calling `markDeparted` directly: `runQuickAction` carries the
                       * OUTBOUND RELEASE GATE (#97) — a 409 opens the release dialog
                       * so a named person signs off the warnings — plus the reload and
                       * the opt-in carrier notification. A driver card that departed a
                       * truck by any other route would be a documented way around a
                       * check Symbia relies on to stop a fraudulent pickup.
                       */
                      onMarkDeparted={(c) => {
                          const appt = calendar.appointments.find(
                              a => String(a.appointmentId) === c.appointmentId);
                          // ⚠️ Only today's loaded day is in `calendar.appointments`.
                          // Say so rather than failing silently on a stale card.
                          if (!appt) {
                              setToast({ kind: 'error', msg: "Couldn't find that appointment on this day — press Refresh." });
                              return;
                          }
                          void runQuickAction(appt, 'depart');
                      }}
                      // Matched from the panel's own Attach menu — same offer.
                      onAttached={(appointmentId) => { setDoorTextKind('door'); setDoorTextFor(appointmentId); }}
                      /**
                       * ---- ⭐⭐⭐ THE SAME HANDLER THE GRID'S DROP USES ---------
                        *
                       * ⛔ THE PANEL'S Attach MENU WAS THE WEAKER OF TWO PATHS: no
                       * answers, neither 409 handled, a raw error string where the
                       * drag offered two prompts. Same rule as
                       * `appointmentMenuItems` above — *"the card can never offer
                       * less than the chip does"* — applied to the one action it
                       * had not covered.
                        *
                       * ⚠️ `onAttached` STAYS and is now the FALLBACK path's door
                       * text only; the delegated path gets its door text from the
                       * handler itself, after the variance and hint dialog.
                       */
                      onAttachRequest={handleDropCheckInOnAppointment}
                      interactingRef={interactingRef}
                      refreshKey={refreshKey}
                      onListChanged={(rows) => {
                          checkInsById.current = new Map(rows.map(r => [r.checkInId, r]));
                          // ⛔ Which appointments already have a truck on them, so the
                          // grid can refuse a second. State, not a ref: the grid has to
                          // re-render when this changes or a just-attached appointment
                          // would still look droppable.
                          setClaimedApptIds(new Set(
                              rows.map(r => r.appointmentId).filter((v): v is string => v !== null),
                          ));
                      }}
                      /* ⛔ THE SAME LATCH AS THE GRID. Pete, 2026-09-10: *"Hovering either
                         outlines both"* — the arrival card and the chip are two views of
                         one appointment, so a latch on one and a transient on the other
                         puts them in disagreement the moment the cursor moves away.
                         ** A DEFECT REPORTED ON ONE VIEW OF SHARED DATA IS A DEFECT ON
                            EVERY VIEW OF IT. ** */
                      highlightAppointmentId={hoverApptId}
                      selectedAppointmentIds={selectedApptIds}
                      onHoverAppointment={handleHoverAppointment}
                      /* ⛔⚤ ~~onHoverCardShown={showAttachedFor}~~ — REMOVED 2026-09-20,
                         decision 190, for the same reason as the grid. The arrival cards'
                         dwell latched the scope; a click sets it now. */
                      /* ⭐ Ⓡ lives on the board header now; this is how its click still
                         collapses the arrival cards. See `resetAllScopes`. */
                      collapseSignal={collapseKey}
                      onChooseRecord={showAttachedFor}
                      /* ⭐ Raised from the driver cards, handed back down for the
                         scoped panels this component renders. */
                      /**
                        * ⭐⭐ `railFocus ?? partyScope` — THE RAIL FOLLOWS THE NARROWER OF
                        * THE TWO. Choosing Hellman scopes all four panels; then pointing
                        * at one of his trucks narrows them to that load; then clearing
                        * the load falls back to Hellman rather than to everything.
                        * ⛔ NOT `partyScope ?? railFocus` — that would make a chosen party
                        * permanently outrank pointing at a truck, which is the opposite
                        * of what a scheduler means by pointing at it.
                        */
                      railFocus={railFocus ?? partyScope}
                      previewFocus={previewFocus}
                      onHoverParty={setHoverPartyFocus}
                      warningScopeKey={warningScope?.key ?? null}
                      onChooseWarningScope={chooseWarningScope}
                      partyNames={partyNames}
                      onClearRailFocus={clearRailFocus}
                      onDropRefused={(reason) => setToast({ kind: 'error', msg: reason })}
                      /**
                       * ⭐ PAPERWORK ONTO A DRIVER (#150) — the walk-in path. He has no
                       * chip on the grid, so his card is the only place it can land.
                       */
                      /**
                       * ⛔ THE SUGGESTION IS RENDERED BY THE PANEL, NOT HERE, because
                       * accepting it must go through the panel's own `doAttach` — the
                       * same path a manual match uses, carrying the substitute-driver
                       * question and the audit line with it. A second way to attach a
                       * truck to a load would be a second set of rules about it.
                       */
                      suggestion={suggested}
                      onSuggestionClose={() => setSuggested(null)}
                      onSuggestionView={() => {
                          if (!suggested) return;
                          void (async () => {
                              try {
                                  const { url } = await getDocumentUrl(await getToken(), suggested.documentId);
                                  window.open(url, '_blank', 'noopener');
                              } catch { /* the row is still in the paperwork list */ }
                          })();
                      }}
                      onSign={(c) => void handleSignPaperwork(scopeOfCheckIn(c))}
                      onPrint={(c) => void handlePrintPaperwork(scopeOfCheckIn(c))}
                      onVoidedPaperwork={(v) => setDocsFor({
                          appointmentId: null,
                          checkInId: v.checkInId,
                          appointmentNumber: v.driverName
                              ? `${v.driverName} · voided arrival`
                              : 'voided arrival',
                          carrierCode: v.carrierCode ?? null,
                      })}
                      onDropPaperworkOnCheckIn={(ev) => void handleDropPaperwork(
                          ev.payload,
                          { checkInId: ev.checkIn.checkInId },
                          ev.checkIn.driverName
                              ? `${ev.checkIn.driverName}'s arrival`
                              : `arrival #${ev.checkIn.arrivalSeq ?? '?'}`,
                      )}
                      footer={
                          <>
                              {/**
                                * ⭐ THE TRAY, DIRECTLY UNDER THE DRIVER CARDS (#150).
                                * Pete, 2026-08-25, on the bottom strip: *"you start to
                                * lose view of the dock doors and all the sites have a
                                * lot of dock doors."* Vertical space is the scarce thing
                                * on a 23-door grid; this column already scrolls.
                                *
                                * ⛔ EXACTLY ONE OF THESE TWO EVER RENDERS. The tray
                                * returns null on a local-folder site and ScanFolderPanel
                                * returns null on a SharePoint one — migration 162's
                                * "only one source is ever consulted", carried up to the
                                * UI.
                                */}
                              <PaperworkSliver
                                  /* ⭐ Item 15 — the header speaks the rails' sentence.
                                     ⛔ BOTH TRAYS TAKE IT: exactly one renders per site. */
                                  focus={paperworkFocus}
                                  facilityCode={facilityId}
                                  getToken={getToken}
                                  refreshKey={paperworkKey}
                                  busy={paperworkBusy}
                                  attached={attachedPaperwork}
                                  trayView={trayView}
                                  onTrayView={onTrayView}
                                  onDropAttached={handleDropOnAttached}
                                  onDropUnfile={handleDropUnfile}
                                  onDropLocal={handleDropLocal}
                                  sourceView={sourceView ?? 'SharePoint'}
                              />
                              <ScanFolderPanel
                                  /* ⭐ Item 15 — see the sliver above. */
                                  focus={paperworkFocus}
                                  facilityCode={facilityId}
                                  appointments={calendar.appointments}
                                  getToken={getToken}
                                  onFiled={reload}
                                  attached={attachedPaperwork}
                                  trayView={trayView}
                                  onTrayView={onTrayView}
                                  onDropAttached={handleDropOnAttached}
                                  onDropUnfile={handleDropUnfile}
                                  onDropLocal={handleDropLocal}
                                  localPick={localPick}
                                  sourceView={sourceView ?? 'SharePoint'}
                                  sharePointConfigured={sharePointConfigured}
                                  /* ⛔ THE SAME KEY THE SLIVER TAKES. Pete: "Local
                                     doesn't have a refresh so both files are showing up
                                     in both spots." Every cross-pile move bumps it; this
                                     tray now hears about them too. */
                                  refreshKey={paperworkKey}
                              />
                          </>
                      }
                  />
              )}
            </div>

            {/**
              * ---- ⭐ SIGNING WITHOUT THE LIST IN THE WAY (#245) ------------------
              *
              * Pete: *"Only show that intermediate screen if you need to edit,
              * print, etc."* So when `choosePaperworkAction` finds exactly one
              * original, this opens on its own and the documents dialog never
              * appears at all.
              *
              * ⚠️ THE DOCUMENTS DIALOG KEEPS ITS OWN INSTANCE for the
              * choose-from-the-list path. Two mounts of a self-contained dialog is
              * not duplication — the ALTERNATIVE was routing every direct sign
              * through a screen Pete explicitly asked not to see.
              */}
            {facilityId !== null && (
                <SignDocumentDialog
                    open={signDirect !== null}
                    document={signDirect?.doc ?? null}
                    facilityCode={facilityId}
                    carrierCode={signDirect?.carrierCode}
                    appointmentNumber={signDirect?.appointmentNumber}
                    getToken={getToken}
                    onClose={() => setSignDirect(null)}
                    onSigned={() => {
                        setSignDirect(null);
                        // The signed copy is a SECOND row: the card's count and the
                        // print target both change, so reload rather than patch.
                        setPaperworkKey(k => k + 1);
                        setRefreshKey(k => k + 1);
                        void reload();
                    }}
                />
            )}

            {/* ---- A load's paperwork (#144) ---- */}
            {facilityId !== null && (
                <AppointmentDocumentsDialog
                    open={docsFor !== null}
                    onClose={() => setDocsFor(null)}
                    appointmentId={docsFor?.appointmentId ?? null}
                    checkInId={docsFor?.checkInId ?? null}
                    appointmentNumber={docsFor?.appointmentNumber}
                    facilityCode={facilityId}
                    carrierCode={docsFor?.carrierCode}
                    getToken={getToken}
                    // ⚠️ CLOSES, rather than opening a second scanner. The scan
                    // tray is already permanently mounted in the arrivals panel
                    // footer — a modal on top of it would be a second way to do
                    // the same thing, and the one underneath is the one schedulers
                    // already use.
                    // Signing or voiding changes the paperwork tick on the driver
                    // card, which is rendered from the check-in list.
                    onChanged={() => { setRefreshKey(k => k + 1); void reload(); }}
                />
            )}

            {/* ---- Settings dialog (admins only) ---- */}
            {settingsOpen && lookups && (
                <SettingsDialog
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    lookups={lookups}
                    getToken={getToken}
                    onChanged={reload}
                    initialTab={settingsTab}
                    focusCode={settingsFocus}
                    currentFacilityCode={facilityId}
                />
            )}

            {/* ---- Door editor opened directly from the calendar door label (admins) ---- */}
            {editingDoor && lookups && facilityId !== null && (
                <DoorEditor
                    facilityCode={facilityId}
                    initial={editingDoor}
                    capabilities={lookups.capabilities.map<CapabilityAdminRow>((c) => ({
                        CapabilityId: c.CapabilityId,
                        Code:         c.Code,
                        Name:         c.Name,
                        Description:  null,
                    }))}
                    getToken={getToken}
                    onClose={() => setEditingDoor(null)}
                    onSaved={() => { setEditingDoor(null); void reload(); }}
                />
            )}

            {/* ---- Mass create day-only appointments (#66) ----
                 ⚠️ Needs `lookups` (the client and carrier lists) but NOT
                 `calendar` — unlike New appointment it asks for no door and no
                 slot length, so there is nothing to wait for the day's DTO for. */}
            {lookups && facilityId !== null && (
                <MassCreateDialog
                    open={massOpen}
                    onClose={() => setMassOpen(false)}
                    facilityCode={facilityId}
                    localDate={date}
                    lookups={lookups}
                    // The batch is invisible until the grid re-reads. Fired on
                    // create AND on undo, so an undone batch does not linger on
                    // screen looking like twenty live appointments.
                    onChanged={() => { setRefreshKey(k => k + 1); void reload(); }}
                    getToken={getToken}
                />
            )}

            {/* ---- Upload appointments from a spreadsheet (#159) ----
                 ⚠️ Still does not need `calendar`: the file carries its own
                 references and the server decides everything else.

                 ⛔ AND IT IS STILL NOT GATED ON `lookups`, deliberately. The client
                 list only fills a picker that is OPTIONAL (#174) — refusing to open
                 the whole dialog because a lookup call failed would take a working
                 feature down to lose an enrichment. An empty list simply means the
                 picker offers nothing and the upload proceeds without a client. */}
            {facilityId !== null && (
                <UploadAppointmentsDialog
                    open={uploadOpen}
                    onClose={() => setUploadOpen(false)}
                    facilityCode={facilityId}
                    clients={lookups?.clients ?? []}
                    localDate={date}
                    // Fired on commit AND on undo, so an undone batch does not
                    // linger on screen looking like forty live appointments.
                    onChanged={() => { setRefreshKey(k => k + 1); void reload(); }}
                    getToken={getToken}
                />
            )}

            {/* ---- New appointment dialog ---- */}
            {lookups && calendar && facilityId !== null && (
                <NewAppointmentDialog
                    open={newOpen}
                    onClose={() => setNewOpen(false)}
                    facilityCode={facilityId}
                    doors={calendar.doors}
                    lookups={lookups}
                    prefill={newPrefill}
                    slotMinutes={calendar.slotMinutes}
                    onCreated={async (appt) => {
                        // ⚠️ Attach BEFORE the caller's own handler runs, so the
                        // reload it triggers already shows the truck matched.
                        if (pendingCheckInId) {
                            try {
                                await attachCheckIn(await getToken(), pendingCheckInId, String(appt.appointmentId));
                            } catch (err) {
                                // The appointment exists either way. Losing the link is
                                // recoverable from the panel; failing the create is not.
                                console.error('could not attach the arrival to the new appointment', err);
                            }
                            setPendingCheckInId(null);
                            // The walk-up now has a booking, so it now has a door
                            // to be told about — or a "waiting" message to send.
                            setDoorTextKind('door');
                            setDoorTextFor(String(appt.appointmentId));
                        }
                        (handleAppointmentCreated)(appt);
                    }}
                    onBlocksCreated={(result) => {
                        void reload();
                        const made = result.created.length;
                        const skipped = result.skipped.length;
                        setToast({
                            kind: 'success',
                            msg: skipped > 0
                                ? `Blocked ${made} door${made === 1 ? '' : 's'}; skipped ${skipped} (already booked).`
                                : `Blocked ${made} door${made === 1 ? '' : 's'}.`,
                        });
                    }}
                    getToken={getToken}
                />
            )}

            {/* ---- Detail dialog ---- */}
            {selectedAppt && calendar && (
                <AppointmentDetailDialog
                    open={true}
                    onClose={() => setSelectedAppt(null)}
                    appt={selectedAppt}
                    doors={calendar.doors}
                    getToken={getToken}
                    onChanged={() => {
                        void reload();
                        setToast({ kind: 'success', msg: 'Appointment updated.' });
                    }}
                    onReschedule={(appt) => {
                        // The Edit dialog now handles door + date/time (with the same
                        // cap/dedication + notify safeguards), so Reschedule opens it.
                        // Dragging on the board still works as a shortcut.
                        setSelectedAppt(null);
                        setEditingAppt(appt);
                    }}
                    onEdit={(appt) => setEditingAppt(appt)}
                    // ⭐ The SAME dialog the right-click "Paperwork…" item opens — one
                    // viewer of the evidence, reached from wherever somebody is standing.
                    onPaperwork={(appt) => setDocsFor({
                        appointmentId: String(appt.appointmentId),
                        appointmentNumber: appt.appointmentNumber,
                        carrierCode: appt.carrierCode,
                    })}
                    onNotify={(appt, event) => {
                        void reload();
                        if (appt.direction === 'BLOCK') return;   // blocks have no carrier
                        setNotifyPrompt({
                            appointmentId: appt.appointmentId,
                            appointmentNumber: appt.appointmentNumber,
                            event,
                            drivers: appt.drivers ?? [],
                            carrierName: appt.carrierName,
                        });
                    }}
                />
            )}
			
			{/**
			  * ⭐ #306 — CHANGE A STATUS BY HAND. Admin only; the menu entry that
			  * opens it is absent for everyone else, and the routes behind it are
			  * gated on EntraGroupAdmins as well — a hidden menu item is not a
			  * permission.
			  */}
			<StatusOverrideDialog
				appt={overrideAppt}
				getToken={getToken}
				onClose={() => setOverrideAppt(null)}
				onApplied={(msg) => {
					setToast({ kind: 'success', msg });
					/* ⛔ A FULL RELOAD, NOT A LOCAL PATCH. The status, the arrival
					   stamp and the departure stamp can all have moved, and a
					   hand-patched tile would show the new chip beside stale times. */
					void reload();
				}}
			/>

			<DoorTextDialog
				open={doorTextFor !== null}
				appointmentId={doorTextFor}
				kind={doorTextKind}
				/* ⛔ BOTH ARE REQUIRED FOR THE AUTOMATIC STAMP (#146), and they are
				   optional props — so omitting them does not fail the build, it
				   just leaves the checkbox permanently and silently disabled.
				   ⚠️ The CARRIER matters specifically: `lastPlacementFor` falls
				   back to any placement at the facility when it is null, which is
				   another carrier's page layout. */
				facilityCode={facilityId}
				carrierCode={
					doorTextFor
						? calendar?.appointments.find(
							a => String(a.appointmentId) === doorTextFor)?.carrierCode ?? null
						: null
				}
				getToken={getToken}
				onClose={() => setDoorTextFor(null)}
				onSent={(s) => {
					// ⚠️ 'opted out' IS NOT A FAILURE — it means he replied STOP.
					// Reporting it as one sends a scheduler chasing a delivery
					// problem that does not exist.
					const bits = [`${s.sent} texted`];
					if (s.optedOut > 0) bits.push(`${s.optedOut} opted out of texts`);
					if (s.failed > 0) bits.push(`${s.failed} failed`);
					const lead = doorTextKind === 'paperwork'
						? 'Paperwork ready: '
						: s.doorCode ? `Door ${s.doorCode}: ` : 'No door yet: ';
					setToast({
						kind: s.failed > 0 ? 'error' : 'success',
						msg: lead + bits.join(', '),
					});
				}}
			/>

			{releaseFor && (
				<OutboundReleaseDialog
					open={true}
					appointmentId={releaseFor.appt.appointmentId}
					appointmentNumber={releaseFor.appt.appointmentNumber}
					getToken={getToken}
					busy={releaseBusy}
					onCancel={() => setReleaseFor(null)}
					onConfirm={(o) => void handleReleaseConfirm(o)}
				/>
			)}

			{assignDoor && calendar && (
				<AssignDoorDialog
					open={!!assignDoor}
					appointmentLabel={assignDoor.appt.appointmentNumber ?? assignDoor.appt.carrierName ?? `Appointment ${assignDoor.appt.appointmentId}`}
					doors={calendar.doors}
					onConfirm={handleAssignDoorConfirm}
					onCancel={() => setAssignDoor(null)}
					busy={assignBusy}
				/>
			)}
 

            {/**
              * ---- ⛔⛔ THE GATE NAMED THE WRONG DOOR SOURCE (fixed 2026-09-27) ------
              *
              * > Pete: *"Double-click on Week and Month view should pull the appointment
              * > into EDIT"*
              *
              * ⛔⛔ ~~`editingAppt && lookups && calendar`~~ — AND THAT `calendar` WAS A
              * SILENT NO-OP IN THE TWO VIEWS HE IS ASKING ABOUT. Only `getCalendar` fills
              * `calendar`, and week and month call `getCalendarRange` instead; the
              * facility-change effect clears it outright. So the dialog could never render
              * from Week or Month however the double-click was wired, and it would have
              * failed by DOING NOTHING — the hardest kind of wrong to see, and the same
              * shape as the `perHour?` bug (a value that was simply never there).
              *   ** A RENDER GATE THAT NAMES A STATE ONE VIEW NEVER FILLS IS A FEATURE
              *      THAT COMPILES, SHIPS, AND IS ABSENT. **
              *
              * ⭐ THE DOORS EXIST IN BOTH — `rangeData.doors` is what `WeekBoard` is
              * already drawn from. ⛔ The dialog needs NOTHING ELSE off `calendar`; doors
              * is the only field it read, which is why this is a gate fix and not a fetch.
              *
              * ⚠️ `calendar` FIRST, deliberately. On Day it is the payload for the date on
              * screen; `rangeData` there is whatever range was last loaded and may be a
              * different week entirely.
              */}
            {editingAppt && lookups && (calendar?.doors ?? rangeData?.doors) && (
                <EditAppointmentDialog
                    open={true}
                    onClose={() => setEditingAppt(null)}
                    appt={editingAppt}
                    doors={(calendar?.doors ?? rangeData?.doors) ?? []}
                    lookups={lookups}
                    getToken={getToken}
                    onSaved={(updated, schedule) => {
                        setEditingAppt(null);
                        if (schedule) {
                            // Content is saved; reflect it locally, then run the shared
                            // move handler so the reschedule (door and/or time) gets the
                            // same cap/dedication override prompt and carrier-notify
                            // prompt a drag would.
                            patchApptEverywhere(updated.appointmentId, updated);
                            void handleMoveAppointment({
                                appointmentId: updated.appointmentId,
                                dockDoorId: schedule.dockDoorId,
                                newStartUtc: schedule.newStartUtc,
                                newEndUtc: schedule.newEndUtc,
                            });
                        } else {
                            /**
                              * ---- ✅ DECISION 186 — A DETAILS-ONLY EDIT NOTIFIES TOO (2026-09-18) ----
                              *
                              * > Pete asked for an "email the carrier" checkbox on this dialog.
                              * > ⛔ The MOVE path above already prompts — it routes through
                              * > `handleMoveAppointment` and gets the shared "Notify carrier and
                              * > drivers?" dialog. ONLY THIS BRANCH was silent: change a carrier,
                              * > a PO or a pallet count and nobody was told.
                              *
                              * ⛔⛤ A CHECKBOX WOULD HAVE BEEN A SECOND CONTROL FOR THE SAME
                              * ACTION. On an edit that also moved the appointment it would send
                              * at save and the prompt would ask again a moment later.
                              *
                              *   ** THE SCREEN DID NOT NEED A NEW CONTROL. IT NEEDED AN EVENT THE
                              *      EXISTING CONTROL HAD NO WORD FOR. **
                              *
                              * ⭐ Same dialog, same four buttons, same "Don't notify" default
                              * posture — nothing is sent unless a human presses a send button.
                              * ⚠️ `'updated'` is NOT in DRIVER_NOTIFY_EVENTS, so `canText` is
                              * false and the prompt says so in its own words rather than hiding
                              * the option silently.
                              * ⚠️⚠️ IF THE EDIT CHANGED THE CARRIER, THE EMAIL GOES TO THE **NEW**
                              * ONE — the server resolves the carrier on the row as it now stands.
                              * ⭐ Safe only because this dialog NAMES the recipient before
                              * sending. ⬜ Telling the OUTGOING carrier is a separate feature.
                              */
                            void reload();
                            setToast({
                                kind: 'success',
                                msg: `${updated.appointmentNumber} updated.`,
                            });
                            setNotifyPrompt({
                                appointmentId: updated.appointmentId,
                                appointmentNumber: updated.appointmentNumber,
                                event: 'updated',
                                drivers: updated.drivers ?? [],
                                carrierName: updated.carrierName,
                            });
                        }
                    }}
                />
            )}

            {/* ---- Right-click menu for a facility-limit window ---- */}
            {limitMenu && (() => {
                const b0 = limitMenu.bands[0];
                const span = !b0?.effectiveStartDate && !b0?.effectiveEndDate
                    ? 'Recurring · every matching weekday'
                    : b0.effectiveStartDate && b0.effectiveStartDate === b0.effectiveEndDate
                        ? `Single date · ${b0.effectiveStartDate}`
                        : `${b0.effectiveStartDate ?? '…'} → ${b0.effectiveEndDate ?? '…'}`;
                const isBlock = limitMenu.bands.every((b) => b.maxConcurrent === 0);
                return (
                    <ContextMenu
                        x={limitMenu.x}
                        y={limitMenu.y}
                        onClose={() => setLimitMenu(null)}
                        items={[
                            { label: `${isBlock ? 'Blocked' : 'Limit'} · ${span}`, onClick: () => {}, disabled: true },
                            { label: 'Make inactive', onClick: () => { void makeLimitInactive(); } },
                            { label: 'Delete (whole rule)', onClick: () => { void deleteLimit(); }, destructive: true },
                            { label: 'Edit in settings…', onClick: () => { setLimitMenu(null); handleOpenLimits(); } },
                        ]}
                    />
                );
            })()}

            {/* ---- Right-click menu for a door dedication ---- */}
            {dedicationMenu && (() => {
                const d = dedicationMenu;
                const span = !d.effectiveStartDate && !d.effectiveEndDate
                    ? 'Recurring · every matching day'
                    : d.effectiveStartDate && d.effectiveStartDate === d.effectiveEndDate
                        ? `Single date · ${d.effectiveStartDate}`
                        : `${d.effectiveStartDate ?? '…'} → ${d.effectiveEndDate ?? '…'}`;
                return (
                    <ContextMenu
                        x={d.x}
                        y={d.y}
                        onClose={() => setDedicationMenu(null)}
                        items={[
                            { label: `${d.label} · ${span}`, onClick: () => {}, disabled: true },
                            { label: 'Make inactive', onClick: () => { void makeDedicationInactive(); } },
                            { label: 'Delete (whole rule)', onClick: () => { void deleteDedication(); }, destructive: true },
                            { label: 'Edit in settings…', onClick: () => { setDedicationMenu(null); handleOpenDedications(); } },
                        ]}
                    />
                );
            })()}

            {/* ---- Right-click on empty door-row space ---- */}
            {doorMenu && (
                <ContextMenu
                    x={doorMenu.x}
                    y={doorMenu.y}
                    onClose={() => setDoorMenu(null)}
                    items={[
                        {
                            label: `Block ${doorMenu.doorCode}…`,
                            onClick: () => {
                                setNewPrefill({
                                    dockDoorId: doorMenu.dockDoorId,
                                    startLocal: doorMenu.startLocal,
                                    endLocal: doorMenu.endLocal,
                                    direction: 'BLOCK',
                                });
                                setNewOpen(true);
                                setDoorMenu(null);
                            },
                        },
                        {
                            label: `New appointment at ${doorMenu.doorCode}…`,
                            onClick: () => {
                                setNewPrefill({
                                    dockDoorId: doorMenu.dockDoorId,
                                    startLocal: doorMenu.startLocal,
                                    endLocal: doorMenu.endLocal,
                                });
                                setNewOpen(true);
                                setDoorMenu(null);
                            },
                        },
                    ]}
                />
            )}

            {/* ---- Right-click context menu (appointments) ---- */}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onClose={() => setContextMenu(null)}
                    items={contextMenuItemsFor(
                        contextMenu.appt,
                        {
                            onDetails: () => setSelectedAppt(contextMenu.appt),
                            onApprove: () => doQuickAction(contextMenu.appt, 'approve'),
                            onReject: () => doQuickAction(contextMenu.appt, 'reject'),
                            onArrive: () => doQuickAction(contextMenu.appt, 'arrive'),
                            onDepart: () => doQuickAction(contextMenu.appt, 'depart'),
                            onNoShow: () => doQuickAction(contextMenu.appt, 'noshow'),
                            onCancel: () => doQuickAction(contextMenu.appt, 'cancel'),
                            onSendDirections: () => setSendDirFacility({
                                code: contextMenu.appt.facilityCode,
                                drivers: contextMenu.appt.drivers.map(d => ({ name: d.name, phone: d.phone })),
                                appointmentId: contextMenu.appt.appointmentId,
                            }),
                            /* ⭐ #324 — OUTBOUND ONLY. `undefined` elsewhere, so the entry
                               is absent rather than present-and-explaining-itself. */
                            onSendConsigneePin: contextMenu.appt.direction === 'OUTBOUND'
                                ? () => setPinAppt({
                                    appointmentId: String(contextMenu.appt.appointmentId),
                                    drivers: contextMenu.appt.drivers.map(d => ({ name: d.name, phone: d.phone })),
                                })
                                : undefined,
                            onTextDoor: () => { setDoorTextKind('door'); setDoorTextFor(String(contextMenu.appt.appointmentId)); },
                            onTextPaperwork: () => { setDoorTextKind('paperwork'); setDoorTextFor(String(contextMenu.appt.appointmentId)); },
                            onTextWork: () => { setDoorTextKind('work'); setDoorTextFor(String(contextMenu.appt.appointmentId)); },
                            /* ⭐ #306 — ADMIN ONLY. `undefined` for everyone else, so the
                               entry is absent rather than present-and-refused. */
                            onOverrideStatus: isAdmin
                                ? () => setOverrideAppt(contextMenu.appt)
                                : undefined,
                            onPaperwork: () => setDocsFor(scopeOfAppointment(contextMenu.appt)),
                            onSignPaperwork: () =>
                                void handleSignPaperwork(scopeOfAppointment(contextMenu.appt)),
                            onPrintPaperwork: () =>
                                void handlePrintPaperwork(scopeOfAppointment(contextMenu.appt)),
                            onEditDriver: () => setDriverAssignAppt(contextMenu.appt),
                        },
                    )}
                />
            )}

            {/* ---- Delivery pin dialog (#324, from right-click, outbound only) ---- */}
            {pinAppt && (
                <SendConsigneePinDialog
                    open={true}
                    appointmentId={pinAppt.appointmentId}
                    drivers={pinAppt.drivers}
                    getToken={getToken}
                    onClose={() => setPinAppt(null)}
                />
            )}

            {/* ---- Send directions dialog (from right-click) ---- */}
            {sendDirFacility && (
                <SendDirectionsDialog
                    open={true}
                    onClose={() => setSendDirFacility(null)}
                    facilityCode={sendDirFacility.code}
                    facilityLabel={sendDirFacility.label}
                    drivers={sendDirFacility.drivers}
                    appointmentId={sendDirFacility.appointmentId}
                    getToken={getToken}
                />
            )}

            {/* ---- Edit driver dialog (from right-click) ---- */}
            {driverAssignAppt && (
                <AssignDriverDialog
                    open={true}
                    onClose={() => setDriverAssignAppt(null)}
                    appt={driverAssignAppt}
                    getToken={getToken}
                    onAssigned={() => reload()}
                />
            )}

            {/* ---- Confirm a recurring limit resize/move ---- */}
            {pendingLimitEdit && (() => {
                const b0 = pendingLimitEdit.bands[0];
                const scope = !b0?.effectiveStartDate && !b0?.effectiveEndDate
                    ? 'every matching weekday (recurring)'
                    : `${b0.effectiveStartDate ?? '…'} – ${b0.effectiveEndDate ?? '…'}`;
                const fmt = (m: number) => {
                    const h = Math.floor(m / 60), mm = m % 60, hr = ((h + 11) % 12) + 1, ap = h < 12 ? 'AM' : 'PM';
                    return `${hr}:${String(mm).padStart(2, '0')} ${ap}`;
                };
                return (
                    <ConfirmDialog
                        open={true}
                        title="Change this limit?"
                        message={`This limit applies to ${scope}. Move it to ${fmt(pendingLimitEdit.startMinutes)}–${fmt(pendingLimitEdit.endMinutes)} for all of those days?`}
                        confirmLabel="Change limit"
                        onConfirm={() => { const ev = pendingLimitEdit; setPendingLimitEdit(null); void applyLimitEdit(ev); }}
                        onCancel={() => setPendingLimitEdit(null)}
                    />
                );
            })()}

            {/**
              * ---- ⭐⭐ MOVING PAPERWORK OUT TO THE SCAN FOLDER -----------------
              *
              * > Pete: *"Can we have a popup from our app and keep it consistent
              * > with our standards?"*
              *
              * ⛔ IT DOES NOT REPLACE CHROME'S PROMPT — nothing can. It goes IN
              * FRONT of it, in our words, and its Continue button is the gesture
              * that triggers Chrome's. See the note on `handleDropLocal`.
              *
              * ⛔⛔ AND IT NAMES THE CONSEQUENCE, because this one is a MOVE: the
              * Dock document is voided and a SharePoint scan goes to that site's
              * recycle bin. ⚠️ `destructive` is set for the same reason.
              */}
            {localDrop && (() => {
                const names = localDrop.items.filter(i => i.kind !== 'localScan').map(i => i.name);
                const what = names.length === 1 ? names[0] : `${names.length} documents`;
                return (
                    <ConfirmDialog
                        open={true}
                        destructive
                        title={`Move ${names.length === 1 ? 'this document' : `${names.length} documents`} to ${localDrop.folderName}?`}
                        message={
                            `${what} will be saved into the ${localDrop.folderName} folder on this machine `
                            + 'and then removed from Dock — a filed document is voided (its history is kept) '
                            + "and a SharePoint scan goes to that site's recycle bin for 93 days.\n\n"
                            + 'Chrome will ask separately for permission to write to the folder. Nothing '
                            + 'already in it is replaced: a name that is taken becomes "name (2)".'
                        }
                        confirmLabel="Move to folder"
                        onConfirm={() => runLocalDrop(localDrop.items)}
                        onCancel={() => setLocalDrop(null)}
                    />
                );
            })()}

            {/* ---- Confirm a recurring dedication resize/move ---- */}
            {pendingDedicationEdit && (() => {
                const d = pendingDedicationEdit;
                const scope = !d.effectiveStartDate && !d.effectiveEndDate
                    ? 'every matching day (recurring)'
                    : `${d.effectiveStartDate ?? '…'} – ${d.effectiveEndDate ?? '…'}`;
                const fmt = (m: number) => {
                    const h = Math.floor(m / 60), mm = m % 60, hr = ((h + 11) % 12) + 1, ap = h < 12 ? 'AM' : 'PM';
                    return `${hr}:${String(mm).padStart(2, '0')} ${ap}`;
                };
                return (
                    <ConfirmDialog
                        open={true}
                        title="Change this dedication?"
                        message={`This dedication (${d.label}) applies to ${scope}. Move it to ${fmt(d.startMinutes)}–${fmt(d.endMinutes)} for all of those days?`}
                        confirmLabel="Change dedication"
                        onConfirm={() => { const ev = pendingDedicationEdit; setPendingDedicationEdit(null); void applyDedicationEdit(ev); }}
                        onCancel={() => setPendingDedicationEdit(null)}
                    />
                );
            })()}

            {/* ---- ⭐ BACK OUT THE DAY'S UPLOAD (#139) ----

                 ⛔ THIS DELETES. Every other undo in the app cancels, so the copy has
                 to say which one this is — a scheduler who reads "undo" and gets a
                 permanent removal has been misled by the button, not by themselves. */}
            {backoutConfirm && backoutFor && (
                <ConfirmDialog
                    open={true}
                    destructive
                    title="Back out this upload?"
                    message={
                        `Back out the ${backoutFor.appointmentCount} appointment(s) created by `
                        + `"${backoutFor.label}" on ${date}.\n\n`
                        + `• Anything nothing has happened to is DELETED — it will not appear anywhere afterwards.\n`
                        + `• Anything already worked on (a message sent, paperwork attached, labor clocked) is CANCELLED, so the record survives.\n`
                        + `• A truck that is on site or has already been and gone is left exactly as it is.\n\n`
                        + `You will get the full breakdown afterwards.`
                    }
                    confirmLabel="Back it out"
                    cancelLabel="Keep them"
                    onConfirm={() => { setBackoutConfirm(false); void runBackout(); }}
                    onCancel={() => setBackoutConfirm(false)}
                />
            )}

            {/* ⛔ THE RESULT STAYS ON SCREEN. A toast is not an answer to "what
                happened to my 41 appointments?" — and the REFUSED list is the half a
                scheduler most needs, because those are still live. */}
            {/**
              * ⛔⛔ #300 — THE OVERWRITE QUESTION, IN PETE'S OWN WORDS.
              *
              * ⚠️ `ConfirmDialog`, NEVER `window.confirm`. Native browser dialogs are
              * banned in this codebase — Pete: *"this is lazy coding and we never do
              * this"* — and this one has to name the driver being replaced, which a
              * native dialog cannot style and a scheduler would skim past.
              *
              * ⭐ `destructive`, because it is. Confirming DELETES the booked driver
              * row and puts this arrival's driver on the load in its place.
              */}
            {/**
              * ⛔ THE WORDING IS THE FEATURE HERE. A scheduler who reads "moved"
              * as "also added" has silently emptied a load, and nothing on the
              * grid will tell them — so the sentence says what the OLD load looks
              * like afterwards, not just what the new one gains.
              */}
            {/* ⭐ D3 / Decision 238 — the Match picker. Choosing a load runs the SAME
                path as dropping the order on it, replace popup included. */}
            <WmsOrderMatchDialog
                open={matchFor !== null}
                orderNumber={matchFor?.orderNumber ?? ''}
                orderKey={matchFor}
                order={matchRow}
                appointments={calendar?.appointments ?? []}
                facilityCode={facilityId}
                getToken={getToken}
                onChoose={(appt, order) => {
                    /* ⭐ F107: `order` is the one compared — the one the screen opened for,
                       or one the scheduler found with the new order search. */
                    setMatchFor(null);
                    void handleDropWmsOrder({ order, appt });
                }}
                onCancel={() => setMatchFor(null)}
            />

            {/* ⭐ F107 / Decision 284 — the SAME Match screen, opened from the No WMS chip:
                the loads with no order on the left, an order found by search on the right.
                ⭐ It stays open after an attach; `noWmsRefresh` re-reads the list and the
                matched load leaves it. The attach path is the drag's (replace popup included). */}
            <WmsOrderMatchDialog
                open={noWmsList !== null}
                mode="noWms"
                orderNumber=""
                appointments={noWmsList?.rows ?? []}
                total={noWmsList?.total}
                facilityCode={facilityId}
                getToken={getToken}
                onChoose={(appt, order) => { void handleDropWmsOrder({ order, appt }); }}
                onCancel={() => setNoWmsList(null)}
            />

            {/* ⭐ A5 — the replace popup. Nothing ticked by default; see the component. */}
            <WmsOrderReplaceDialog
                open={orderAsk !== null}
                orderNumber={orderAsk?.ev.order.orderNumber ?? ''}
                targetAppointmentNumber={orderAsk?.ev.appt.appointmentNumber ?? ''}
                links={orderAsk?.others ?? []}
                hiddenElsewhere={orderAsk?.hiddenElsewhere ?? 0}
                busy={orderBusy}
                onConfirm={(removeFrom) => { if (orderAsk) void attachDroppedOrder(orderAsk.ev, removeFrom); }}
                onCancel={() => setOrderAsk(null)}
            />

            <ConfirmDialog
                open={moveAsk !== null}
                title={`Move this arrival off ${moveAsk?.ev.fromAppointmentNumber ?? ''}?`}
                message={
                    `This MOVES the arrival to ${moveAsk?.ev.appt.appointmentNumber ?? ''}. It does not copy it.\n\n`
                    + `${moveAsk?.ev.fromAppointmentNumber ?? ''} will be left with no arrival at all — as though `
                    + 'this truck never checked in for it. Its check-in time, its place in today\u2019s queue and '
                    + 'its paperwork link all go with the move.\n\n'
                    + 'If he is actually doing a second load, do not move this one. Have him check in again: a '
                    + 'driver gets one check-in per appointment, so three inbounds today means three arrivals. He will '
                    + 'not be asked to scan his license again — we already have it against his number.\n\n'
                    + 'Only move it if this arrival was attached to the wrong load in the first place.'
                }
                confirmLabel="Move it anyway"
                destructive
                onConfirm={() => {
                    const pending = moveAsk;
                    setMoveAsk(null);
                    /* ⛔ `...pending.answers` FIRST. Anything already answered has to
                       survive this lap, or the next prompt asks it again — which is
                       the loop this shape was introduced to end. */
                    if (pending) {
                        void handleDropCheckInOnAppointment(
                            pending.ev, { ...pending.answers, confirmedMove: true },
                        );
                    }
                }}
                onCancel={() => setMoveAsk(null)}
            />

            {driverOverwrite && (
                <ConfirmDialog
                    open={true}
                    destructive
                    title="Replace the driver booked on this appointment?"
                    message={
                        `${driverOverwrite.ev.appt.appointmentNumber} is already booked to `
                        + `${driverOverwrite.bookedDrivers.join(', ')}.\n\n`
                        + `This driver was NOT booked on it originally — he arrived at the gate.\n\n`
                        + `• The booked driver is removed from the appointment.\n`
                        + `• The arrival at the gate becomes the appointment's driver.\n`
                        + `• Nothing else about the appointment changes, and no message is sent.`
                    }
                    confirmLabel="Replace the driver"
                    cancelLabel="Leave it alone"
                    onConfirm={() => {
                        const pending = driverOverwrite;
                        setDriverOverwrite(null);
                        /* ⛔⛤ ~~`(pending.ev, true)`~~ — it dropped `confirmedMove`, so a
                           MOVED arrival onto an appointment with a booked driver alternated
                           between this prompt and the move prompt forever. */
                        void handleDropCheckInOnAppointment(
                            pending.ev, { ...pending.answers, replaceBookedDriver: true },
                        );
                    }}
                    onCancel={() => setDriverOverwrite(null)}
                />
            )}

            {/**
              * ---- ⭐⭐ #73 — THE BOOKING SAYS ONE BOX, THE TRUCK IS ANOTHER ------
              *
              * ⛔ NOT `destructive`, UNLIKE THE DRIVER PROMPT BESIDE IT. Replacing a
              * booked driver deletes a row; this replaces two identifying numbers on a
              * booking, both of which are recorded in the audit with the scheduler's
              * name. Painting every confirmation red is how red stops meaning
              * anything.
              *
              * ⚠️ BOTH BUTTONS COMPLETE THE ATTACH, AND THE LABELS SAY SO. This is
              * the one prompt here where Cancel is NOT "do nothing" — the truck goes
              * on the load either way and the only question is whose numbers the
              * booking keeps. A "Cancel" that quietly attached the truck anyway would
              * be the worst possible wording.
              *   ** WHEN NEITHER ANSWER IS "DO NOTHING", NEITHER BUTTON MAY SAY
              *      CANCEL. **
              */}
            {vehicleOverwrite && (
                <ConfirmDialog
                    open={true}
                    title="Use this truck's numbers on the appointment?"
                    message={
                        `${vehicleOverwrite.ev.appt.appointmentNumber} and this truck disagree:\n\n`
                        + vehicleOverwrite.conflicts.map(c => `• ${c.detail}`).join('\n')
                        + '\n\nThe driver is attached to the appointment either way.\n\n'
                        + '• Use the truck\u2019s numbers — the booking is updated, and the change is '
                        + 'recorded on the appointment with your name and both values.\n'
                        + '• Keep the booked numbers — nothing on the booking changes, and the '
                        + 'disagreement is logged on the appointment for review.'
                    }
                    confirmLabel="Use the truck's numbers"
                    cancelLabel="Keep the booked numbers"
                    onConfirm={() => {
                        const pending = vehicleOverwrite;
                        setVehicleOverwrite(null);
                        void handleDropCheckInOnAppointment(
                            pending.ev, { ...pending.answers, overwriteVehicleNumbers: true },
                        );
                    }}
                    /* ⛔ FALSE, NOT `null`. Closing this dialog must still ATTACH the
                       truck — leaving it unanswered would abandon a drag the scheduler
                       already made, with no sign anything had happened. */
                    onCancel={() => {
                        const pending = vehicleOverwrite;
                        setVehicleOverwrite(null);
                        void handleDropCheckInOnAppointment(
                            pending.ev, { ...pending.answers, overwriteVehicleNumbers: false },
                        );
                    }}
                />
            )}

            {/**
              * ⭐⭐ THE PAPERWORK PROMPT — the same shape as the vehicle one above,
              * deliberately, so a scheduler who has just answered that one recognises
              * this one. ⚠️ THE WORDING IS NOT THE SAME: this is the number the load
              * is worked under downstream, and the dialog says so rather than leaving
              * the two decisions looking interchangeable.
              */}
            {paperworkOverwrite && (
                <ConfirmDialog
                    open={true}
                    title="Use the driver's paperwork numbers on the appointment?"
                    message={
                        `${paperworkOverwrite.ev.appt.appointmentNumber} and this driver disagree:\n\n`
                        + paperworkOverwrite.conflicts.map(c => `• ${c.detail}`).join('\n')
                        + '\n\nThe driver is attached to the appointment either way.\n\n'
                        + '• Use the driver\u2019s numbers — the booking is updated, and the change is '
                        + 'recorded on the appointment with your name and both values. This is the '
                        + 'number the appointment is worked and reported under, so it travels further than '
                        + 'a trailer number does.\n'
                        + '• Keep the booked numbers — nothing on the booking changes, and the '
                        + 'disagreement is logged on the appointment for review.'
                    }
                    confirmLabel="Use the driver's numbers"
                    cancelLabel="Keep the booked numbers"
                    onConfirm={() => {
                        const pending = paperworkOverwrite;
                        setPaperworkOverwrite(null);
                        void handleDropCheckInOnAppointment(
                            pending.ev, { ...pending.answers, overwritePaperworkNumbers: true },
                        );
                    }}
                    /* ⛔ FALSE, NOT `null`. Closing this dialog must still ATTACH the
                       truck — leaving it unanswered would abandon a drag the scheduler
                       already made, with no sign anything had happened. */
                    onCancel={() => {
                        const pending = paperworkOverwrite;
                        setPaperworkOverwrite(null);
                        void handleDropCheckInOnAppointment(
                            pending.ev, { ...pending.answers, overwritePaperworkNumbers: false },
                        );
                    }}
                />
            )}

            {/**
              * ⭐⭐ THE PALLET WARNING — A STATEMENT, NOT A QUESTION ABOUT WHETHER TO
              * PROCEED. The attach is done. ⚠️ BOTH BUTTONS THEREFORE DO SOMETHING
              * DIFFERENT AND NEITHER IS "CANCEL": one opens the booking so the count
              * can be corrected, the other carries on to the door text that a
              * variance-free attach would have shown immediately.
              *   ** WHEN NEITHER ANSWER IS "DO NOTHING", NEITHER BUTTON MAY SAY
              *      CANCEL. **
              * ⛔ IT DOES NOT OFFER TO REWRITE THE BOOKED COUNT. `recordCheckIn`
              * already logs the variance under the rule that the appointment's own
              * count is NOT overwritten — *"expected 13, arrived 12" is what a client
              * disputes six weeks later* — and a button here would undo that on a
              * click.
              */}
            {attachNotice && (
                <ConfirmDialog
                    open={true}
                    title={attachNotice.variance
                        ? 'The pallet count does not match'
                        : 'Check these numbers'}
                    message={
                        (attachNotice.variance
                            ? `${attachNotice.appt.appointmentNumber} is booked for `
                              + `${attachNotice.variance.appointmentCount} pallet(s), and the driver `
                              + `told the gate he is carrying ${attachNotice.variance.driverCount}.\n\n`
                            : '')
                        /* ⭐ THE HINTS READ AS SENTENCES BECAUSE THE SERVER WROTE THEM
                           AS SENTENCES. `detail` already names both fields and both
                           percentages; rebuilding it here would be a second wording of
                           one judgement. */
                        + (attachNotice.hints.length > 0
                            ? attachNotice.hints.map(h => `• ${h.detail}`).join('\n') + '\n\n'
                            : '')
                        + 'The driver is attached either way, and nothing here changes a number. '
                        + 'A pallet disagreement is already recorded on the appointment\u2019s history.'
                    }
                    confirmLabel="Open the appointment"
                    cancelLabel="Leave it for now"
                    onConfirm={() => {
                        const pending = attachNotice;
                        setAttachNotice(null);
                        /* ⚠️ THE APPOINTMENT FROM THE DAY'S GRID, not a refetch — and via
                           `calendar?.appointments`, which is how every other lookup on
                           this screen reaches it. There is no bare `appointments` in
                           scope; the first version of this line assumed one and would
                           have failed at `tsc` rather than at runtime, which is the
                           good direction to be wrong in. */
                        const found = calendar?.appointments.find(
                            a => String(a.appointmentId) === String(pending.appt.appointmentId));
                        if (found) setEditingAppt(found);
                    }}
                    onCancel={() => {
                        const pending = attachNotice;
                        setAttachNotice(null);
                        /* ⭐ THE DOOR TEXT THE NOTICE DEFERRED. A driver is still in the
                           lot not knowing where to go, and that does not stop being true
                           because his pallet count was wrong. */
                        setDoorTextKind('door');
                        setDoorTextFor(String(pending.appt.appointmentId));
                    }}
                />
            )}

            {backoutResult && (
                <ConfirmDialog
                    open={true}
                    title={backoutResult.deleted + backoutResult.cancelled > 0 ? 'Upload backed out' : 'Nothing changed'}
                    message={
                        backoutResult.message
                        + (backoutResult.kept.length > 0
                            ? '\n\n' + backoutResult.kept.slice(0, 10).map(
                                (r: { sourceRowNumber: number | null; appointmentId: string; reason: string }) =>
                                `• ${r.sourceRowNumber !== null ? `Row ${r.sourceRowNumber}` : `Appointment ${r.appointmentId}`}: ${r.reason}`
                              ).join('\n')
                              + (backoutResult.kept.length > 10
                                  ? `\n• …and ${backoutResult.kept.length - 10} more`
                                  : '')
                            : '')
                    }
                    confirmLabel="Close"
                    onConfirm={() => setBackoutResult(null)}
                    onCancel={() => setBackoutResult(null)}
                />
            )}

            {/* ---- Notify-carrier prompt (after a carrier-requested move) ---- */}
            {pendingQuick && (
                <ConfirmDialog
                    open={true}
                    destructive
                    requireReason={pendingQuick.label === 'reject'}
                    title={
                        pendingQuick.label === 'noshow' ? 'Mark no-show?'
                        : pendingQuick.label === 'cancel' ? 'Cancel appointment?'
                        : 'Reject request?'
                    }
                    message={
                        // Name the appointment. "This appointment" is fine when
                        // you opened the dialog on purpose and ambiguous when
                        // three are open on the board — the carrier portal
                        // already names it, and these two should agree.
                        pendingQuick.label === 'noshow' ? `Mark appointment ${pendingQuick.appt.appointmentNumber} as a no-show? The truck did not arrive.`
                        : pendingQuick.label === 'cancel' ? `Cancel appointment ${pendingQuick.appt.appointmentNumber}? This can't be undone.`
                        : 'The carrier will receive this reason in the decline email.'
                    }
                    confirmLabel={
                        pendingQuick.label === 'noshow' ? 'Mark no-show'
                        : pendingQuick.label === 'cancel' ? 'Cancel appointment'
                        : 'Reject request'
                    }
                    // ⚠️ "Cancel" NEXT TO "Cancel appointment" (2026-08-17).
                    // On a cancel dialog the default dismiss label is the same
                    // word as the destructive action, so the safe button and the
                    // irreversible one read identically. The carrier portal got
                    // this right with "Keep appointment"; match it.
                    cancelLabel={
                        pendingQuick.label === 'cancel' ? 'Keep appointment'
                        : pendingQuick.label === 'noshow' ? 'Keep as scheduled'
                        : 'Keep request'
                    }
                    reasonLabel="Reason"
                    reasonPlaceholder="e.g. No availability at that time"
                    onConfirm={(reason) => {
                        const { appt, label } = pendingQuick;
                        setPendingQuick(null);
                        void runQuickAction(appt, label, reason);
                    }}
                    onCancel={() => setPendingQuick(null)}
                />
            )}

            {notifyPrompt && (() => {
                // Drivers are only offered when this event actually texts them AND
                // this appointment has drivers on file. Both conditions matter:
                // before 2026-08-17 the prompt said "Email carrier" while the
                // server also texted every driver, so the text was invisible here
                // and — because approve normally happens before drivers are
                // added — had never once fired in production.
                const canText = DRIVER_TEXT_EVENTS.includes(notifyPrompt.event)
                    && notifyPrompt.drivers.length > 0;
                const driverNames = notifyPrompt.drivers.map(d => d.name).filter(Boolean).join(', ');
                const who = notifyPrompt.carrierName ?? 'the carrier';
                const action =
                    /* ⭐ 'had its details updated', not 'was updated' — "updated" on its
                       own reads as "was moved", which is the one thing this event
                       specifically is not. Decision 186. */
                    notifyPrompt.event === 'updated' ? 'had its details updated'
                    : notifyPrompt.event === 'rescheduled' ? 'was moved'
                    : notifyPrompt.event === 'approved' ? 'was approved'
                    : notifyPrompt.event === 'cancelled' ? 'was cancelled'
                    : notifyPrompt.event === 'noshow' ? 'was marked a no-show'
                    : notifyPrompt.event === 'arrived' ? 'was marked arrived'
                    : 'was marked departed';
                return (
                <Dialog open={true} onOpenChange={(o) => !o && handleDismissNotify()}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>{canText ? 'Notify carrier and drivers?' : 'Notify the carrier?'}</DialogTitle>
                            <DialogDescription>
                                Appointment {notifyPrompt.appointmentNumber} {action}.
                                <span className="block mt-2">
                                    Email goes to <strong>{who}</strong>.
                                </span>
                                {canText && (
                                    <span className="block mt-1">
                                        Text goes to {notifyPrompt.drivers.length === 1
                                            ? <strong>{driverNames}</strong>
                                            : <><strong>{notifyPrompt.drivers.length} drivers</strong> ({driverNames})</>}.
                                        {' '}A driver who has replied STOP is skipped automatically.
                                    </span>
                                )}
                                {!canText && notifyPrompt.drivers.length > 0 && (
                                    // Say WHY there's no text option. "No drivers on
                                    // file" and "this event doesn't text" look
                                    // identical from the outside, and the second one
                                    // reads as a bug.
                                    <span className="block mt-1 text-xs">
                                        Drivers aren't texted for this event — only when an appointment is approved, moved or cancelled.
                                    </span>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <div className="flex flex-wrap gap-2 justify-end w-full">
                                <Button variant="ghost" size="sm" onClick={handleDismissNotify}>
                                    Don't notify
                                </Button>
                                {canText && (
                                    <Button variant="outline" size="sm" onClick={() => void handleNotify(['drivers'])}>
                                        Text drivers
                                    </Button>
                                )}
                                <Button variant={canText ? 'outline' : 'default'} size="sm" onClick={() => void handleNotify(['carrier'])}>
                                    Email carrier
                                </Button>
                                {canText && (
                                    <Button variant="default" size="sm" onClick={() => void handleNotify(['carrier', 'drivers'])}>
                                        Email + text
                                    </Button>
                                )}
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                );
            })()}

            {capOverride && (
                <Dialog open={true} onOpenChange={(o) => !o && setCapOverride(null)}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>{capOverride.title}</DialogTitle>
                            <DialogDescription>
                                {capOverride.detail} Move {capOverride.appointmentNumber} there anyway?
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <div className="flex gap-2 justify-end w-full">
                                <Button variant="ghost" size="sm" onClick={() => setCapOverride(null)}>
                                    Keep where it was
                                </Button>
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => {
                                        const pending = capOverride;
                                        setCapOverride(null);
                                        void handleMoveAppointment(pending.ev, true);
                                    }}
                                >
                                    Move anyway
                                </Button>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* ---- Toast ----
                Rendered into document.body via a portal. The app header uses
                backdrop-filter, which creates a containing block for fixed
                descendants — so a fixed toast nested in the page would anchor
                to that subtree (and get clipped/mispositioned) instead of the
                viewport. Portaling to body guarantees true viewport-fixed. */}
            {toast && createPortal(
                <div
                    className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 px-4 py-2 rounded-md shadow-lg border text-sm font-medium max-w-md ${
                        toast.kind === 'error'
                            ? 'bg-destructive text-destructive-foreground border-destructive'
                            : 'bg-status-approved text-white border-status-approved'
                    }`}
                    role="status"
                    aria-live="polite"
                >
                    {toast.kind === 'error' ? (
                        <AlertCircle className="h-4 w-4 shrink-0" />
                    ) : (
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                    )}
                    <span>{toast.msg}</span>
                </div>,
                document.body,
            )}
        </div>
    );
}

// ---- Helpers ----

function contextMenuItemsFor(
    appt: AppointmentDto,
    handlers: {
        onDetails: () => void;
        onApprove: () => void;
        onReject: () => void;
        onArrive: () => void;
        onDepart: () => void;
        onNoShow: () => void;
        onCancel: () => void;
        onSendDirections: () => void;
        /**
         * ⭐ #324 — text the driver a pin to where he is DELIVERING.
         *
         * ⛔ OPTIONAL, AND `undefined` ON AN INBOUND. A consignee is where a load
         * is GOING; an inbound has none (migration 105: "Inbound appointments will
         * have zero rows"). Showing the entry there and then explaining that there
         * is no consignee is a control overclaiming what it does — absent is the
         * honest shape.
         */
        onSendConsigneePin?: () => void;
        onTextDoor: () => void;
        onTextPaperwork: () => void;
        /** ⭐ #302 — "unloading/loading has started". */
        onTextWork: () => void;
        onEditDriver: () => void;
        onPaperwork: () => void;
        onSignPaperwork: () => void;
        onPrintPaperwork: () => void;
        /**
         * ⭐ #306 — repair the status by hand. ADMIN ONLY, and undefined when the
         * signed-in user is not one.
         *
         * ⛔ OPTIONAL RATHER THAN ALWAYS-PRESENT-AND-DISABLED. A greyed-out entry
         * on a right-click menu tells a scheduler the feature exists and that they
         * are not allowed it, which invites a request; absent is the honest shape
         * for a control they will never use.
         */
        onOverrideStatus?: () => void;
    },
) {
    const isBlock = appt.direction === 'BLOCK';
    const isTerminal =
        appt.statusCode === 'DEPARTED' ||
        appt.statusCode === 'CANCELLED' ||
        appt.statusCode === 'NOSHOW';
    const isRequested = !isBlock && appt.statusCode === 'REQUESTED';
    const canArrive = !isBlock && (appt.statusCode === 'APPROVED' || appt.statusCode === 'REQUESTED');
    const canNoShow = !isBlock && (appt.statusCode === 'APPROVED' || appt.statusCode === 'REQUESTED');
    const canDepart = !isBlock && appt.statusCode === 'ARRIVED';
    const canCancel = !isTerminal;

    const items: {
        label: string;
        icon?: React.ReactNode;
        onClick: () => void;
        destructive?: boolean;
        disabled?: boolean;
    }[] = [
        { label: 'View details…', icon: <Info className="h-3.5 w-3.5" />, onClick: handlers.onDetails },
    ];
    // Send directions is carrier-facing — not applicable to a block.
    if (!isBlock) {
        // ⛔ IT SAYS "DIRECTIONS", NOT "TEXT DRIVER". All three text items in this
        // menu used to open with the words "Text driver", so a scheduler could not
        // tell which one sent a map link, which one sent a dock door, and which one
        // said the paperwork was ready — three completely different messages
        // (Pete, 2026-08-20: *"otherwise scheduler won't know"*).
        //
        // ⚠️ The driver's NAME stays in the label. It is how a scheduler confirms
        // they are about to text the man they are looking at, on an appointment that
        // may carry several drivers.
        const label = appt.drivers.length === 0
            ? 'Send directions…'
            : appt.drivers.length === 1
                ? `Text directions (${appt.drivers[0].name})…`
                : `Text directions (${appt.drivers.length} drivers)…`;
        items.push({ label, icon: <Send className="h-3.5 w-3.5" />, onClick: handlers.onSendDirections });

        // ⭐ #324 — A SEPARATE ITEM FROM DIRECTIONS, for the same reason the door
        // text is: directions answer "how do I get to YOU", this answers "where am
        // I taking this". A driver may need both, and they point at different sites.
        if (handlers.onSendConsigneePin) {
            items.push({
                label: 'Text delivery pin…',
                icon: <MapPin className="h-3.5 w-3.5" />,
                onClick: handlers.onSendConsigneePin,
            });
        }

        // ⭐ A SEPARATE ITEM FROM DIRECTIONS, DELIBERATELY. Directions answer
        // "how do I get to you" before he sets off; this answers "where do I
        // back in" to a man already in the yard. Folding them together would
        // put a map link in front of somebody standing on the site.
        //
        // ⚠️ THE LABEL NAMES THE DOOR when there is one, so a scheduler can see
        // what the driver is about to be told without opening the dialog — and
        // sees plainly when there is nothing to tell him yet.
        items.push({
            label: appt.dockDoorId != null
                ? `Text driver his door${appt.dockDoorCode ? ` (${appt.dockDoorCode})` : ''}…`
                : 'Text driver — no door yet…',
            icon: <DoorOpen className="h-3.5 w-3.5" />,
            onClick: handlers.onTextDoor,
        });

        // ⭐ THE OTHER END OF THE VISIT. Only offered once the truck is actually
        // here — telling a driver his paperwork is ready before he has arrived
        // is nonsense, and an item that is nonsense most of the time is an item
        // people stop reading.
        if (appt.statusCode === 'ARRIVED') {
            /**
             * ⭐⭐ "UNLOADING HAS STARTED" (#302, 2026-08-27). Pete: *"we need to have
             * the manual option to in right-click on the appointment and in the Comm
             * icon on the driver card."*
             *
             * ⭐ THE LABEL SAYS WHICH WORD THE DRIVER WILL GET, because the message
             * differs by direction and a scheduler should not have to open the dialog
             * to find out which one this load sends.
             *
             * ⚠️ ARRIVED ONLY, like the paperwork item beside it and for the same
             * reason: telling a driver his unload has started before he is on site is
             * nonsense, and an item that is nonsense most of the time stops being read.
             */
            items.push({
                label: appt.direction === 'OUTBOUND'
                    ? 'Text driver — loading started…'
                    : 'Text driver — unloading started…',
                icon: <MessageSquare className="h-3.5 w-3.5" />,
                onClick: handlers.onTextWork,
            });
            items.push({
                label: 'Text driver — paperwork ready…',
                icon: <MessageSquare className="h-3.5 w-3.5" />,
                onClick: handlers.onTextPaperwork,
            });
        }
    }
    // ⭐ PAPERWORK (#144). Offered on every non-block appointment INCLUDING
    // terminal ones — the paperwork of a departed load is exactly what somebody
    // goes looking for when a carrier disputes a delivery weeks later, and that is
    // the case this dialog is most useful in.
    if (!isBlock) {
        /**
         * ⭐ SIGN AND PRINT ACT; "Paperwork…" ASKS (#245/#245a). Pete wanted the
         * same three options wherever paperwork is reachable, and they behave
         * identically here to the driver card because they call the same handlers
         * — not a copy of them.
         *
         * ⚠️ EXPECT THESE TO FALL THROUGH TO THE LIST MORE OFTEN HERE, and that is
         * the rule working rather than failing. A driver card is ONE arrival; an
         * appointment is the LOAD, whose dossier gathers the load's documents AND
         * every arrival's — so a mature load usually holds more than one original,
         * which is a genuine choice and belongs to the scheduler.
         */
        items.push({
            label: 'Sign paperwork',
            icon: <PenLine className="h-3.5 w-3.5" />,
            onClick: handlers.onSignPaperwork,
        });
        items.push({
            label: 'Print paperwork',
            icon: <Printer className="h-3.5 w-3.5" />,
            onClick: handlers.onPrintPaperwork,
        });
        items.push({
            label: 'Paperwork…',
            icon: <FileText className="h-3.5 w-3.5" />,
            onClick: handlers.onPaperwork,
        });
    }
    // Driver assignment — same non-terminal gate as cancel, since a departed/
    // cancelled/no-show appointment's drivers are historical record, not
    // something to be edited after the fact.
    if (!isBlock && !isTerminal) {
        const label = appt.drivers.length > 0 ? `Drivers (${appt.drivers.length})…` : 'Add driver…';
        items.push({ label, icon: <Truck className="h-3.5 w-3.5" />, onClick: handlers.onEditDriver });
    }
    // Approve/Reject are the primary decisions on a carrier request — surface
    // them prominently (just under the info items) and only while REQUESTED.
    if (isRequested) {
        items.push({
            label: 'Approve request',
            icon: <CheckCircle2 className="h-3.5 w-3.5" />,
            onClick: handlers.onApprove,
        });
        items.push({
            label: 'Reject request…',
            icon: <Ban className="h-3.5 w-3.5" />,
            onClick: handlers.onReject,
            destructive: true,
        });
    }
    if (canArrive) {
        items.push({ label: 'Mark arrived', icon: <LogIn className="h-3.5 w-3.5" />, onClick: handlers.onArrive });
    }
    if (canDepart) {
        items.push({ label: 'Mark departed', icon: <LogOut className="h-3.5 w-3.5" />, onClick: handlers.onDepart });
    }
    if (canNoShow) {
        items.push({ label: 'Mark no-show', icon: <UserX className="h-3.5 w-3.5" />, onClick: handlers.onNoShow });
    }
    // Pencil icon imported for future reschedule shortcut — leave a placeholder
    void Pencil;
    if (canCancel) {
        items.push({
            label: 'Cancel appointment',
            icon: <XCircle className="h-3.5 w-3.5" />,
            onClick: handlers.onCancel,
            destructive: true,
        });
    }
    /**
     * ⭐ #306 — LAST, AND BELOW CANCEL, DELIBERATELY.
     *
     * ⚠️ IT IS NOT ONE OF THE ORDINARY ACTIONS AND MUST NOT SIT AMONG THEM. Every
     * item above enforces a transition; this one goes anywhere from anywhere and
     * can undo the outbound release gate. Putting it beside "Mark departed" would
     * make it look like a peer.
     *
     * ⛔ AND IT IS OFFERED ON A BLOCK TOO. A block has a status like anything else
     * and is exactly the kind of row that ends up needing repair — but nothing
     * else on this menu will touch one.
     */
    if (handlers.onOverrideStatus) {
        items.push({
            label: 'Change status (admin)…',
            icon: <ShieldAlert className="h-3.5 w-3.5" />,
            onClick: handlers.onOverrideStatus,
            destructive: true,
        });
    }
    return items;
}

function formatError(err: unknown): string {
    if (err instanceof ApiCallError) {
        return `${err.message} (HTTP ${err.details.status})`;
    }
    if (err instanceof Error) return err.message;
    return String(err);
}
